import React, { useState, useEffect, useRef } from 'react';
import qz from 'qz-tray';
import html2canvas from 'html2canvas';

// ========== นำเข้าจากโมดูลที่แยกออกมา ==========
import { supabase } from './lib/supabaseClient';
import { logoutLine } from './lib/lineAuth';
import { WAREHOUSES_LIST, getUserRole, ADMIN_EMAILS } from './lib/constants';
import { getStatusConfig, getSafeTime, isSameDate, chunkArray, generateUID, validateUID } from './lib/helpers';
import { uploadItemImage, uploadRecallImage } from './lib/r2Storage';
import useDevice from './lib/useDevice';
import CameraScanner from './components/CameraScanner';
import DentalLabel from './components/DentalLabel';
import BlankLabelSheet from './components/BlankLabelSheet';
import LoginPage from './pages/LoginPage';
import HomePage from './components/HomePage';
import DurableDashboard from './components/DurableDashboard';
import SterileLocations from './components/SterileLocations';
// eslint-disable-next-line no-unused-vars
import InstrumentTags from './components/InstrumentTags';
import { QRCodeCanvas } from 'qrcode.react';
import BarcodeTrackingPage from './pages/BarcodeTrackingPage';
import TracePage from './pages/TracePage';
import WashingPage from './pages/WashingPage';
import RecallPage from './pages/RecallPage';
import DispensePage from './pages/DispensePage';
import ClinicOrderPage from './pages/ClinicOrderPage';
import SupplyPage from './pages/SupplyPage';
import PackagingPage from './pages/PackagingPage';
import SetConfigPage from './pages/SetConfigPage';
import DoctorBoxPage from './pages/DoctorBoxPage';
import ToolAssemblyPage from './pages/ToolAssemblyPage';
import ManageUsersPage from './pages/ManageUsersPage';
import StockPage from './pages/StockPage';
import HandpiecePage from './pages/HandpiecePage';
import RegisterItemPage from './pages/RegisterItemPage';
import QROrderGeneratorPage from './pages/QROrderGeneratorPage';
import StockInReceiptPage from './pages/StockInReceiptPage';
import ReceiptInspectionPage from './pages/ReceiptInspectionPage';
import StorageAdminPage from './pages/StorageAdminPage';
import ConsumableDashboardPage from './pages/ConsumableDashboardPage';
import DispenseReportPage from './pages/DispenseReportPage';
import ConsumableTransferModal from './components/ConsumableTransferModal';
import SubWarehouseAdminPage from './pages/SubWarehouseAdminPage';
import DentalRiskReportPage from './pages/DentalRiskReportPage';
import RiskPortalStandalonePage from './pages/RiskPortalStandalonePage';
import UserProfileModal from './components/profile/UserProfileModal';

// ตรวจสอบ Subdomain, Pathname (/risk), และ Query Parameter สำหรับ Risk Portal
export const isRiskSubdomain = () => {
  if (typeof window === 'undefined') return false;
  const host = (window.location.hostname || '').toLowerCase();
  const pathname = (window.location.pathname || '').toLowerCase();
  const search = window.location.search || '';
  const params = new URLSearchParams(search);
  return (
    host === 'risk.fangdent.com' ||
    host.startsWith('risk.') ||
    params.get('portal') === 'risk' ||
    pathname === '/risk' ||
    pathname.startsWith('/risk/')
  );
};

const PAGES_LIST = [
  { id: 'consumable', name: 'สิ้นเปลือง' },
  { id: 'consumable-dashboard', name: '📊 แดชบอร์ดสิ้นเปลือง 4 คลัง' },
  { id: 'sub-warehouse-admin', name: '🏬 จัดการคลังย่อย (ผู้ดูแลคลัง)' },
  { id: 'dispense-report', name: '📄 รายงานเบิกจ่าย' },
  { id: 'durable', name: 'คงทน' },
  { id: 'handpiece', name: 'หัวกรอ' },
  { id: 'receipt-stockin', name: 'รับเข้าสินค้า/ใบเสร็จ' },
  { id: 'receipt-inspection', name: 'สรุปตรวจรับ & QR' },
  { id: 'storage-admin', name: 'คลังรูป & ซิงค์ R2' },
  { id: 'packaging', name: 'แพ็กซอง' },
  { id: 'washing', name: 'ห้องล้าง' },
  { id: 'supply', name: 'Sterile' },
  { id: 'dispense', name: 'จ่ายตาม Order' },
  { id: 'clinic-order', name: 'สั่งของ' },
  { id: 'barcode-tracking', name: 'บาร์โค้ด' },
  { id: 'trace', name: 'ข้อมูล QR' },
  { id: 'recall', name: 'เรียกคืนวัสดุ (Recall)' },
  { id: 'durable-dashboard', name: 'แดชบอร์ดวงจรวัสดุ' },
  { id: 'dental-risk', name: '🛡️ จัดการความเสี่ยง (Staff)' }
];



// Helper to optimize Cloudinary image URLs
const getOptimizedImageUrl = (url) => {
  if (!url || typeof url !== 'string' || !url.includes("cloudinary.com")) return url;
  if (url.includes("/upload/c_")) return url;
  return url.replace("/upload/", "/upload/c_limit,w_300,q_auto,f_auto/");
};

// Helper to parse ordered_by and message from order notes
const parseOrderNote = (note) => {
  if (!note) return { orderedBy: 'ไม่ระบุ', noteText: '' };
  if (typeof note === 'string' && note.startsWith("ordered_by:")) {
    const pipeIdx = note.indexOf("|note:");
    if (pipeIdx !== -1) {
      const orderedBy = note.substring(11, pipeIdx);
      const noteText = note.substring(pipeIdx + 6);
      return { orderedBy, noteText };
    }
  }
  return { orderedBy: 'ไม่ระบุ', noteText: note };
};

// Helper to parse database image_url column into full and zoom URLs
const parseImages = (imageUrlField) => {
  if (!imageUrlField) return { full: '', zoom: '', other: '', document: '' };
  if (typeof imageUrlField !== 'string') return { full: '', zoom: '', other: '', document: '' };

  // JSON format
  if (imageUrlField.startsWith('{') && imageUrlField.endsWith('}')) {
    try {
      const parsed = JSON.parse(imageUrlField);
      return {
        full: parsed.full || '',
        zoom: parsed.zoom || '',
        other: parsed.other || '',
        document: parsed.document || ''
      };
    } catch (e) {
      // fallback
    }
  }

  // Pipe delimited format
  if (imageUrlField.includes('|')) {
    const parts = imageUrlField.split('|');
    return {
      full: parts[0] || '',
      zoom: parts[1] || '',
      other: parts[2] || '',
      document: parts[3] || ''
    };
  }

  // Standard plain URL
  return { full: imageUrlField, zoom: '', other: '', document: '' };
};

// Helper function to highlight key data in log actions (barcodes, users, cycles)
const highlightActionText = (text) => {
  if (!text) return '';
  const parts = text.split(/(รหัส\s*[A-Za-z0-9-]+|โดย\s*[ก-๙a-zA-Z0-9\s]+|รอบที่\s*\d+|เครื่องนึ่ง\s*[ก-๙a-zA-Z0-9-\s]+)/g);
  return parts.map((part, index) => {
    if (part.startsWith('รหัส')) {
      return <span key={index} style={{ color: '#2563eb', fontWeight: 'bold' }}>{part}</span>;
    }
    if (part.startsWith('โดย')) {
      return <span key={index} style={{ color: '#059669', fontWeight: 'bold' }}>{part}</span>;
    }
    if (part.startsWith('รอบที่') || part.startsWith('เครื่องนึ่ง')) {
      return <span key={index} style={{ color: '#d97706', fontWeight: 'bold' }}>{part}</span>;
    }
    return part;
  });
};

// ==========================================================================
// ส่วนที่ 3: Dashboard — หน้าต่างการทำงานหลัก
// (Components, utilities, constants ถูกย้ายไปไฟล์แยกแล้ว)
// ==========================================================
const Dashboard = ({ user, onLogout, isGuestMode = false, guestRoom = '', initialPage = null }) => {

  const [activeUserRole, setActiveUserRole] = useState(isGuestMode ? 'guest' : getUserRole(user?.email));
  const [activeAllowedPages, setActiveAllowedPages] = useState('');
  const [navDropdownOpen, setNavDropdownOpen] = useState(false);
  const userRole = activeUserRole;
  const isAdmin = userRole === 'admin';

  const [userProfiles, setUserProfiles] = useState([]);
  const [dbError, setDbError] = useState(null);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserRole, setNewUserRole] = useState('อื่นๆ');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const roleOptions = ['admin', 'ทันตแพทย์', 'ทันตาภิบาล', 'ผู้ช่วยทันตแพทย์', 'อื่นๆ'];

  const [roleDefaults, setRoleDefaults] = useState({
    'admin': 'consumable,durable,handpiece,packaging,washing,supply,dispense,clinic-order,barcode-tracking,trace,receipt-stockin,receipt-inspection,dental-risk',
    'ทันตแพทย์': 'consumable,durable,handpiece,packaging,washing,supply,dispense,clinic-order,barcode-tracking,trace,receipt-stockin,receipt-inspection',
    'ทันตาภิบาล': 'consumable,durable,handpiece,packaging,washing,supply,dispense,clinic-order,barcode-tracking,trace,receipt-stockin,receipt-inspection',
    'ผู้ช่วยทันตแพทย์': 'washing,packaging,supply,dispense',
    'อื่นๆ': 'clinic-order,barcode-tracking,trace'
  });
  const [selectedDefaultRoleTab, setSelectedDefaultRoleTab] = useState('อื่นๆ');
  const [defaultSaveStatus, setDefaultSaveStatus] = useState('');
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPasswordValue, setNewPasswordValue] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [sqlScriptToShow, setSqlScriptToShow] = useState(false);
  const [successModal, setSuccessModal] = useState(null); // { title: '', message: '' }
  const [showSelfResetPasswordModal, setShowSelfResetPasswordModal] = useState(false);
  const [selfNewPasswordValue, setSelfNewPasswordValue] = useState('');
  const [selfConfirmNewPasswordValue, setSelfConfirmNewPasswordValue] = useState('');
  const [selfCurrentPasswordValue, setSelfCurrentPasswordValue] = useState('');
  const [isUpdatingSelfPassword, setIsUpdatingSelfPassword] = useState(false);
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const currentStaffProfile = userProfiles.find(
    (p) => (p.email && user?.email && p.email.toLowerCase() === user.email.toLowerCase()) || p.id === user?.id
  ) || null;

  const hasPageAccess = (pageId) => {
    // 🛡️ Strict Route & Page Access Guard for Risk Subdomain
    if (isRiskSubdomain()) {
      return pageId === 'dental-risk' || pageId === 'dental-risk-portal';
    }

    // 🏥 CSSD / Dev Domain (When NOT in risk subdomain)
    if (pageId === 'dental-risk-portal') return isAdmin;
    if (pageId === 'dental-risk') return isAdmin;

    if (isGuestMode) {
      return pageId === 'clinic-order';
    }
    if (pageId === 'home') return true;
    if (pageId === 'consumable-dashboard') return hasPageAccess('consumable');
    if (pageId === 'sub-warehouse-admin') return hasPageAccess('consumable');
    if (pageId === 'dispense-report') return hasPageAccess('consumable');
    if (pageId === 'durable-dashboard') return true;
    if (pageId === 'sterile-locations') return true;
    if (isAdmin) return true;
    const allowed = activeAllowedPages ? activeAllowedPages.split(',') : [];
    if (pageId === 'receipt-stockin' || pageId === 'receipt-inspection') {
      return allowed.includes('receipt-stockin') || allowed.includes('receipt-inspection') || allowed.includes('consumable') || allowed.includes('durable');
    }
    if (pageId === 'storage-admin') {
      return isAdmin || allowed.includes('storage-admin') || allowed.includes('receipt-stockin') || allowed.includes('receipt-inspection');
    }
    if (pageId === 'recall') {
      return allowed.includes('recall') || allowed.includes('supply');
    }
    if (pageId === 'tool-assembly') {
      return allowed.includes('tool-assembly') || allowed.includes('packaging') || allowed.includes('washing') || allowed.includes('supply');
    }
    return allowed.includes(pageId);
  };

  const handleRiskNavigation = () => {
    if (isAdmin) {
      setCurrentPage('dental-risk');
    } else {
      window.location.href = 'https://risk.fangdent.com';
    }
  };

  const defaultPage = initialPage || (isRiskSubdomain() ? 'dental-risk-portal' : 'home');

  // 📱 Responsive: ตรวจจับอุปกรณ์
  const { isMobile } = useDevice();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(defaultPage);
  const [dispenseReportParams, setDispenseReportParams] = useState({ mode: 'summary', itemId: null });

  // 🛡️ Strict Route & Page Access Guard for Risk Subdomain
  useEffect(() => {
    if (isRiskSubdomain()) {
      if (currentPage !== 'dental-risk' && currentPage !== 'dental-risk-portal') {
        setCurrentPage('dental-risk-portal');
      }
    }
  }, [currentPage]);

  const handleNavigateToPage = (pageId, params = null) => {
    if (isRiskSubdomain()) {
      if (pageId !== 'dental-risk' && pageId !== 'dental-risk-portal') {
        setCurrentPage('dental-risk-portal');
        return;
      }
    }
    if (pageId === 'dispense-report' && params) {
      setDispenseReportParams(params);
    } else if (pageId === 'dispense-report' && !params) {
      setDispenseReportParams({ mode: 'summary', itemId: null });
    }
    setCurrentPage(pageId);
  };

  const [inspectionReceiptId, setInspectionReceiptId] = useState(null);

  const navigateToReceiptInspection = (receiptId = null) => {
    setInspectionReceiptId(receiptId);
    setCurrentPage('receipt-inspection');
  };

  const [expandedImage, setExpandedImage] = useState(null);
  const [selectedQrRoom, setSelectedQrRoom] = useState('');

  // 🔄 State & Handlers สำหรับ ConsumableTransferModal
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [transferInitialItem, setTransferInitialItem] = useState(null);
  const handleOpenTransferModal = (itemData = null) => {
    setTransferInitialItem(itemData);
    setIsTransferModalOpen(true);
  };
  const handleCloseTransferModal = () => {
    setIsTransferModalOpen(false);
    setTransferInitialItem(null);
  };

  const [supplies, setSupplies] = useState([]);
  const [itemMaster, setItemMaster] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [logs, setLogs] = useState([]);
  const [instrumentTags, setInstrumentTags] = useState([]);

  // States: Registration Page & Editing
  const [regType, setRegType] = useState('วัสดุสิ้นเปลือง');
  const [regName, setRegName] = useState('');
  const [regAliases, setRegAliases] = useState('');
  const [inventorySearchQuery, setInventorySearchQuery] = useState('');
  const [handpieceSearchQuery, setHandpieceSearchQuery] = useState('');
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [isNameDuplicate, setIsNameDuplicate] = useState(false);

  const [newMasterItem, setNewMasterItem] = useState('');
  const [setCategory, setSetCategory] = useState('วัสดุคงทน');
  const [lotNumber, setLotNumber] = useState('');
  const [barcode, setBarcode] = useState('');
  const [expDate, setExpDate] = useState('');
  const [globalMinStock, setGlobalMinStock] = useState('');
  const [minStock, setMinStock] = useState('');
  const [cabinet, setCabinet] = useState('');
  const [shelf, setShelf] = useState('');
  const [row, setRow] = useState('');
  const [unit, setUnit] = useState('');
  const [unitSize, setUnitSize] = useState('');
  const [initialQty, setInitialQty] = useState('');
  const [pricePerUnit, setPricePerUnit] = useState('');
  const [company, setCompany] = useState('');
  const [receiveDate, setReceiveDate] = useState('');
  const [deliveryNote, setDeliveryNote] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [orderDate, setOrderDate] = useState('');
  const [approvalDate, setApprovalDate] = useState('');

  const [imageUrl, setImageUrl] = useState('');
  const [zoomImageUrl, setZoomImageUrl] = useState('');
  const [otherImageUrl, setOtherImageUrl] = useState('');
  const [documentImageUrl, setDocumentImageUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingZoom, setIsUploadingZoom] = useState(false);
  const [isUploadingOther, setIsUploadingOther] = useState(false);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [showPreview, setShowPreview] = useState({});


  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [selectedComponents, setSelectedComponents] = useState([]);

  // States: Packaging
  const [pkgType, setPkgType] = useState('sterile-pouch');
  const [prepDate, setPrepDate] = useState(new Date().toISOString().split('T')[0]);
  const [expDateInst, setExpDateInst] = useState('');
  const [preparedBy, setPreparedBy] = useState('');
  const [checkedBy, setCheckedBy] = useState('');
  const [sterileMachine, setSterileMachine] = useState('หน่วยจ่ายกลาง');
  const [sterileCycle, setSterileCycle] = useState('');

  // Tab filter state for Dental Handpieces page
  const [handpieceFilter, setHandpieceFilter] = useState('all');
  const [selectedHandpieceName, setSelectedHandpieceName] = useState(null);

  // Tab filter state for Tool Assembly page
  const [assemblyTab, setAssemblyTab] = useState('sets');
  const [selectedSetTemplate, setSelectedSetTemplate] = useState(null);
  const [selectedSingleItem, setSelectedSingleItem] = useState(null);
  const [selectedForAssembly, setSelectedForAssembly] = useState([]);
  const [selectedInspectionItem, setSelectedInspectionItem] = useState(null);
  const [isUsingPreparedScan, setIsUsingPreparedScan] = useState(false);
  const [isBarcodeScanned, setIsBarcodeScanned] = useState(false);

  // Dynamic lists for dropdowns in Packaging page
  const [preparersList, setPreparersList] = useState(() => {
    const saved = localStorage.getItem('preparersList');
    return saved ? JSON.parse(saved) : [];
  });
  const [checkersList, setCheckersList] = useState(() => {
    const saved = localStorage.getItem('checkersList');
    return saved ? JSON.parse(saved) : [];
  });
  const [machinesList, setMachinesList] = useState(() => {
    const saved = localStorage.getItem('machinesList');
    return saved ? JSON.parse(saved) : ['หน่วยจ่ายกลาง', 'Autoclave 1', 'Autoclave 2', 'Autoclave 3'];
  });
  const [cyclesList, setCyclesList] = useState(() => {
    const saved = localStorage.getItem('cyclesList');
    return saved ? JSON.parse(saved) : [
      'หน่วยจ่ายกลาง:รอบ 08:00 น.',
      'หน่วยจ่ายกลาง:รอบ 10:00 น.',
      'หน่วยจ่ายกลาง:รอบ 13:00 น.',
      'หน่วยจ่ายกลาง:รอบ 15:00 น.',
      'Autoclave 1:รอบ 10:00 น.',
      'Autoclave 1:รอบ 16:00 น.',
      'Autoclave 2:รอบ 11:00 น.',
      'Autoclave 3:รอบ 13:00 น.'
    ];
  });

  const getAvailableCycles = (selectedMachine, allCycles) => {
    if (!selectedMachine) {
      return allCycles.filter(c => !c.includes(':'));
    }
    const machineSpecific = allCycles.filter(c => c.startsWith(`${selectedMachine}:`));
    if (machineSpecific.length === 0) {
      return allCycles.filter(c => !c.includes(':'));
    }
    return machineSpecific;
  };

  const getCycleDisplayName = (cycleName) => {
    if (cycleName.includes(':')) {
      const parts = cycleName.split(':');
      return parts.slice(1).join(':');
    }
    return cycleName;
  };

  const getFilteredPreparers = () => {
    const dummyNames = ['สมชาย', 'สมหญิง', 'สมศักดิ์', 'สมศรี'];
    const userNames = userProfiles.map(p => p.name).filter(Boolean);
    const combined = Array.from(new Set([...userNames, ...preparersList]));
    return combined.filter(name => !dummyNames.includes(name));
  };

  const getFilteredCheckers = () => {
    const dummyNames = ['สมชาย', 'สมหญิง', 'สมศักดิ์', 'สมศรี'];
    const userNames = userProfiles.map(p => p.name).filter(Boolean);
    const combined = Array.from(new Set([...userNames, ...checkersList]));
    return combined.filter(name => !dummyNames.includes(name));
  };

  const parseTimeFromCycle = (cycleText) => {
    const match = cycleText.match(/(\d{1,2})[:.](\d{2})/);
    if (match) {
      return {
        hours: parseInt(match[1], 10),
        minutes: parseInt(match[2], 10)
      };
    }
    return null;
  };

  const getAutoSelectedCycle = (selectedMachine, allCycles) => {
    const available = getAvailableCycles(selectedMachine, allCycles);
    if (available.length === 0) return '';
    
    const now = new Date();
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTotal = currentHours * 60 + currentMinutes;
    
    let bestCycle = '';
    let minDiff = Infinity;
    
    for (const cycle of available) {
      const time = parseTimeFromCycle(cycle);
      if (!time) continue;
      
      const cycleTotal = time.hours * 60 + time.minutes;
      let diff = cycleTotal - currentTotal;
      if (diff < 0) {
        diff += 24 * 60; // Next day
      }
      
      if (diff < minDiff) {
        minDiff = diff;
        bestCycle = cycle;
      }
    }
    
    return bestCycle || available[0] || '';
  };

  const handleMachineChange = (machineVal) => {
    setSterileMachine(machineVal);
    setSterileCycle(getAutoSelectedCycle(machineVal, cyclesList));
  };

  const [cyclesListLoaded, setCyclesListLoaded] = useState(false);

  useEffect(() => {
    if (cyclesList.length > 0 && !cyclesListLoaded) {
      setCyclesListLoaded(true);
      if (sterileMachine && !sterileCycle) {
        setSterileCycle(getAutoSelectedCycle(sterileMachine, cyclesList));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cyclesList, sterileMachine, sterileCycle, cyclesListLoaded]);

  // State to manage inline inputs for adding new options
  /* eslint-disable no-unused-vars */
  const [showAddPreparer, setShowAddPreparer] = useState(false);
  const [newPreparerName, setNewPreparerName] = useState('');
  const [showAddChecker, setShowAddChecker] = useState(false);
  const [newCheckerName, setNewCheckerName] = useState('');
  const [showAddMachine, setShowAddMachine] = useState(false);
  const [newMachineName, setNewMachineName] = useState('');
  const [showAddCycle, setShowAddCycle] = useState(false);
  const [newCycleName, setNewCycleName] = useState('');
  /* eslint-enable no-unused-vars */

  const [activeManagerField, setActiveManagerField] = useState(null); // null | 'preparer' | 'checker' | 'machine' | 'cycle'
  const [editingOptionIndex, setEditingOptionIndex] = useState(null);
  const [editingOptionValue, setEditingOptionValue] = useState('');
  const [deleteConfirmTarget, setDeleteConfirmTarget] = useState(null); // null | { item, index, managerDetails }

  const syncOptionToSupabase = async (category, oldVal, newVal) => {
    if (!supabase) return;
    try {
      if (oldVal === null && newVal !== null) {
        // ADD
        const { error } = await supabase.from('sterile_options').insert([{ category, value: newVal }]);
        if (error && error.code !== '23505') { // Ignore duplicate key errors
          console.error(`Error adding ${category} to Supabase:`, error.message);
        }
      } else if (oldVal !== null && newVal !== null) {
        // EDIT
        const { error } = await supabase.from('sterile_options').update({ value: newVal }).eq('category', category).eq('value', oldVal);
        if (error) {
          console.error(`Error editing ${category} in Supabase:`, error.message);
        }
      } else if (oldVal !== null && newVal === null) {
        // DELETE
        const { error } = await supabase.from('sterile_options').delete().eq('category', category).eq('value', oldVal);
        if (error) {
          console.error(`Error deleting ${category} from Supabase:`, error.message);
        }
      }
    } catch (err) {
      console.error("Supabase sync failed:", err);
    }
  };

  const getActiveManagerDetails = () => {
    if (!activeManagerField) return null;
    switch (activeManagerField) {
      case 'preparer':
        return {
          title: 'ผู้เตรียม',
          list: preparersList,
          onSave: (updated, oldVal, newVal) => {
            setPreparersList(updated);
            localStorage.setItem('preparersList', JSON.stringify(updated));
            if (preparedBy === oldVal) setPreparedBy(newVal || '');
            syncOptionToSupabase('preparer', oldVal, newVal);
          }
        };
      case 'checker':
        return {
          title: 'ผู้ตรวจ',
          list: checkersList,
          onSave: (updated, oldVal, newVal) => {
            setCheckersList(updated);
            localStorage.setItem('checkersList', JSON.stringify(updated));
            if (checkedBy === oldVal) setCheckedBy(newVal || '');
            syncOptionToSupabase('checker', oldVal, newVal);
          }
        };
      case 'machine':
        return {
          title: 'เครื่องนึ่ง',
          list: machinesList,
          onSave: (updated, oldVal, newVal) => {
            setMachinesList(updated);
            localStorage.setItem('machinesList', JSON.stringify(updated));
            if (sterileMachine === oldVal) setSterileMachine(newVal || '');
            syncOptionToSupabase('machine', oldVal, newVal);
          }
        };
      case 'cycle':
        return {
          title: 'รอบที่',
          list: cyclesList,
          onSave: (updated, oldVal, newVal) => {
            setCyclesList(updated);
            localStorage.setItem('cyclesList', JSON.stringify(updated));
            if (sterileCycle === oldVal) setSterileCycle(newVal || '');
            syncOptionToSupabase('cycle', oldVal, newVal);
          }
        };
      default:
        return null;
    }
  };

  const managerDetails = getActiveManagerDetails();

  /* eslint-disable no-unused-vars */
  const saveNewPreparer = () => {
    const name = newPreparerName.trim();
    if (!name) return;
    if (preparersList.includes(name)) {
      alert("มีรายชื่อนี้อยู่แล้ว");
      return;
    }
    const updated = [...preparersList, name];
    setPreparersList(updated);
    localStorage.setItem('preparersList', JSON.stringify(updated));
    setNewPreparerName('');
    setShowAddPreparer(false);
    syncOptionToSupabase('preparer', null, name);
  };

  const saveNewChecker = () => {
    const name = newCheckerName.trim();
    if (!name) return;
    if (checkersList.includes(name)) {
      alert("มีรายชื่อนี้อยู่แล้ว");
      return;
    }
    const updated = [...checkersList, name];
    setCheckersList(updated);
    localStorage.setItem('checkersList', JSON.stringify(updated));
    setNewCheckerName('');
    setShowAddChecker(false);
    syncOptionToSupabase('checker', null, name);
  };

  const saveNewMachine = () => {
    const name = newMachineName.trim();
    if (!name) return;
    if (machinesList.includes(name)) {
      alert("มีเครื่องนี้อยู่แล้ว");
      return;
    }
    const updated = [...machinesList, name];
    setMachinesList(updated);
    localStorage.setItem('machinesList', JSON.stringify(updated));
    setNewMachineName('');
    setShowAddMachine(false);
    syncOptionToSupabase('machine', null, name);
  };

  const saveNewCycle = () => {
    const name = newCycleName.trim();
    if (!name) return;
    if (cyclesList.includes(name)) {
      alert("มีรอบนี้อยู่แล้ว");
      return;
    }
    const updated = [...cyclesList, name];
    setCyclesList(updated);
    localStorage.setItem('cyclesList', JSON.stringify(updated));
    setNewCycleName('');
    setShowAddCycle(false);
    syncOptionToSupabase('cycle', null, name);
  };
  /* eslint-enable no-unused-vars */

  const [numBlankLabels, setNumBlankLabels] = useState('');
  const [blankLabelsToPrint, setBlankLabelsToPrint] = useState([]);
  const [scanBlankItem, setScanBlankItem] = useState(null);
  const [blankBarcodeInput, setBlankBarcodeInput] = useState('');
  const [scannedBlankList, setScannedBlankList] = useState([]);
  const [isBinding, setIsBinding] = useState(false);

  const [printPreviewItem, setPrintPreviewItem] = useState(null);
  const [packagingItem, setPackagingItem] = useState(null);
  const [selectedPrints, setSelectedPrints] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printedImagesPreview, setPrintedImagesPreview] = useState([]);

  const [historyFilterDate, setHistoryFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [washingHistoryDate, setWashingHistoryDate] = useState(new Date().toISOString().split('T')[0]);
  const [assemblyHistoryDate, setAssemblyHistoryDate] = useState(new Date().toISOString().split('T')[0]);
  const [sterileHistoryDate, setSterileHistoryDate] = useState(new Date().toISOString().split('T')[0]);
  const [dispenseHistoryDate, setDispenseHistoryDate] = useState(new Date().toISOString().split('T')[0]);
  const [clinicOrderHistoryDate, setClinicOrderHistoryDate] = useState(new Date().toISOString().split('T')[0]);

  const [barcodeInput, setBarcodeInput] = useState('');
  const [targetWarehouse, setTargetWarehouse] = useState('');
  const [actionType, setActionType] = useState('increase');
  const [washingSuccessList, setWashingSuccessList] = useState([]);
  const [washingErrorList, setWashingErrorList] = useState([]);
  const [sterileSuccessList, setSterileSuccessList] = useState([]);
  const [sterileProblemList, setSterileProblemList] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [scanErrorMessage, setScanErrorMessage] = useState('');
  const [orderBasket, setOrderBasket] = useState([]);
  const [orderNote, setOrderNote] = useState('');
  const [clinicOrderSearchQuery, setClinicOrderSearchQuery] = useState('');
  const [orderedByName, setOrderedByName] = useState('');
  const [isCartModalOpen, setIsCartModalOpen] = useState(false);

  const [traceInput, setTraceInput] = useState('');
  const [traceData, setTraceData] = useState(null);
  const [traceLogs, setTraceLogs] = useState([]);
  const [traceError, setTraceError] = useState('');

  const [targetBox, setTargetBox] = useState(null);
  const [targetBoxForPack, setTargetBoxForPack] = useState('');
  const [isSterileModalOpen, setIsSterileModalOpen] = useState(false);
  const [sterileErrorAlert, setSterileErrorAlert] = useState(null);
  const [selectedTimelineItem, setSelectedTimelineItem] = useState(null);
  const [isDispenseModalOpen, setIsDispenseModalOpen] = useState(false);
  const [sterileProcessingState, setSterileProcessingState] = useState(null); // null | 'processing' | 'success'
  const [sterileConfirmModal, setSterileConfirmModal] = useState(null); // null | { message: '', onConfirm: () => void }

  const [filterRecallItemName, setFilterRecallItemName] = useState('');
  const [filterRecallBarcode, setFilterRecallBarcode] = useState('');
  const [filterRecallMfgDate, setFilterRecallMfgDate] = useState('');
  const [filterRecallPreparedBy, setFilterRecallPreparedBy] = useState('');
  const [filterRecallCheckedBy, setFilterRecallCheckedBy] = useState('');
  const [filterRecallMachine, setFilterRecallMachine] = useState('');
  const [filterRecallCycle, setFilterRecallCycle] = useState('');
  const [recallSearchResults, setRecallSearchResults] = useState([]);
  const [selectedRecallIds, setSelectedRecallIds] = useState([]);
  const [isRecallSearching, setIsRecallSearching] = useState(false);
  const [selectedDetailCountName, setSelectedDetailCountName] = useState(null);
  const [detailModalItems, setDetailModalItems] = useState([]);
  const [recallScanPromptItem, setRecallScanPromptItem] = useState(null);
  const [allRecallBatches, setAllRecallBatches] = useState([]);
  const [isFetchingAllBatches, setIsFetchingAllBatches] = useState(false);
  const [recallRoundsPage, setRecallRoundsPage] = useState(1);
  const [recallReason, setRecallReason] = useState('');
  const [recallImageUrl, setRecallImageUrl] = useState('');
  const [isRecallUploading, setIsRecallUploading] = useState(false);
  const [showRecallUploadPopup, setShowRecallUploadPopup] = useState(false);
  const recallCameraInputRef = useRef(null);
  const recallGalleryInputRef = useRef(null);
  const [cancelRecallPromptItem, setCancelRecallPromptItem] = useState(null); // null | { item, reason: '' }
  const [zoomedRecallImageUrl, setZoomedRecallImageUrl] = useState('');
  const [activeRecallBatchIdForUpload, setActiveRecallBatchIdForUpload] = useState(null);

  const getUserNameByEmail = (email) => {
    if (!email) return 'ไม่ระบุ';
    if (email === 'guest@hospital.com') return 'Guest User';
    const profile = userProfiles.find(p => p.email && p.email.toLowerCase() === email.toLowerCase());
    return profile && profile.name ? profile.name : email;
  };

  const getTimeline = (instr, barcode) => {
    const steps = [];
    const addStep = (name, timestamp, details = {}) => {
      if (steps.length > 0 && steps[steps.length - 1].name === name) {
        return;
      }
      steps.push({ name, timestamp, ...details });
    };

    const targetBarcode = barcode || (instr ? instr.unique_barcode : null);

    if (targetBarcode) {
      const barcodeLogs = logs.filter(l => l.action && l.action.includes(targetBarcode));
      const sortedLogs = [...barcodeLogs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      sortedLogs.forEach(l => {
        const wh = (l.warehouse || "").trim();
        const act = l.action || "";

        if (wh.includes("ล้าง")) {
          addStep("ห้องล้าง", l.timestamp, { action: act, warehouse: wh });
        } else if (act.includes("นึ่ง") || act.includes("sterile-ready")) {
          addStep("ห้องนึ่ง", l.timestamp, { action: act, warehouse: wh });
        } else if (wh.includes("sterile")) {
          addStep("ห้อง supply", l.timestamp, { action: act, warehouse: wh });
        } else {
          addStep(wh || "อื่นๆ", l.timestamp, { action: act, warehouse: wh });
        }
      });
    }

    if (instr) {
      if (steps.length === 0) {
        if (instr.status === 'sterile-pending') {
          addStep("ห้องล้าง", instr.last_updated);
        } else if (instr.status === 'sterile-ready') {
          addStep("ห้องล้าง", instr.last_updated);
          addStep("ห้องนึ่ง", instr.last_updated);
          addStep("ห้อง supply", instr.last_updated);
        } else if (instr.status === 'in-use') {
          addStep("ห้องล้าง", instr.last_updated);
          addStep("ห้องนึ่ง", instr.last_updated);
          addStep("ห้อง supply", instr.last_updated);
          const clinicName = (instr.location || "").replace(/^\s*-\s*/, "").trim() || "ห้องฟัน";
          addStep(clinicName, instr.last_updated);
        }
      } else {
        const lastStepName = steps[steps.length - 1].name;
        if (instr.status === 'sterile-ready') {
          if (lastStepName === "ห้องนึ่ง") {
            addStep("ห้อง supply", instr.last_updated);
          }
        } else if (instr.status === 'in-use') {
          if (lastStepName === "ห้องนึ่ง") {
            addStep("ห้อง supply", instr.last_updated);
          }
          const clinicName = (instr.location || "").replace(/^\s*-\s*/, "").trim() || "ห้องฟัน";
          addStep(clinicName, instr.last_updated);
        }
      }
    } else {
      if (steps.length === 0) {
        addStep("ห้อง supply", new Date().toISOString());
      }
    }

    return steps;
  };

  const handleCloseSterileModal = () => {
    setIsSterileModalOpen(false);
    setSterileSuccessList([]);
    setSterileProblemList([]);
    setTargetBox(null);
    setBarcodeInput('');
    setScanErrorMessage('');
    setSterileErrorAlert(null);
  };

  const showSterileError = (message) => {
    setSterileErrorAlert(message);
    setBarcodeInput('');
  };


  // 🌟 States: สำหรับกล้องมือถือ
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraTargetField, setCameraTargetField] = useState(''); // 'blank', 'main', 'trace'
  const [cameraScanSuccessData, setCameraScanSuccessData] = useState(null);

  // 🌟 ฟังก์ชันรับค่าจากกล้อง
  const handleCameraScanSuccess = async (decodedText, keepOpen = false) => {
    if (!keepOpen) {
      setIsCameraOpen(false); // ปิดกล้อง
    }

    // ตรวจสอบข้อมูลบาร์โค้ดใน instruments
    let instrData = instruments.find(i => i.unique_barcode === decodedText);
    if (!instrData && supabase) {
      try {
        const { data, error } = await supabase
          .from('instruments')
          .select('*')
          .eq('unique_barcode', decodedText)
          .single();
        if (!error && data) {
          instrData = data;
        }
      } catch (err) {
        console.error("Supabase search error during camera scan:", err);
      }
    }

    // แยกการทำงานตามช่องที่กดเปิดกล้อง
    if (cameraTargetField === 'blank') {
      setBlankBarcodeInput(decodedText);
      // นำ Logic การตรวจสอบจาก onChange เดิมมาใส่ที่นี่ด้วย
      if (decodedText.length === 13 || decodedText.length === 14) {
        if (!validateUID(decodedText)) return alert("❌ บาร์โค้ดไม่ถูกต้อง (Checksum ผิด)");
        if (instruments.find(i => i.unique_barcode === decodedText)) return alert("❌ บาร์โค้ดนี้ถูกผูกข้อมูลไปแล้ว!");
        if (scannedBlankList.includes(decodedText)) return alert("❌ คุณสแกนบาร์โค้ดนี้ไปแล้วในรอบนี้!");
        setScannedBlankList(prev => [decodedText, ...prev]);
        setBlankBarcodeInput('');
      }
    }
    else if (cameraTargetField === 'main') {
      setBarcodeInput(decodedText);
      // ยิงฟังก์ชันของหน้านั้นๆ ทันที
      if (currentPage === 'washing') handleWashingScan(decodedText);
      else if (currentPage === 'supply') {
        if (isDispenseModalOpen) handleDispenseScan(decodedText);
        else handleSterileScan(decodedText, keepOpen);
      }
      else if (currentPage === 'dispense') handleDispenseScan(decodedText);

      // หากรหัสถูกต้อง (พบในระบบ) ให้แสดง popup
      if (instrData) {
        setCameraScanSuccessData(instrData);
        if (scanTimeoutRef.current) {
          clearTimeout(scanTimeoutRef.current);
        }
        if (keepOpen) {
          scanTimeoutRef.current = setTimeout(() => {
            setCameraScanSuccessData(null);
            scanTimeoutRef.current = null;
          }, 3000);
        }
      }
    }
    else if (cameraTargetField === 'trace') {
      setTraceInput(decodedText);
      if (instrData) {
        setTraceData(instrData);
        setCameraScanSuccessData(instrData);
        try {
          if (supabase) {
            const { data: logsData } = await supabase
              .from('logs')
              .select('*')
              .like('action', `%${decodedText}%`)
              .order('timestamp', { ascending: true });
            setTraceLogs(logsData || []);
          }
        } catch (err) {
          console.error("Error fetching trace logs:", err);
        }
      } else {
        setTraceError("❌ ไม่พบรหัสบาร์โค้ดนี้ในระบบ กรุณาตรวจสอบความถูกต้อง");
        setTraceData(null);
        setTraceLogs([]);
      }
    }
    else if (cameraTargetField === 'inspection') {
      if (!validateUID(decodedText)) {
        alert("❌ บาร์โค้ดไม่ถูกต้อง (Checksum ผิด)");
        return;
      }
      let isUsed = instruments.some(i => i.unique_barcode === decodedText && i.is_active && i.id !== selectedInspectionItem?.id);
      if (!isUsed && supabase) {
        try {
          const { data, error } = await supabase
            .from('instruments')
            .select('id')
            .eq('unique_barcode', decodedText)
            .eq('is_active', true)
            .maybeSingle();
          if (!error && data && data.id !== selectedInspectionItem?.id) {
            isUsed = true;
          }
        } catch (err) {
          console.error("Supabase search error during inspection camera scan:", err);
        }
      }
      if (isUsed) {
        alert("❌ บาร์โค้ดนี้ถูกผูกข้อมูลไปแล้วในระบบ!");
        return;
      }
      setSelectedInspectionItem(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          unique_barcode: decodedText
        };
      });
      setIsBarcodeScanned(true);
      alert("✅ เปลี่ยนบาร์โค้ดเป็น: " + decodedText);
    }
    else if (cameraTargetField === 'direct-card-inspection') {
      if (!selectedInspectionItem) return;
      if (!validateUID(decodedText)) {
        alert("❌ สแกนไม่สำเร็จ: บาร์โค้ดไม่ถูกต้อง (Checksum ผิด)");
        return;
      }
      let isUsed = instruments.some(i => i.unique_barcode === decodedText && i.is_active && i.id !== selectedInspectionItem?.id);
      if (!isUsed && supabase) {
        try {
          const { data, error } = await supabase
            .from('instruments')
            .select('id')
            .eq('unique_barcode', decodedText)
            .eq('is_active', true)
            .maybeSingle();
          if (!error && data && data.id !== selectedInspectionItem?.id) {
            isUsed = true;
          }
        } catch (err) {
          console.error("Supabase search error during direct card inspection camera scan:", err);
        }
      }
      if (isUsed) {
        alert("❌ สแกนไม่สำเร็จ: บาร์โค้ดนี้ถูกผูกข้อมูลไปแล้วในระบบ!");
        return;
      }

      try {
        const today = new Date();
        if (pkgType === 'cloth') today.setDate(today.getDate() + 7);
        else today.setMonth(today.getMonth() + 6);
        const calculatedExpDate = today.toISOString().split('T')[0];
        const calculatedMfgDate = new Date().toISOString().split('T')[0];

        // Resolve preparedBy for this item
        const assemblerEmailPrefix = selectedInspectionItem.details?.assembledBy || '';
        const assemblerProfile = userProfiles.find(p => p.email.toLowerCase().startsWith(assemblerEmailPrefix.toLowerCase()));
        const preparedName = assemblerProfile?.name || assemblerEmailPrefix || '';
        const finalPrepared = preparedName || preparedBy;

        if (finalPrepared.trim() === checkedBy.trim()) {
          alert("❌ สแกนไม่สำเร็จ: ผู้เตรียมและผู้ตรวจต้องไม่เป็นคนเดียวกัน เพื่อป้องกันความผิดพลาด");
          return;
        }

        const detailsObj = {
          ...selectedInspectionItem.details,
          mfgDate: calculatedMfgDate,
          expDate: calculatedExpDate,
          preparedBy: preparedName || preparedBy,
          checkedBy: checkedBy,
          machine: sterileMachine,
          cycle: sterileCycle,
          packaging: pkgType === 'cloth' ? 'ห่อผ้า' : 'ซอง sterile',
          isPrinted: false
        };

        const { error: updateError } = await supabase
          .from('instruments')
          .update({
            unique_barcode: decodedText,
            status: 'sterile-pending',
            location: '   - ห้องล้างเครื่องมือ',
            details: detailsObj,
            last_updated: new Date().toISOString()
          })
          .eq('id', selectedInspectionItem.id);

        if (updateError) throw updateError;

        const ps = supplies.find(s => s.master_id === selectedInspectionItem.master_id && s.warehouse_name.includes("กำลัง sterile"));
        if (ps) {
          const newQty = ps.item_amount + 1;
          await supabase
            .from('supplies')
            .update({ item_amount: newQty, last_updated: new Date().toISOString() })
            .eq('id', ps.id);
        }

        addLog(selectedInspectionItem.item_name, "ห้องตรวจเครื่องมือ", `ตรวจสอบสำเร็จและเตรียมพิมพ์บาร์โค้ด ${decodedText}`);

        const updatedItem = {
          ...selectedInspectionItem,
          status: 'sterile-pending',
          location: '   - ห้องล้างเครื่องมือ',
          unique_barcode: decodedText,
          details: detailsObj
        };

        setInstruments(prev => prev.map(i => i.id === selectedInspectionItem.id ? updatedItem : i));
        setSelectedInspectionItem(null);
        
        setSuccessModal({
          title: "สแกนและบันทึกสำเร็จ",
          message: `สแกนสำเร็จ ทำการบันทึกรหัส QR code: ${decodedText} กับเครื่องมือ ${selectedInspectionItem.item_name} เรียบร้อยแล้ว\n\nรายละเอียด:\nผู้เตรียม: ${preparedName || preparedBy}\nผู้ตรวจ: ${checkedBy}\nเครื่องนึ่ง: ${sterileMachine}\nรอบที่: ${sterileCycle}`,
          hideButton: true
        });

        setTimeout(() => {
          setSuccessModal(null);
        }, 1000);

        fetchData();
      } catch (err) {
        console.error(err);
        alert("❌ สแกนไม่สำเร็จ: " + err.message);
      }
    }
  };

  const barcodeInputRef = useRef(null);
  const scanTimeoutRef = useRef(null);

  const isSterileInfoComplete = preparedBy.trim() !== '' && checkedBy.trim() !== '' && sterileMachine.trim() !== '' && sterileCycle !== '';

  const handleBlurToFocus = () => {
    if (window.isConfirmingDialogActive) return;
    setTimeout(() => {
      if (window.isConfirmingDialogActive) return;
      const active = document.activeElement;
      if (active && ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(active.tagName)) return;
      if (barcodeInputRef.current) barcodeInputRef.current.focus();
    }, 150);
  };

  useEffect(() => {
    if (['washing', 'supply', 'dispense', 'trace'].includes(currentPage)) {
      setTimeout(() => { if (barcodeInputRef.current) barcodeInputRef.current.focus(); }, 200);
    }
  }, [currentPage]);

  useEffect(() => {
    if (isSterileModalOpen) {
      setTimeout(() => { if (barcodeInputRef.current) barcodeInputRef.current.focus(); }, 200);
    }
  }, [isSterileModalOpen]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const roomParam = params.get('room');
    if (isRiskSubdomain()) {
      setCurrentPage('dental-risk-portal');
    } else if (roomParam) {
      setTargetWarehouse(roomParam);
      setCurrentPage('clinic-order');
    } else if (initialPage) {
      setCurrentPage(initialPage);
    }
  }, [initialPage]);


  useEffect(() => {
    // 🛡️ CSSD Data Isolation: If in Risk Subdomain, do NOT fetch CSSD data (item_master, supplies, orders, etc.)
    // and do NOT subscribe to CSSD real-time channels.
    if (isRiskSubdomain()) {
      syncUserProfile().then(() => {
        fetchUserProfiles();
      });
      return;
    }

    fetchData();
    fetchRoleDefaultPages();
    syncUserProfile().then(() => {
      fetchUserProfiles();
    });
    let debounceTimer;
    const handleRealtimeUpdate = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => { fetchData(); fetchUserProfiles(); }, 500);
    };
    const channel = supabase.channel('public-db-changes').on('postgres_changes', { event: '*', schema: 'public' }, handleRealtimeUpdate).subscribe();
    return () => { supabase.removeChannel(channel); clearTimeout(debounceTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Auto-set default checker to current logged-in user display name or email prefix
  useEffect(() => {
    if (user?.email && userProfiles.length > 0) {
      const currentProfile = userProfiles.find(p => p.email.toLowerCase() === user.email.toLowerCase());
      const currentUserName = currentProfile?.name || user.email.split('@')[0];
      if (currentUserName && checkedBy !== currentUserName) {
        setCheckedBy(currentUserName);
      }
    }
  }, [user, userProfiles, checkedBy]);

  const loadOptionsFromLocalStorage = () => {
    const pSaved = localStorage.getItem('preparersList');
    setPreparersList(pSaved ? JSON.parse(pSaved) : []);
    const cSaved = localStorage.getItem('checkersList');
    setCheckersList(cSaved ? JSON.parse(cSaved) : []);
    const mSaved = localStorage.getItem('machinesList');
    setMachinesList(mSaved ? JSON.parse(mSaved) : ['หน่วยจ่ายกลาง', 'Autoclave 1', 'Autoclave 2', 'Autoclave 3']);
    const cySaved = localStorage.getItem('cyclesList');
    setCyclesList(cySaved ? JSON.parse(cySaved) : [
      'หน่วยจ่ายกลาง:รอบ 08:00 น.',
      'หน่วยจ่ายกลาง:รอบ 10:00 น.',
      'หน่วยจ่ายกลาง:รอบ 13:00 น.',
      'หน่วยจ่ายกลาง:รอบ 15:00 น.',
      'Autoclave 1:รอบ 10:00 น.',
      'Autoclave 1:รอบ 16:00 น.',
      'Autoclave 2:รอบ 11:00 น.',
      'Autoclave 3:รอบ 13:00 น.'
    ]);
  };

  const fetchData = async () => {
    const { data: master } = await supabase.from('item_master').select('*').order('created_at', { ascending: false });
    const { data: sups } = await supabase.from('supplies').select('*');
    const { data: insts } = await supabase.from('instruments').select('*').order('last_updated', { ascending: false }).limit(500);
    const { data: ords } = await supabase.from('orders').select('*').order('timestamp', { ascending: false });
    const { data: lg } = await supabase.from('logs').select('*').order('timestamp', { ascending: false }).limit(500);

    if (master) setItemMaster(master);
    if (sups) setSupplies(sups);
    if (insts) setInstruments(insts);
    if (ords) setOrders(ords);
    if (lg) setLogs(lg);

    try {
      if (supabase) {
        const { data: tg, error: tgErr } = await supabase.from('instrument_tags').select('*').order('id', { ascending: true });
        if (tgErr) throw tgErr;

        let activeTags = tg || [];
        const hasNonSterile = activeTags.some(t => t.name.toUpperCase() === 'NON-STERILE');
        if (!hasNonSterile) {
          const newTag = {
            name: 'NON-STERILE',
            description: 'สถานะยังไม่ได้ผ่านการฆ่าเชื้อ (Non-Sterile)',
            image_url: '/non_sterile_tag.png'
          };
          const { data: inserted, error: insertErr } = await supabase.from('instrument_tags').insert([newTag]).select();
          if (!insertErr && inserted && inserted.length > 0) {
            activeTags = [...activeTags, inserted[0]];
          }
        }
        setInstrumentTags(activeTags);
      }
    } catch (e) {
      console.warn("Could not load tags in main App, using localStorage:", e.message);
      const localTags = localStorage.getItem('mock_instrument_tags');
      let parsedTags = localTags ? JSON.parse(localTags) : [];
      const hasNonSterile = parsedTags.some(t => t.name.toUpperCase() === 'NON-STERILE');
      if (!hasNonSterile) {
        const newId = parsedTags.length > 0 ? Math.max(...parsedTags.map(t => t.id)) + 1 : 1;
        parsedTags.push({
          id: newId,
          name: 'NON-STERILE',
          description: 'สถานะยังไม่ได้ผ่านการฆ่าเชื้อ (Non-Sterile)',
          image_url: '/non_sterile_tag.png',
          created_at: new Date().toISOString()
        });
        localStorage.setItem('mock_instrument_tags', JSON.stringify(parsedTags));
      }
      setInstrumentTags(parsedTags);
    }

    try {
      const { data: optData, error: optErr } = await supabase.from('sterile_options').select('*');
      if (optErr) {
        console.warn("Could not load options from Supabase, using localStorage:", optErr.message);
        loadOptionsFromLocalStorage();
      } else if (optData) {
        const preps = optData.filter(x => x.category === 'preparer').map(x => x.value);
        const checks = optData.filter(x => x.category === 'checker').map(x => x.value);
        const macs = optData.filter(x => x.category === 'machine').map(x => x.value);
        const cycs = optData.filter(x => x.category === 'cycle').map(x => x.value);

        setPreparersList(preps.length > 0 ? preps : []);
        setCheckersList(checks.length > 0 ? checks : []);
        setMachinesList(macs.length > 0 ? macs : ['หน่วยจ่ายกลาง', 'Autoclave 1', 'Autoclave 2', 'Autoclave 3']);
        setCyclesList(cycs.length > 0 ? cycs : [
          'หน่วยจ่ายกลาง:รอบ 08:00 น.',
          'หน่วยจ่ายกลาง:รอบ 10:00 น.',
          'หน่วยจ่ายกลาง:รอบ 13:00 น.',
          'หน่วยจ่ายกลาง:รอบ 15:00 น.',
          'Autoclave 1:รอบ 10:00 น.',
          'Autoclave 1:รอบ 16:00 น.',
          'Autoclave 2:รอบ 11:00 น.',
          'Autoclave 3:รอบ 13:00 น.'
        ]);
      }
    } catch (err) {
      console.warn("Error loading options, using localStorage:", err);
      loadOptionsFromLocalStorage();
    }
  };

  const fetchUserProfiles = async () => {
    if (!supabase) return;
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) {
        if (error.code === 'PGRST205' || error.message?.includes("not find the table")) {
          setDbError('table_not_found');
          const localData = localStorage.getItem('mock_user_profiles');
          if (localData) {
            setUserProfiles(JSON.parse(localData));
          } else {
            const initialMock = [
              { id: '1', email: 'nuntsak@gmail.com', name: 'นันทศักดิ์', role: 'admin', allowed_pages: 'consumable,durable,handpiece,packaging,washing,supply,dispense,clinic-order,barcode-tracking,trace', created_at: new Date().toISOString(), last_login: new Date().toISOString() },
              { id: '2', email: 'nuntsak@hotmail.com', name: 'นันทศักดิ์ (Hotmail)', role: 'admin', allowed_pages: 'consumable,durable,handpiece,packaging,washing,supply,dispense,clinic-order,barcode-tracking,trace', created_at: new Date().toISOString(), last_login: new Date().toISOString() },
              { id: '3', email: 'cssd_staff1@hospital.com', name: 'สมชาย (Staff 1)', role: 'ผู้ช่วยทันตแพทย์', allowed_pages: 'washing,packaging,supply,dispense', created_at: new Date().toISOString(), last_login: new Date().toISOString() },
              { id: '4', email: 'clinic_user1@hospital.com', name: 'หมอสมศรี (Clinic)', role: 'ทันตแพทย์', allowed_pages: 'consumable,durable,handpiece,packaging,washing,supply,dispense,clinic-order,barcode-tracking,trace', created_at: new Date().toISOString(), last_login: new Date().toISOString() }
            ];
            localStorage.setItem('mock_user_profiles', JSON.stringify(initialMock));
            setUserProfiles(initialMock);
          }
        } else {
          console.error("Error fetching user profiles:", error);
        }
      } else if (data) {
        setUserProfiles(data);
        setDbError(null);
      }
    } catch (err) {
      console.error("Exception fetching user profiles:", err);
    }
  };

  const syncUserProfile = async () => {
    if (!supabase || !user?.email) return;
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('email', user.email)
        .single();

      if (error && (error.code === 'PGRST205' || error.message?.includes("not find the table"))) {
        setDbError('table_not_found');
        let localData = localStorage.getItem('mock_user_profiles');
        let profiles = localData ? JSON.parse(localData) : [];
        let profile = profiles.find(p => p.email.toLowerCase() === user.email.toLowerCase());

        if (profile) {
          setActiveUserRole(profile.role);
          setActiveAllowedPages(profile.allowed_pages);
        } else {
          const isInitialAdmin = ADMIN_EMAILS.includes(user.email);
          const initialRole = isInitialAdmin ? 'admin' : 'อื่นๆ';
          const initialAllowed = isInitialAdmin ? 'consumable,durable,handpiece,packaging,washing,supply,dispense,clinic-order,barcode-tracking,trace' : 'clinic-order,barcode-tracking,trace';

          const newProfile = {
            id: Math.random().toString(),
            email: user.email.toLowerCase(),
            name: user.email.split('@')[0],
            role: initialRole,
            allowed_pages: initialAllowed,
            created_at: new Date().toISOString(),
            last_login: new Date().toISOString()
          };
          profiles.push(newProfile);
          localStorage.setItem('mock_user_profiles', JSON.stringify(profiles));
          setActiveUserRole(initialRole);
          setActiveAllowedPages(initialAllowed);
        }
      } else if (data) {
        setActiveUserRole(data.role);
        setActiveAllowedPages(data.allowed_pages);
        await supabase
          .from('user_profiles')
          .update({ last_login: new Date().toISOString() })
          .eq('email', user.email);
      } else {
        const isInitialAdmin = ADMIN_EMAILS.includes(user.email);
        const initialRole = isInitialAdmin ? 'admin' : 'อื่นๆ';
        const initialAllowed = isInitialAdmin ? 'consumable,durable,handpiece,packaging,washing,supply,dispense,clinic-order,barcode-tracking,trace' : 'clinic-order,barcode-tracking,trace';

        const newProfile = {
          email: user.email.toLowerCase(),
          name: user.email.split('@')[0],
          role: initialRole,
          allowed_pages: initialAllowed,
          created_at: new Date().toISOString(),
          last_login: new Date().toISOString()
        };
        await supabase.from('user_profiles').insert([newProfile]);
        setActiveUserRole(initialRole);
        setActiveAllowedPages(initialAllowed);
      }
    } catch (err) {
      console.error("Exception syncing user profile:", err);
    }
  };

  const fetchRoleDefaultPages = async () => {
    if (!supabase) return;
    try {
      const { data, error } = await supabase
        .from('role_default_pages')
        .select('*');

      if (error) {
        if (error.code === 'PGRST205' || error.message?.includes("not find the table")) {
          const localDefaults = localStorage.getItem('mock_role_defaults');
          if (localDefaults) {
            setRoleDefaults(JSON.parse(localDefaults));
          }
        } else {
          console.error("Error fetching role defaults:", error);
        }
      } else if (data && data.length > 0) {
        const defaultsMap = {};
        data.forEach(d => {
          defaultsMap[d.role] = d.allowed_pages;
        });
        setRoleDefaults(prev => ({ ...prev, ...defaultsMap }));
      }
    } catch (err) {
      console.error("Exception fetching role defaults:", err);
    }
  };

  const handleUpdateRoleDefaultPages = async (role, allowed_pages) => {
    setDefaultSaveStatus('กำลังบันทึก...');
    setRoleDefaults(prev => {
      const next = { ...prev, [role]: allowed_pages };
      localStorage.setItem('mock_role_defaults', JSON.stringify(next));
      return next;
    });

    if (dbError === 'table_not_found') {
      setTimeout(() => setDefaultSaveStatus('✓ บันทึกสำเร็จ!'), 400);
    } else {
      try {
        const { error } = await supabase
          .from('role_default_pages')
          .upsert({ role, allowed_pages });
        if (error) {
          console.error("Error updating role defaults:", error);
          setDefaultSaveStatus('✗ บันทึกไม่สำเร็จ');
        } else {
          setDefaultSaveStatus('✓ บันทึกสำเร็จ!');
        }
      } catch (err) {
        console.error("Exception updating role defaults:", err);
        setDefaultSaveStatus('✗ บันทึกไม่สำเร็จ');
      }
    }
    setTimeout(() => setDefaultSaveStatus(''), 2500);
  };

  const handleUpdateRole = async (email, role) => {
    const defaultPages = roleDefaults[role] || '';
    setUserProfiles(prev => prev.map(p => p.email.toLowerCase() === email.toLowerCase() ? { ...p, role, allowed_pages: defaultPages } : p));

    if (dbError === 'table_not_found') {
      let localData = localStorage.getItem('mock_user_profiles');
      let profiles = localData ? JSON.parse(localData) : [];
      profiles = profiles.map(p => p.email.toLowerCase() === email.toLowerCase() ? { ...p, role, allowed_pages: defaultPages } : p);
      localStorage.setItem('mock_user_profiles', JSON.stringify(profiles));
    } else {
      try {
        await supabase
          .from('user_profiles')
          .update({ role, allowed_pages: defaultPages })
          .eq('email', email);
      } catch (err) {
        console.error("Exception updating user role:", err);
      }
    }
  };

  const handleUpdateAllowedPages = async (email, allowed_pages) => {
    setUserProfiles(prev => prev.map(p => p.email.toLowerCase() === email.toLowerCase() ? { ...p, allowed_pages } : p));

    if (dbError === 'table_not_found') {
      let localData = localStorage.getItem('mock_user_profiles');
      let profiles = localData ? JSON.parse(localData) : [];
      profiles = profiles.map(p => p.email.toLowerCase() === email.toLowerCase() ? { ...p, allowed_pages } : p);
      localStorage.setItem('mock_user_profiles', JSON.stringify(profiles));
    } else {
      try {
        await supabase
          .from('user_profiles')
          .update({ allowed_pages })
          .eq('email', email);
      } catch (err) {
        console.error("Exception updating user allowed pages:", err);
      }
    }
  };

  const handleUpdateNameLocal = (email, name) => {
    setUserProfiles(prev => prev.map(p => p.email.toLowerCase() === email.toLowerCase() ? { ...p, name } : p));
  };

  const handleUpdateNameDb = async (email, name) => {
    const trimmed = (name || '').trim();
    const parts = trimmed.split(/\s+/);
    const firstName = parts[0] || '';
    const lastName = parts.slice(1).join(' ') || '';

    if (dbError === 'table_not_found') {
      let localData = localStorage.getItem('mock_user_profiles');
      let profiles = localData ? JSON.parse(localData) : [];
      profiles = profiles.map(p => p.email.toLowerCase() === email.toLowerCase() ? { ...p, name: trimmed, first_name: firstName, last_name: lastName } : p);
      localStorage.setItem('mock_user_profiles', JSON.stringify(profiles));
    } else {
      try {
        await supabase
          .from('user_profiles')
          .update({ name: trimmed, first_name: firstName, last_name: lastName })
          .eq('email', email);
      } catch (err) {
        console.error("Exception updating user name database:", err);
      }
    }
  };

  const handleDeleteUserProfile = (profile) => {
    if (profile.email.toLowerCase() === user?.email?.toLowerCase()) {
      return alert("คุณไม่สามารถลบตัวเองได้");
    }
    setDeleteConfirmUser(profile);
  };

  const handleExecuteDeleteUserProfile = async () => {
    if (!deleteConfirmUser) return;
    const profile = deleteConfirmUser;
    setDeleteConfirmUser(null);

    if (dbError === 'table_not_found') {
      let localData = localStorage.getItem('mock_user_profiles');
      let profiles = localData ? JSON.parse(localData) : [];
      profiles = profiles.filter(p => p.email.toLowerCase() !== profile.email.toLowerCase());
      localStorage.setItem('mock_user_profiles', JSON.stringify(profiles));
      setUserProfiles(profiles);
      alert("ลบผู้ใช้งานสำเร็จ (จำลอง)");
    } else {
      let query = supabase.from('user_profiles').delete();
      if (profile.id) {
        query = query.eq('id', profile.id);
      } else {
        query = query.ilike('email', profile.email);
      }
      const { error } = await query;
      if (error) {
        alert("เกิดข้อผิดพลาดในการลบผู้ใช้งาน: " + error.message);
      } else {
        setUserProfiles(prev => prev.filter(p => p.email.toLowerCase() !== profile.email.toLowerCase()));
        alert("ลบผู้ใช้งานสำเร็จ!");
      }
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPasswordValue || newPasswordValue.length < 6) {
      return alert("รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร");
    }

    setIsResettingPassword(true);
    try {
      if (dbError === 'table_not_found') {
        setSuccessModal({
          title: "เปลี่ยนรหัสผ่านสำเร็จ!",
          message: `เปลี่ยนรหัสผ่านให้กับผู้ใช้งาน ${resetPasswordUser.email} สำเร็จ! (โหมดจำลอง)`
        });
        setResetPasswordUser(null);
        setNewPasswordValue('');
        return;
      }

      const { error } = await supabase.rpc('admin_reset_password', {
        target_email: resetPasswordUser.email,
        new_password: newPasswordValue
      });

      if (error) {
        if (error.code === 'PGRST202' || error.message?.includes('could not find') || error.message?.includes('does not exist')) {
          setSqlScriptToShow(true);
        } else {
          alert("เกิดข้อผิดพลาด: " + error.message);
        }
      } else {
        setSuccessModal({
          title: "เปลี่ยนรหัสผ่านสำเร็จ!",
          message: `เปลี่ยนรหัสผ่านให้กับผู้ใช้งาน ${resetPasswordUser.email} สำเร็จ!`
        });
        setResetPasswordUser(null);
        setNewPasswordValue('');
      }
    } catch (err) {
      console.error("Error calling admin_reset_password RPC:", err);
      alert("เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน");
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleUpdateSelfPassword = async (e) => {
    e.preventDefault();
    if (!selfCurrentPasswordValue) {
      return alert("กรุณากรอกรหัสผ่านปัจจุบัน");
    }
    if (!selfNewPasswordValue || selfNewPasswordValue.length < 6) {
      return alert("รหัสผ่านใหม่ต้องมีอย่างน้อย 6 ตัวอักษร");
    }
    if (selfNewPasswordValue !== selfConfirmNewPasswordValue) {
      return alert("รหัสผ่านใหม่และยืนยันรหัสผ่านใหม่ไม่ตรงกัน");
    }

    setIsUpdatingSelfPassword(true);
    try {
      // 1. ตรวจสอบรหัสผ่านปัจจุบันด้วยการลอง signInWithPassword
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: selfCurrentPasswordValue
      });

      if (authError) {
        return alert("รหัสผ่านปัจจุบันไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง");
      }

      // 2. ถ้าผ่าน จึงทำการอัปเดตรหัสผ่านใหม่
      const { error } = await supabase.auth.updateUser({
        password: selfNewPasswordValue
      });

      if (error) {
        alert("เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน: " + error.message);
      } else {
        setSuccessModal({
          title: "เปลี่ยนรหัสผ่านสำเร็จ!",
          message: "เปลี่ยนรหัสผ่านของคุณเรียบร้อยแล้ว กรุณาใช้รหัสผ่านใหม่นี้ในการเข้าใช้งานระบบครั้งถัดไป"
        });
        setShowSelfResetPasswordModal(false);
        setSelfCurrentPasswordValue('');
        setSelfNewPasswordValue('');
        setSelfConfirmNewPasswordValue('');
      }
    } catch (err) {
      console.error("Error updating own password:", err);
      alert("เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน");
    } finally {
      setIsUpdatingSelfPassword(false);
    }
  };

  const handleAddUserProfile = async (e) => {
    e.preventDefault();
    if (!newUserEmail.trim()) return alert("กรุณากรอกอีเมล");

    let emailToRegister = newUserEmail.trim();
    if (!emailToRegister.includes('@')) {
      emailToRegister = `${emailToRegister}@fangdent.com`;
    }

    const roleToAssign = newUserRole;
    const defaultPagesForUser = roleDefaults[newUserRole] || '';

    if (userProfiles.some(p => p.email.toLowerCase() === emailToRegister.toLowerCase())) {
      return alert("มีอีเมลนี้ในระบบแล้ว");
    }

    if (dbError === 'table_not_found') {
      let localData = localStorage.getItem('mock_user_profiles');
      let profiles = localData ? JSON.parse(localData) : [];
      const parts = newUserName.trim().split(/\s+/);
      const newP = {
        id: Math.random().toString(),
        email: emailToRegister.toLowerCase(),
        name: newUserName.trim() || emailToRegister.split('@')[0],
        first_name: parts[0] || '',
        last_name: parts.slice(1).join(' ') || '',
        is_activated: false,
        role: roleToAssign,
        allowed_pages: defaultPagesForUser,
        created_at: new Date().toISOString(),
        last_login: new Date().toISOString()
      };
      profiles.push(newP);
      localStorage.setItem('mock_user_profiles', JSON.stringify(profiles));
      setUserProfiles(profiles);
      setNewUserEmail('');
      setNewUserRole('อื่นๆ');
      setNewUserName('');
      setSuccessModal({
        title: "เพิ่มผู้ใช้งานสำเร็จ (จำลอง)",
        message: "ระบบบันทึกโปรไฟล์จำลองเรียบร้อยแล้ว (รอเปิดใช้งาน)"
      });
    } else {
      try {
        const tempPassword = 'password123';
        const { error } = await supabase.rpc('admin_create_user', {
          new_email: emailToRegister.toLowerCase(),
          new_password: tempPassword,
          new_role: roleToAssign,
          new_allowed_pages: defaultPagesForUser
        });

        const parts = newUserName.trim().split(/\s+/);
        const fName = parts[0] || '';
        const lName = parts.slice(1).join(' ') || '';

        if (error) {
          if (error.code === 'PGRST202' || error.message?.includes('could not find')) {
            const newP = {
              email: emailToRegister.toLowerCase(),
              name: newUserName.trim() || emailToRegister.split('@')[0],
              first_name: fName,
              last_name: lName,
              is_activated: false,
              role: roleToAssign,
              allowed_pages: defaultPagesForUser,
              created_at: new Date().toISOString(),
              last_login: new Date().toISOString()
            };
            const { error: insertErr } = await supabase.from('user_profiles').insert([newP]);
            if (insertErr) {
              alert("เกิดข้อผิดพลาดในการเพิ่มผู้ใช้: " + insertErr.message);
            } else {
              setNewUserEmail('');
              setNewUserRole('อื่นๆ');
              setNewUserName('');
              fetchUserProfiles();
              setSuccessModal({
                title: "เพิ่มโปรไฟล์สำเร็จ!",
                message: "เพิ่มโปรไฟล์สำเร็จเรียบร้อยแล้ว (รอเจ้าหน้าที่เปิดใช้งานครั้งแรก)"
              });
            }
          } else {
            alert("เกิดข้อผิดพลาดในการเพิ่มผู้ใช้: " + error.message);
          }
        } else {
          await supabase
            .from('user_profiles')
            .update({
              name: newUserName.trim(),
              first_name: fName,
              last_name: lName,
              is_activated: false
            })
            .eq('email', emailToRegister.toLowerCase());

          setNewUserEmail('');
          setNewUserRole('อื่นๆ');
          setNewUserName('');
          fetchUserProfiles();
          setSuccessModal({
            title: "เพิ่มผู้ใช้งานสำเร็จ!",
            message: `บัญชีสำหรับเจ้าหน้าที่ถูกสร้างเรียบร้อยแล้ว\n\n📧 อีเมล: ${emailToRegister.toLowerCase()}\n👤 ชื่อ: ${fName || '-'}\n🏷️ นามสกุล: ${lName || '-'}\n\n* เจ้าหน้าที่สามารถเข้าสู่ระบบครั้งแรกโดยกรอก "ชื่อ" เป็น Username และ "นามสกุล" เป็น Password เพื่อเปิดใช้งานบัญชี`
          });
        }
      } catch (err) {
        console.error("Exception in handleAddUserProfile:", err);
      }
    }
  };

  const filteredProfiles = userProfiles.filter(p =>
    (p.email || '').toLowerCase().includes((userSearchQuery || '').toLowerCase()) ||
    (p.role || '').toLowerCase().includes((userSearchQuery || '').toLowerCase())
  );

  useEffect(() => {
    if (currentPage === 'packaging') {
      const today = new Date();
      if (pkgType === 'cloth') today.setDate(today.getDate() + 7);
      else today.setMonth(today.getMonth() + 6);
      setExpDateInst(today.toISOString().split('T')[0]);
      setPrepDate(new Date().toISOString().split('T')[0]);
      setSelectedPrints([]);
    }
  }, [pkgType, currentPage]);

  const resetForm = () => {
    setRegName(''); setRegAliases(''); setInventorySearchQuery(''); setHandpieceSearchQuery(''); setNewMasterItem(''); setLotNumber(''); setBarcode(''); setExpDate('');
    setGlobalMinStock(''); setMinStock(''); setCabinet(''); setShelf(''); setRow('');
    setUnit(''); setUnitSize(''); setInitialQty(''); setPricePerUnit(''); setCompany('');
    setReceiveDate(''); setDeliveryNote(''); setPoNumber(''); setOrderDate(''); setApprovalDate('');
    setImageUrl(''); setZoomImageUrl(''); setOtherImageUrl(''); setDocumentImageUrl(''); setIsEditing(false); setEditId(null); setSelectedComponents([]);
    setNameSuggestions([]); setIsNameDuplicate(false); setSetCategory('วัสดุคงทน');
  };

  const handleNameChange = (val) => {
    setRegName(val);
    if (val.trim().length > 1) {
      const matches = itemMaster.filter(masterLot => {
        const nameMatches = masterLot.item_name.toLowerCase().includes(val.toLowerCase());
        const aliasMatches = masterLot.aliases && masterLot.aliases.toLowerCase().includes(val.toLowerCase());
        return nameMatches || aliasMatches;
      });
      setNameSuggestions(matches.slice(0, 5));
      setIsNameDuplicate(itemMaster.some(masterLot => masterLot.item_name.toLowerCase() === val.toLowerCase()));
    } else {
      setNameSuggestions([]); setIsNameDuplicate(false);
    }
  };

  const startEdit = (masterLot) => {
    setRegType(masterLot.category); setRegName(masterLot.item_name); setRegAliases(masterLot.aliases || '');
    setNewMasterItem(masterLot.item_name); setLotNumber(masterLot.lot_number || '');
    setBarcode(masterLot.barcode || ''); setGlobalMinStock(masterLot.global_min_stock || 0);
    setMinStock(masterLot.min_stock || 0); setExpDate(masterLot.exp_date === '9999-12-31' ? '' : masterLot.exp_date);
    setCabinet(masterLot.cabinet || ''); setShelf(masterLot.shelf || ''); setRow(masterLot.row || '');
    setUnit(masterLot.unit || ''); setUnitSize(masterLot.unit_size || '');
    setPricePerUnit(masterLot.price_per_unit || ''); setCompany(masterLot.company || '');
    setReceiveDate(masterLot.receive_date || ''); setDeliveryNote(masterLot.delivery_note || '');
    setPoNumber(masterLot.po_number || ''); setOrderDate(masterLot.order_date || ''); setApprovalDate(masterLot.approval_date || '');
    setInitialQty('');

    const parsedImgs = parseImages(masterLot.image_url);
    setImageUrl(parsedImgs.full || '');
    setZoomImageUrl(parsedImgs.zoom || '');
    setOtherImageUrl(parsedImgs.other || '');
    setDocumentImageUrl(parsedImgs.document || '');

    setSelectedComponents(masterLot.components || []);
    setIsEditing(true); setEditId(masterLot.id);

    if (masterLot.is_set) {
      if (masterLot.category === 'กล่องหัวกรอ') setCurrentPage('doctor-box');
      else {
        setSetCategory(masterLot.category === 'หัวกรอ' ? 'หัวกรอ' : 'วัสดุคงทน');
        setCurrentPage('set-config');
      }
    } else setCurrentPage('register');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const addLog = async (item_name, warehouse, action) => {
    const newLog = { id: Math.random().toString(), item_name, warehouse, action, timestamp: new Date().toISOString() };
    setLogs(prev => [newLog, ...prev]);
    const { error } = await supabase.from('logs').insert([{ item_name, warehouse, action }]);
    if (error) console.error("Log Saving Error:", error.message);
  };

  const handleUpdateGlobalMinStock = async (e, itemName, currentGlobalMin) => {
    e.preventDefault(); e.stopPropagation();
    const newVal = window.prompt(`ระบุจำนวน "ขั้นต่ำรวม" ใหม่สำหรับ "${itemName}":`, currentGlobalMin);
    if (newVal !== null && newVal.trim() !== "") {
      const numVal = Number(newVal);
      if (!isNaN(numVal) && numVal >= 0) {
        setItemMaster(prev => prev.map(masterLot => masterLot.item_name === itemName ? { ...masterLot, global_min_stock: numVal } : masterLot));
        const { error } = await supabase.from('item_master').update({ global_min_stock: numVal }).eq('item_name', itemName);
        if (error) alert("อัปเดตไม่สำเร็จ: " + error.message);
      } else alert("กรุณากรอกตัวเลขที่ถูกต้อง");
    }
  };

  const handleImageUpload = async (e, slotParam = 'full') => {
    const file = e.target.files[0];
    if (!file) return;

    let slot = 'full';
    if (typeof slotParam === 'boolean') {
      slot = slotParam ? 'zoom' : 'full';
    } else {
      slot = slotParam;
    }

    if (slot === 'full') setIsUploading(true);
    else if (slot === 'zoom') setIsUploadingZoom(true);
    else if (slot === 'other') setIsUploadingOther(true);
    else if (slot === 'document') setIsUploadingDocument(true);

    const startTime = Date.now();

    try {
      let options = {};
      if (slot === 'document') {
        options = { maxWidth: 2048, maxHeight: 2048, quality: 0.85 };
      } else {
        options = { maxWidth: 1200, maxHeight: 1200, quality: 0.75 };
      }

      const secureUrl = await uploadItemImage(file, options);

      // Ensure the loading state is shown for at least 1.5 seconds so the user can see it
      const elapsed = Date.now() - startTime;
      if (elapsed < 1500) {
        await new Promise(resolve => setTimeout(resolve, 1500 - elapsed));
      }

      if (slot === 'full') setImageUrl(secureUrl);
      else if (slot === 'zoom') setZoomImageUrl(secureUrl);
      else if (slot === 'other') setOtherImageUrl(secureUrl);
      else if (slot === 'document') setDocumentImageUrl(secureUrl);

      setSuccessModal({
        title: "📸 อัปโหลดรูปสำเร็จ!",
        message: "ระบบได้อัปโหลดรูปภาพไปยังคลังรูปภาพเรียบร้อยแล้ว"
      });
    } catch (err) {
      console.error("Cloudinary Error:", err);
      
      // Ensure the loading state is shown for at least 1.5 seconds so the user can see it
      const elapsed = Date.now() - startTime;
      if (elapsed < 1500) {
        await new Promise(resolve => setTimeout(resolve, 1500 - elapsed));
      }

      setSuccessModal({
        title: "❌ เกิดข้อผิดพลาด",
        message: "เกิดข้อผิดพลาดในการเชื่อมต่อเพื่ออัปโหลดรูปภาพ: " + (err.message || "")
      });
    } finally {
      if (slot === 'full') setIsUploading(false);
      else if (slot === 'zoom') setIsUploadingZoom(false);
      else if (slot === 'other') setIsUploadingOther(false);
      else if (slot === 'document') setIsUploadingDocument(false);
    }
  };

  const handleTogglePreview = (itemName) => {
    setShowPreview(prev => ({ ...prev, [itemName]: !prev[itemName] }));
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!regName) return alert("กรุณาใส่ชื่อวัสดุ");

    const proceedSubmit = async () => {
      const finalExp = (regType === 'วัสดุคงทน' || regType === 'หัวกรอ') ? '9999-12-31' : expDate;
      const combinedImageUrl = [
        imageUrl || '',
        zoomImageUrl || '',
        otherImageUrl || '',
        documentImageUrl || ''
      ].some(url => url) ? [
        imageUrl || '',
        zoomImageUrl || '',
        otherImageUrl || '',
        documentImageUrl || ''
      ].join('|') : '';

      const dbPayload = {
        item_name: regName, aliases: regAliases, category: regType, lot_number: lotNumber, barcode: barcode,
        exp_date: finalExp, min_stock: Number(minStock) || 0, global_min_stock: Number(globalMinStock) || 0,
        is_set: false, components: [], last_updated: new Date().toISOString(),
        cabinet: cabinet, shelf: shelf, row: row, unit: unit, unit_size: unitSize, price_per_unit: Number(pricePerUnit) || 0, company: company,
        receive_date: receiveDate || null, delivery_note: deliveryNote, po_number: poNumber,
        order_date: orderDate || null, approval_date: approvalDate || null, image_url: combinedImageUrl
      };

      try {
        if (isEditing) {
          const { error } = await supabase.from('item_master').update(dbPayload).eq('id', editId);
          if (error) throw error;
          await supabase.from('item_master').update({ global_min_stock: Number(globalMinStock) }).eq('item_name', regName);
        } else {
          const { data: insertedMaster, error } = await supabase.from('item_master').insert([dbPayload]).select();
          if (error) throw error;
          await supabase.from('item_master').update({ global_min_stock: Number(globalMinStock) }).eq('item_name', regName);

          if (insertedMaster && insertedMaster.length > 0) {
            const masterId = insertedMaster[0].id;
            const suppliesData = [];
            const qty = Number(initialQty) > 0 ? Number(initialQty) : 0;

            if (regType === 'วัสดุสิ้นเปลือง') {
              suppliesData.push({ master_id: masterId, warehouse_name: "คลังใหญ่ (คลังพัสดุ)", item_amount: qty });
              suppliesData.push({ master_id: masterId, warehouse_name: "คลังย่อย (คลังห้องฟัน)", item_amount: 0 });
              suppliesData.push({ master_id: masterId, warehouse_name: "ห้องเก็บของ", item_amount: 0 });
              suppliesData.push({ master_id: masterId, warehouse_name: "ห้องคลีนิก", item_amount: 0 });
            } else {
              WAREHOUSES_LIST.forEach(w => {
                if (!["ห้องคลีนิก"].includes(w.name)) suppliesData.push({ master_id: masterId, warehouse_name: w.name, item_amount: w.name === "คลังใหญ่ (คลังพัสดุ)" ? qty : 0 });
              });
            }
            await supabase.from('supplies').insert(suppliesData);
          }
        }
        
        setSuccessModal({
          title: "บันทึกสำเร็จ!",
          message: isEditing ? "ระบบได้ทำการอัปเดตข้อมูลวัสดุเรียบร้อยแล้ว" : "ระบบได้ทำการบันทึกข้อมูลวัสดุเรียบร้อยแล้ว",
          onClose: () => {
            const redirectPage = regType === 'วัสดุสิ้นเปลือง' ? 'consumable' : (regType === 'หัวกรอ' ? 'handpiece' : 'durable');
            resetForm();
            fetchData();
            setCurrentPage(redirectPage);
          }
        });
      } catch (e) {
        setSuccessModal({
          title: "❌ เกิดข้อผิดพลาด",
          message: "เกิดข้อผิดพลาดในการบันทึกข้อมูล: " + e.message
        });
      }
    };

    if (!isEditing && isNameDuplicate) {
      setSterileConfirmModal({
        message: `⚠️ ชื่อ "${regName}" มีอยู่ในระบบแล้ว คุณแน่ใจหรือไม่ว่าต้องการลงทะเบียนเพิ่ม? (หากต้องการเพิ่มของเข้า Lot เดิม ให้ไปกดยืนยัน)`,
        onConfirm: proceedSubmit
      });
    } else {
      await proceedSubmit();
    }
  };

  const handleSaveBox = async () => {
    let flattenedComponents = [...selectedComponents];

    if (currentPage === 'doctor-box') {
      if (!isEditing) {
        if (!newMasterItem) return alert("กรุณาระบุชื่อกล่อง");
        const dbPayload = {
          item_name: newMasterItem, category: 'กล่องหัวกรอ', exp_date: '9999-12-31',
          is_set: true, components: flattenedComponents, last_updated: new Date().toISOString(),
          cabinet: cabinet, shelf: shelf, row: row, image_url: imageUrl
        };
        try {
          const { data: insertedMaster } = await supabase.from('item_master').insert([dbPayload]).select();
          if (insertedMaster && insertedMaster.length > 0) {
            const masterId = insertedMaster[0].id;
            const random9 = Math.floor(100000000 + Math.random() * 900000000);
            const boxUid = `BOX-${random9}`;
            const newBoxInstrument = {
              unique_barcode: boxUid, master_id: masterId, item_name: newMasterItem,
              status: 'sterile-ready', location: '   - ห้อง sterile', details: { isPrinted: true },
              is_active: true, last_updated: new Date().toISOString()
            };
            await supabase.from('instruments').insert([newBoxInstrument]);
          }
          setSuccessModal({
            title: "บันทึกสำเร็จ!",
            message: "บันทึกกล่อง และสร้างบาร์โค้ด BOX- สำเร็จ!",
            onClose: () => {
              resetForm();
              fetchData();
            }
          });
        } catch (e) {
          setSuccessModal({
            title: "❌ เกิดข้อผิดพลาด",
            message: "เกิดข้อผิดพลาดในการบันทึกกล่อง: " + e.message
          });
        }
      } else {
        const dbPayload = {
          item_name: newMasterItem, components: flattenedComponents, last_updated: new Date().toISOString(),
          cabinet: cabinet, shelf: shelf, row: row, image_url: imageUrl
        };
        try {
          await supabase.from('item_master').update(dbPayload).eq('id', editId);
          await supabase.from('instruments').update({ item_name: newMasterItem }).eq('master_id', editId);
          setSuccessModal({
            title: "บันทึกสำเร็จ!",
            message: "อัปเดตการตั้งค่ากล่องสำเร็จ!",
            onClose: () => {
              resetForm();
              fetchData();
            }
          });
        } catch (e) {
          setSuccessModal({
            title: "❌ เกิดข้อผิดพลาด",
            message: "เกิดข้อผิดพลาดในการอัปเดตการตั้งค่ากล่อง: " + e.message
          });
        }
      }
    } else {
      if (!newMasterItem) return alert("กรุณาระบุชื่อชุด/กล่อง");
      const dbPayload = {
        item_name: newMasterItem, category: setCategory, exp_date: '9999-12-31',
        is_set: true, components: flattenedComponents, last_updated: new Date().toISOString(),
        cabinet: cabinet, shelf: shelf, row: row, image_url: imageUrl
      };
      try {
        if (isEditing) {
          await supabase.from('item_master').update(dbPayload).eq('id', editId);
        } else {
          const { data: insertedMaster } = await supabase.from('item_master').insert([dbPayload]).select();
          if (insertedMaster && insertedMaster.length > 0) {
            const masterId = insertedMaster[0].id;
            const suppliesData = [];
            WAREHOUSES_LIST.forEach(w => {
              if (!["คลังย่อย (คลังห้องฟัน)", "ห้องเก็บของ", "ห้องคลีนิก"].includes(w.name)) suppliesData.push({ master_id: masterId, warehouse_name: w.name, item_amount: 0 });
            });
            await supabase.from('supplies').insert(suppliesData);
          }
        }
        setSuccessModal({
          title: "บันทึกสำเร็จ!",
          message: "บันทึกชุด/กล่องสำเร็จ!",
          onClose: () => {
            resetForm();
            fetchData();
          }
        });
      } catch (e) {
        setSuccessModal({
          title: "❌ เกิดข้อผิดพลาด",
          message: "เกิดข้อผิดพลาดในการบันทึกชุด/กล่อง: " + e.message
        });
      }
    }
  };

  const handleUpdateAmount = async (sId, cur, type, itemName, warehouseName) => {
    let val = type === 'increase' ? cur + 1 : cur - 1;
    if (val < 0) return;
    setSupplies(prevSupplies => prevSupplies.map(item => item.id === sId ? { ...item, item_amount: val } : item));
    const { error } = await supabase.from('supplies').update({ item_amount: val, last_updated: new Date().toISOString() }).eq('id', sId);
    if (error) alert("ข้อผิดพลาดในการอัปเดตสต็อก: " + error.message);
    else addLog(itemName, warehouseName, type === 'increase' ? 'รับเข้า (กดมือ)' : 'เบิกออก (กดมือ)');
  };

  const handleDeleteMaster = async (id) => {
    const targetMaster = itemMaster.find(m => m.id === id);
    if (targetMaster && targetMaster.category === 'กล่องหัวกรอ') {
      const physicalBox = instruments.find(i => i.master_id === id);
      if (physicalBox) {
        await supabase.from('instruments').update({ container_code: null }).eq('container_code', physicalBox.unique_barcode);
      }
    }

    const { error } = await supabase.from('item_master').delete().eq('id', id);
    if (error) alert("ลบไม่สำเร็จ: " + error.message);
    else {
      await supabase.from('instruments').delete().eq('master_id', id);
      fetchData();
    }
  };

  const handleUnbindFromBox = async (instr) => {
    if (!instr) return;
    try {
      const { error } = await supabase.from('instruments').update({ container_code: null, last_updated: new Date().toISOString() }).eq('id', instr.id);
      if (error) alert("ปลดอุปกรณ์ออกจากกล่องไม่สำเร็จ: " + error.message);
      else {
        setInstruments(prev => prev.map(i => i.id === instr.id ? { ...i, container_code: null } : i));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteDoctorBox = (id) => {
    handleDeleteMaster(id);
  };

  const handleCreateUniqueCode = async (masterLot, predefinedUID = null) => {
    if (currentPage !== 'tool-assembly' && (!preparedBy || !checkedBy || !sterileMachine || !sterileCycle)) return alert("กรุณากรอกข้อมูลการนึ่งให้ครบถ้วน");

    const ps = supplies.find(s => s.master_id === masterLot.id && s.warehouse_name.includes("กำลัง sterile"));
    const uid = predefinedUID || generateUID(0);

    let tBarcode = null, tName = null;
    if (currentPage !== 'tool-assembly' && targetBoxForPack) {
      const parts = targetBoxForPack.split('|');
      tBarcode = parts[0];
      tName = parts[1];
    }

    const nonSterileTag = instrumentTags.find(t => t.name.toUpperCase() === 'NON-STERILE');
    const tagIdToUse = nonSterileTag ? nonSterileTag.id : null;

    const detailsObj = {
      mfgDate: prepDate, expDate: expDateInst,
      preparedBy: currentPage === 'tool-assembly' ? '' : preparedBy,
      checkedBy: currentPage === 'tool-assembly' ? '' : checkedBy,
      machine: currentPage === 'tool-assembly' ? '' : sterileMachine,
      cycle: currentPage === 'tool-assembly' ? '' : sterileCycle,
      packaging: pkgType === 'cloth' ? 'ห่อผ้า' : 'ซอง sterile',
      isPrinted: predefinedUID ? true : false,
      targetBoxBarcode: tBarcode,
      targetBoxName: tName,
      tag_id: tagIdToUse
    };

    if (masterLot.is_set) {
      for (const compId of masterLot.components) {
        const ws = supplies.find(s => s.master_id === compId && s.warehouse_name.includes("ห้องล้างเครื่องมือ"));
        if (!ws || ws.item_amount <= 0) {
          const compName = itemMaster.find(i => i.id === compId)?.item_name;
          return alert(`ของย่อยไม่พอจัดชุดครับ ขาด: ${compName}`);
        }
      }

      setSupplies(prev => prev.map(s => {
        if (masterLot.components.includes(s.master_id) && s.warehouse_name.includes("ห้องล้างเครื่องมือ")) return { ...s, item_amount: s.item_amount - 1 };
        if (ps && s.id === ps.id) return { ...s, item_amount: s.item_amount + 1 };
        return s;
      }));

      for (const compId of masterLot.components) {
        const ws = supplies.find(s => s.master_id === compId && s.warehouse_name.includes("ห้องล้างเครื่องมือ"));
        await supabase.from('supplies').update({ item_amount: ws.item_amount - 1, last_updated: new Date().toISOString() }).eq('id', ws.id);
      }
    }
    else {
      const ws = supplies.find(s => s.master_id === masterLot.id && s.warehouse_name.includes("ห้องล้างเครื่องมือ"));
      if (!ws || ws.item_amount <= 0) return alert("ของในห้องล้างมีไม่พอครับ");

      setSupplies(prev => prev.map(s => {
        if (s.id === ws.id) return { ...s, item_amount: s.item_amount - 1 };
        if (ps && s.id === ps.id) return { ...s, item_amount: s.item_amount + 1 };
        return s;
      }));
      await supabase.from('supplies').update({ item_amount: ws.item_amount - 1, last_updated: new Date().toISOString() }).eq('id', ws.id);
    }

    const newInstrForUI = { id: uid, unique_barcode: uid, master_id: masterLot.id, item_name: masterLot.item_name, status: 'sterile-pending', location: '   - ห้องล้างเครื่องมือ', details: detailsObj, is_active: true, last_updated: new Date().toISOString() };
    setInstruments(prev => [newInstrForUI, ...prev]);
    const dbInstrPayload = { unique_barcode: uid, master_id: masterLot.id, item_name: masterLot.item_name, status: 'sterile-pending', location: '   - ห้องล้างเครื่องมือ', details: detailsObj, is_active: true, last_updated: new Date().toISOString() };
    const { error: insError } = await supabase.from('instruments').insert([dbInstrPayload]);

    if (insError) alert("เกิดข้อผิดพลาดตอนบันทึกบาร์โค้ด: " + insError.message);
    if (ps) await supabase.from('supplies').update({ item_amount: ps.item_amount + 1, last_updated: new Date().toISOString() }).eq('id', ps.id);

    if (predefinedUID) {
      addLog(masterLot.item_name, "ห้องล้างเครื่องมือ", `ผูกข้อมูลกับฉลากเปล่า รหัส ${uid}`);
      setScanBlankItem(null); setBlankBarcodeInput('');
    } else {
      addLog(masterLot.item_name, "ห้องล้างเครื่องมือ", `แพ็กซองสร้างรหัสใหม่ ${uid}`);
    }
  };

  const handleConfirmAssembly = async () => {
    if (selectedForAssembly.length === 0) return;

    // 1. Verify component availability for all selected items first
    const tempSupplies = supplies.map(s => ({ ...s }));
    for (const item of selectedForAssembly) {
      const { type, masterLot } = item;
      if (type === 'set') {
        for (const compId of masterLot.components) {
          const wsIdx = tempSupplies.findIndex(s => s.master_id === compId && s.warehouse_name.includes("ห้องล้างเครื่องมือ"));
          if (wsIdx === -1 || tempSupplies[wsIdx].item_amount <= 0) {
            const compName = itemMaster.find(i => i.id === compId)?.item_name || compId;
            return alert(`ไม่สามารถจัดเสร็จได้เนื่องจากของย่อยไม่พอในห้องล้าง: ${compName} (สำหรับชุด ${masterLot.item_name})`);
          }
          tempSupplies[wsIdx].item_amount -= 1;
        }
      } else {
        const wsIdx = tempSupplies.findIndex(s => s.master_id === masterLot.id && s.warehouse_name.includes("ห้องล้างเครื่องมือ"));
        if (wsIdx === -1 || tempSupplies[wsIdx].item_amount <= 0) {
          return alert(`ไม่สามารถจัดเสร็จได้เนื่องจากอุปกรณ์เดี่ยวไม่พอในห้องล้าง: ${masterLot.item_name}`);
        }
        tempSupplies[wsIdx].item_amount -= 1;
      }
    }

    // 2. Perform DB deductions and inserts
    try {
      const nonSterileTag = instrumentTags.find(t => t.name.toUpperCase() === 'NON-STERILE');
      const tagIdToUse = nonSterileTag ? nonSterileTag.id : null;
      const assemblerName = user?.email ? user.email.split('@')[0] : 'system';

      const newInstrsForUI = [];

      for (let idx = 0; idx < selectedForAssembly.length; idx++) {
        const item = selectedForAssembly[idx];
        const { type, masterLot } = item;
        const uid = `ASM-${Date.now()}-${idx}`;

        const detailsObj = {
          assembledBy: assemblerName,
          assemblyDate: new Date().toISOString().split('T')[0],
          mfgDate: new Date().toISOString().split('T')[0],
          expDate: '',
          packaging: 'ซอง sterile',
          isPrinted: false,
          tag_id: tagIdToUse
        };

        if (type === 'set') {
          for (const compId of masterLot.components) {
            const ws = supplies.find(s => s.master_id === compId && s.warehouse_name.includes("ห้องล้างเครื่องมือ"));
            const updatedAmount = ws.item_amount - 1;
            setSupplies(prev => prev.map(s => s.id === ws.id ? { ...s, item_amount: updatedAmount } : s));
            await supabase.from('supplies').update({ item_amount: updatedAmount, last_updated: new Date().toISOString() }).eq('id', ws.id);
          }
        } else {
          const ws = supplies.find(s => s.master_id === masterLot.id && s.warehouse_name.includes("ห้องล้างเครื่องมือ"));
          const updatedAmount = ws.item_amount - 1;
          setSupplies(prev => prev.map(s => s.id === ws.id ? { ...s, item_amount: updatedAmount } : s));
          await supabase.from('supplies').update({ item_amount: updatedAmount, last_updated: new Date().toISOString() }).eq('id', ws.id);
        }

        const newInstrForUI = {
          id: uid,
          unique_barcode: uid,
          master_id: masterLot.id,
          item_name: masterLot.item_name,
          status: 'assembled',
          location: '   - ห้องจัดเครื่องมือ',
          details: detailsObj,
          is_active: true,
          last_updated: new Date().toISOString()
        };
        newInstrsForUI.push(newInstrForUI);

        const dbInstrPayload = {
          unique_barcode: uid,
          master_id: masterLot.id,
          item_name: masterLot.item_name,
          status: 'assembled',
          location: '   - ห้องจัดเครื่องมือ',
          details: detailsObj,
          is_active: true,
          last_updated: new Date().toISOString()
        };

        const { error: insError } = await supabase.from('instruments').insert([dbInstrPayload]);
        if (insError) throw insError;

        addLog(masterLot.item_name, "ห้องจัดเครื่องมือ", `จัดเสร็จสิ้น ส่งตรวจเครื่องมือ รหัส ${uid}`);
      }

      setInstruments(prev => [...newInstrsForUI, ...prev]);
      alert(`จัดเสร็จสิ้นจำนวน ${selectedForAssembly.length} รายการ ส่งต่อไปยังห้องตรวจสอบเรียบร้อยแล้ว`);
      setSelectedForAssembly([]);
      fetchData();
    } catch (err) {
      console.error(err);
      alert("เกิดข้อผิดพลาดในการจัดเสร็จเครื่องมือ: " + err.message);
    }
  };

  const handleConfirmInspection = async () => {
    if (!selectedInspectionItem) return;
    if (selectedInspectionItem.unique_barcode.startsWith('ASM-')) {
      return alert("❌ ยังไม่มีรหัสบาร์โค้ด\nกรุณาสแกนบาร์โค้ดก่อน");
    }
    const mustScan = !isAdmin || isUsingPreparedScan;
    if (mustScan && !isBarcodeScanned) {
      return alert("❌ ยังไม่มีรหัสบาร์โค้ด\nกรุณาสแกนบาร์โค้ดก่อน");
    }
    if (!preparedBy || !checkedBy || !sterileMachine || !sterileCycle) {
      return alert("กรุณากรอกข้อมูลขั้นตอนการนึ่งให้ครบถ้วน (ผู้เตรียม, ผู้ตรวจ, เครื่อง, รอบ)");
    }
    if (preparedBy.trim() === checkedBy.trim()) {
      return alert("❌ ผู้เตรียมและผู้ตรวจต้องไม่เป็นคนเดียวกัน เพื่อป้องกันความผิดพลาด");
    }

    try {
      const today = new Date();
      if (pkgType === 'cloth') today.setDate(today.getDate() + 7);
      else today.setMonth(today.getMonth() + 6);
      const calculatedExpDate = today.toISOString().split('T')[0];
      const calculatedMfgDate = new Date().toISOString().split('T')[0];

      const detailsObj = {
        ...selectedInspectionItem.details,
        mfgDate: calculatedMfgDate,
        expDate: calculatedExpDate,
        preparedBy,
        checkedBy,
        machine: sterileMachine,
        cycle: sterileCycle,
        packaging: pkgType === 'cloth' ? 'ห่อผ้า' : 'ซอง sterile',
        isPrinted: false
      };

      const { error: updateError } = await supabase
        .from('instruments')
        .update({
          unique_barcode: selectedInspectionItem.unique_barcode,
          status: 'sterile-pending',
          location: '   - ห้องล้างเครื่องมือ',
          details: detailsObj,
          last_updated: new Date().toISOString()
        })
        .eq('id', selectedInspectionItem.id);

      if (updateError) throw updateError;

      const ps = supplies.find(s => s.master_id === selectedInspectionItem.master_id && s.warehouse_name.includes("กำลัง sterile"));
      if (ps) {
        const newQty = ps.item_amount + 1;
        await supabase
          .from('supplies')
          .update({ item_amount: newQty, last_updated: new Date().toISOString() })
          .eq('id', ps.id);
      }

      addLog(selectedInspectionItem.item_name, "ห้องตรวจเครื่องมือ", `ตรวจสอบสำเร็จและเตรียมพิมพ์บาร์โค้ด ${selectedInspectionItem.unique_barcode}`);

      const updatedItem = {
        ...selectedInspectionItem,
        status: 'sterile-pending',
        location: '   - ห้องล้างเครื่องมือ',
        details: detailsObj
      };

      setInstruments(prev => prev.map(i => i.id === selectedInspectionItem.id ? updatedItem : i));
      setPrintPreviewItem(updatedItem);
      setSelectedInspectionItem(null);
      alert("บันทึกข้อมูลและตรวจสอบเครื่องมือสำเร็จ! กำลังแสดงพรีวิวสำหรับการพิมพ์บาร์โค้ด");
      fetchData();
    } catch (err) {
      console.error(err);
      alert("เกิดข้อผิดพลาดในการบันทึกผลการตรวจ: " + err.message);
    }
  };

  const executeQZPrint = async (base64ImagesArray) => {
    if (!qz.websocket.isActive()) await qz.websocket.connect();
    const printer = await qz.printers.find("TSC");
    const config = qz.configs.create(printer, {
      density: 203,
      interpolation: 'bicubic'
    });
    const dataToPrint = base64ImagesArray.map(base64Image => ({
      type: 'image', format: 'base64', data: base64Image,
      options: { language: "ZPL", x: 0, y: 0, rotation: 0, scaleContent: false }
    }));
    await qz.print(config, dataToPrint);
  };

  const handleConfirmPrint = async () => {
    if (printPreviewItem) {
      setIsPrinting(true);
      try {
        const node = document.getElementById('single-print-node');
        const canvas = await html2canvas(node, { scale: 1, useCORS: true, backgroundColor: '#ffffff' });
        const dataUrl = canvas.toDataURL('image/png');
        setPrintedImagesPreview([dataUrl]);

        const base64Image = dataUrl.split(',')[1];
        await executeQZPrint([base64Image]);

        const newDetails = { ...printPreviewItem.details, isPrinted: true };
        setInstruments(prev => prev.map(i => i.id === printPreviewItem.id ? { ...i, details: newDetails } : i));
        await supabase.from('instruments').update({ details: newDetails, last_updated: new Date().toISOString() }).eq('id', printPreviewItem.id);
        addLog(printPreviewItem.item_name, "แพ็กซอง", `ปริ้นบาร์โค้ดสำเร็จ ${printPreviewItem.unique_barcode}`);
        setPrintPreviewItem(null);
      } catch (err) { console.error(err); alert("เกิดข้อผิดพลาดในการสั่งพิมพ์ผ่าน QZ Tray: \n" + err.message); }
      finally { setIsPrinting(false); }
    }
  };

  const handleBatchPrint = async () => {
    if (selectedPrints.length === 0) return alert("กรุณาเลือกรายการที่ต้องการพิมพ์");
    setIsPrinting(true);
    try {
      const base64ImagesArray = [];
      const dataUrlsToPreview = [];
      for (const item of selectedPrints) {
        const node = document.getElementById(`batch-print-node-${item.id}`);
        if (node) {
          const canvas = await html2canvas(node, { scale: 1, useCORS: true, backgroundColor: '#ffffff' });
          const dataUrl = canvas.toDataURL('image/png');
          dataUrlsToPreview.push(dataUrl);
          const base64Image = dataUrl.split(',')[1];
          base64ImagesArray.push(base64Image);
        }
      }
      setPrintedImagesPreview(dataUrlsToPreview);
      await executeQZPrint(base64ImagesArray);

      const updatedItems = [];
      for (const item of selectedPrints) {
        const newDetails = { ...item.details, isPrinted: true };
        updatedItems.push({ ...item, details: newDetails });
        await supabase.from('instruments').update({ details: newDetails, last_updated: new Date().toISOString() }).eq('id', item.id);
        addLog(item.item_name, "แพ็กซอง", `ปริ้นบาร์โค้ดกลุ่มสำเร็จ ${item.unique_barcode}`);
      }
      setInstruments(prev => prev.map(i => {
        const found = updatedItems.find(u => u.id === i.id);
        return found ? found : i;
      }));
      setSelectedPrints([]);
    } catch (err) { console.error(err); alert("เกิดข้อผิดพลาดในการสั่งพิมพ์: \n" + err.message); }
    finally { setIsPrinting(false); }
  };

  useEffect(() => {
    if (blankLabelsToPrint.length > 0 && !isPrinting) {
      const printBlanks = async () => {
        setIsPrinting(true);
        try {
          const base64ImagesArray = [];
          const dataUrlsToPreview = [];
          const chunks = chunkArray(blankLabelsToPrint, 8);

          for (let i = 0; i < chunks.length; i++) {
            const node = document.getElementById(`blank-sheet-node-${i}`);
            if (node) {
              const canvas = await html2canvas(node, { scale: 1, useCORS: true, backgroundColor: '#ffffff' });
              const dataUrl = canvas.toDataURL('image/png');
              dataUrlsToPreview.push(dataUrl);

              const base64Image = dataUrl.split(',')[1];
              base64ImagesArray.push(base64Image);
            }
          }

          setPrintedImagesPreview(dataUrlsToPreview);
          await executeQZPrint(base64ImagesArray);
        } catch (err) { console.error(err); alert("เกิดข้อผิดพลาดในการพิมพ์ฉลากเปล่า: " + err.message); }
        finally { setIsPrinting(false); setBlankLabelsToPrint([]); setNumBlankLabels(''); }
      };
      setTimeout(printBlanks, 500);
    }
  }, [blankLabelsToPrint, isPrinting]);

  const handlePrepareBlankLabels = () => {
    const count = parseInt(numBlankLabels);
    if (!count || count <= 0 || count > 100) return alert("กรุณาระบุจำนวนที่ต้องการพิมพ์ล่วงหน้า (1-100 ใบ)");
    const newBlanks = [];
    for (let i = 0; i < count; i++) {
      const uid = generateUID(i);
      newBlanks.push({
        id: `blank-${uid}`, unique_barcode: uid, item_name: '--- ฉลากเปล่า ---',
        details: { mfgDate: '-', expDate: '-', preparedBy: '-', checkedBy: '-', machine: '-', cycle: '-', isPrinted: false }
      });
    }
    setBlankLabelsToPrint(newBlanks);
  };

  const handleConfirmBatchBind = async () => {
    if (scannedBlankList.length === 0) return;
    setIsBinding(true);

    let currentDbSupplies = JSON.parse(JSON.stringify(supplies));
    const newInstruments = [];
    const count = scannedBlankList.length;
    const masterLot = scanBlankItem;

    let tBarcode = null, tName = null;
    if (currentPage !== 'tool-assembly' && targetBoxForPack) {
      const parts = targetBoxForPack.split('|');
      tBarcode = parts[0];
      tName = parts[1];
    }

    const nonSterileTag = instrumentTags.find(t => t.name.toUpperCase() === 'NON-STERILE');
    const tagIdToUse = nonSterileTag ? nonSterileTag.id : null;

    const detailsObj = {
      mfgDate: prepDate, expDate: expDateInst,
      preparedBy: currentPage === 'tool-assembly' ? '' : preparedBy,
      checkedBy: currentPage === 'tool-assembly' ? '' : checkedBy,
      machine: currentPage === 'tool-assembly' ? '' : sterileMachine,
      cycle: currentPage === 'tool-assembly' ? '' : sterileCycle,
      packaging: pkgType === 'cloth' ? 'ห่อผ้า' : 'ซอง sterile', isPrinted: true,
      targetBoxBarcode: tBarcode,
      targetBoxName: tName,
      tag_id: tagIdToUse
    };

    if (masterLot.is_set) {
      for (const compId of masterLot.components) {
        const wsIndex = currentDbSupplies.findIndex(s => s.master_id === compId && s.warehouse_name.includes("ห้องล้างเครื่องมือ"));
        if (wsIndex > -1) currentDbSupplies[wsIndex].item_amount -= count;
      }
      const psIndex = currentDbSupplies.findIndex(s => s.master_id === masterLot.id && s.warehouse_name.includes("กำลัง sterile"));
      if (psIndex > -1) currentDbSupplies[psIndex].item_amount += count;
    } else {
      const wsIndex = currentDbSupplies.findIndex(s => s.master_id === masterLot.id && s.warehouse_name.includes("ห้องล้างเครื่องมือ"));
      if (wsIndex > -1) currentDbSupplies[wsIndex].item_amount -= count;
      const psIndex = currentDbSupplies.findIndex(s => s.master_id === masterLot.id && s.warehouse_name.includes("กำลัง sterile"));
      if (psIndex > -1) currentDbSupplies[psIndex].item_amount += count;
    }

    for (const uid of scannedBlankList) {
      newInstruments.push({
        unique_barcode: uid, master_id: masterLot.id, item_name: masterLot.item_name,
        status: 'sterile-pending', location: '   - ห้องล้างเครื่องมือ', details: detailsObj, is_active: true, last_updated: new Date().toISOString()
      });
    }

    try {
      for (const sup of currentDbSupplies) {
        const originalSup = supplies.find(s => s.id === sup.id);
        if (originalSup && originalSup.item_amount !== sup.item_amount) {
          await supabase.from('supplies').update({ item_amount: sup.item_amount, last_updated: new Date().toISOString() }).eq('id', sup.id);
        }
      }
      setSupplies(currentDbSupplies);

      const { data: insertedInsts, error: insError } = await supabase.from('instruments').insert(newInstruments).select();
      if (insError) throw insError;
      if (insertedInsts) setInstruments(prev => [...insertedInsts, ...prev]);

      addLog(masterLot.item_name, "ห้องล้างเครื่องมือ", `ผูกข้อมูลฉลากเปล่าจำนวน ${count} รายการ`);
      alert(`✅ ผูกข้อมูลสำเร็จจำนวน ${count} รายการ!`);
    } catch (e) { alert("เกิดข้อผิดพลาดในการบันทึก: " + e.message); }
    finally { setIsBinding(false); setScanBlankItem(null); setScannedBlankList([]); }
  };

  const handleCancelPackaging = async (item) => {
    if (!window.confirm(`ต้องการยกเลิกรหัส ${item.unique_barcode} ใช่หรือไม่?`)) return;
    const master = itemMaster.find(masterLot => masterLot.id === item.master_id);
    const ps = supplies.find(s => s.master_id === item.master_id && s.warehouse_name.includes("กำลัง sterile"));
    setInstruments(prev => prev.filter(i => i.id !== item.id));

    if (master?.is_set) {
      setSupplies(prev => prev.map(s => {
        if (master.components.includes(s.master_id) && s.warehouse_name.includes("ห้องล้างเครื่องมือ")) return { ...s, item_amount: s.item_amount + 1 };
        if (ps && s.id === ps.id) return { ...s, item_amount: s.item_amount - 1 };
        return s;
      }));
      for (const compId of master.components) {
        const ws = supplies.find(s => s.master_id === compId && s.warehouse_name.includes("ห้องล้างเครื่องมือ"));
        if (ws) await supabase.from('supplies').update({ item_amount: ws.item_amount + 1, last_updated: new Date().toISOString() }).eq('id', ws.id);
      }
    }
    else {
      const ws = supplies.find(s => s.master_id === item.master_id && s.warehouse_name.includes("ห้องล้างเครื่องมือ"));
      setSupplies(prev => prev.map(s => {
        if (ws && s.id === ws.id) return { ...s, item_amount: s.item_amount + 1 };
        if (ps && s.id === ps.id) return { ...s, item_amount: s.item_amount - 1 };
        return s;
      }));
      if (ws) await supabase.from('supplies').update({ item_amount: ws.item_amount + 1, last_updated: new Date().toISOString() }).eq('id', ws.id);
    }
    const { error } = await supabase.from('instruments').delete().eq('id', item.id);
    if (error) alert("ลบข้อมูลไม่สำเร็จ: " + error.message);
    if (ps) await supabase.from('supplies').update({ item_amount: ps.item_amount - 1, last_updated: new Date().toISOString() }).eq('id', ps.id);
    addLog(item.item_name, "แพ็กซอง", `ยกเลิกบาร์โค้ด (ดึงกลับห้องล้าง) รหัส ${item.unique_barcode}`);
  };

  const handleRecallSearch = async () => {
    setIsRecallSearching(true);
    try {
      let query = supabase.from('instruments').select('*').eq('is_active', true);

      if (filterRecallBarcode) {
        query = query.ilike('unique_barcode', `%${filterRecallBarcode}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      const filtered = (data || []).filter(inst => {
        if (filterRecallItemName) {
          const nameMatches = inst.item_name && inst.item_name.toLowerCase().includes(filterRecallItemName.toLowerCase());
          const masterItem = itemMaster.find(m => m.id === inst.master_id);
          const aliasMatches = masterItem && masterItem.aliases && masterItem.aliases.toLowerCase().includes(filterRecallItemName.toLowerCase());
          if (!nameMatches && !aliasMatches) return false;
        }
        const details = inst.details || {};
        if (filterRecallMfgDate && details.mfgDate !== filterRecallMfgDate) return false;
        if (filterRecallPreparedBy && details.preparedBy !== filterRecallPreparedBy) return false;
        if (filterRecallCheckedBy && details.checkedBy !== filterRecallCheckedBy) return false;
        if (filterRecallMachine && details.machine !== filterRecallMachine) return false;
        if (filterRecallCycle && String(details.cycle) !== String(filterRecallCycle)) return false;
        return true;
      });

      setRecallSearchResults(filtered);
      // Automatically select all items that are not already recalled
      setSelectedRecallIds(filtered.filter(i => i.status !== 'recalled').map(i => i.id));
    } catch (err) {
      console.error("Recall search error:", err);
      alert("เกิดข้อผิดพลาดในการค้นหา: " + err.message);
    } finally {
      setIsRecallSearching(false);
    }
  };

  const handleClearRecallFilters = () => {
    setFilterRecallItemName('');
    setFilterRecallBarcode('');
    setFilterRecallMfgDate('');
    setFilterRecallPreparedBy('');
    setFilterRecallCheckedBy('');
    setFilterRecallMachine('');
    setFilterRecallCycle('');
    setRecallSearchResults([]);
    setSelectedRecallIds([]);
  };

  const getRecallImages = (round) => {
    if (!round) return [];
    const list = [];
    if (round.recallImageUrl) {
      list.push(round.recallImageUrl);
    }
    if (Array.isArray(round.recallImageUrls)) {
      round.recallImageUrls.forEach(url => {
        if (url && !list.includes(url)) {
          list.push(url);
        }
      });
    }
    return list;
  };

  const handleAddRecallRoundImage = async (batchId, newImageUrl) => {
    try {
      setSterileProcessingState('processing');
      const round = allRecallBatches.find(r => r.batchId === batchId);
      if (!round) return;

      const existingUrls = Array.isArray(round.recallImageUrls) ? round.recallImageUrls : [];
      const updatedUrls = [...existingUrls];
      
      // Migrate the legacy single imageUrl if it exists and isn't already included
      if (round.recallImageUrl && !updatedUrls.includes(round.recallImageUrl)) {
        updatedUrls.push(round.recallImageUrl);
      }
      
      if (!updatedUrls.includes(newImageUrl)) {
        updatedUrls.push(newImageUrl);
      }

      // Update in Supabase
      for (const inst of round.items) {
        const updatedDetails = {
          ...(inst.details || {}),
          recallImageUrls: updatedUrls
        };

        const { error } = await supabase
          .from('instruments')
          .update({
            details: updatedDetails,
            last_updated: new Date().toISOString()
          })
          .eq('id', inst.id);

        if (error) throw error;
      }

      // Update local state
      setInstruments(prev => prev.map(i => {
        if (round.items.some(item => item.id === i.id)) {
          return {
            ...i,
            details: {
              ...(i.details || {}),
              recallImageUrls: updatedUrls
            },
            last_updated: new Date().toISOString()
          };
        }
        return i;
      }));

      await fetchAllRecallBatches();
      setSterileProcessingState('success');
      setTimeout(() => {
        setSterileProcessingState(null);
      }, 1000);
    } catch (err) {
      console.error("Error adding recall round image:", err);
      setSterileProcessingState(null);
      alert("❌ ไม่สามารถเพิ่มรูปภาพเพิ่มเติมได้: " + err.message);
    }
  };

  const handleRecallImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsRecallUploading(true);
    try {
      const secureUrl = await uploadRecallImage(file);
      if (activeRecallBatchIdForUpload) {
        await handleAddRecallRoundImage(activeRecallBatchIdForUpload, secureUrl);
      } else {
        setRecallImageUrl(secureUrl);
        alert("📸 อัปโหลดรูปภาพวัสดุมีปัญหาสำเร็จ!");
      }
    } catch (err) {
      console.error("Recall image upload error:", err);
      alert("❌ อัปโหลดรูปภาพไม่สำเร็จ: " + (err.message || ""));
    } finally {
      setIsRecallUploading(false);
      setShowRecallUploadPopup(false);
      setActiveRecallBatchIdForUpload(null);
      e.target.value = '';
    }
  };

  const handleRemoveRecallImage = () => {
    setRecallImageUrl('');
  };

  const handleRecallAction = async () => {
    const selectedItems = recallSearchResults.filter(i => selectedRecallIds.includes(i.id));

    if (selectedItems.length === 0) {
      alert("กรุณาเลือกรายการเครื่องมืออย่างน้อย 1 รายการเพื่ออายัด");
      return;
    }

    if (!recallReason.trim()) {
      alert("กรุณากรอกเหตุผลสำหรับการเรียกคืน/อายัดวัสดุก่อนกดยืนยัน");
      return;
    }

    setSterileConfirmModal({
      message: `คุณกำลังจะอายัด (Recall) วัสดุจำนวน ${selectedItems.length} รายการที่เลือกไว้ การดำเนินการนี้จะเปลี่ยนสถานะวัสดุทั้งหมดเป็น "ถูกอายัด (Recall)" และต้องส่งคืนห้องล้างเพื่อตรวจสอบเท่านั้น`,
      onConfirm: async () => {
        try {
          setSterileProcessingState('processing');
          const timeStr = new Date().toISOString();
          const batchId = 'RECALL-' + new Date().getTime();
          const ids = selectedItems.map(i => i.id);

          // update status and details for each instrument in Supabase
          for (const inst of selectedItems) {
            const updatedDetails = {
              ...(inst.details || {}),
              originalStatus: inst.status || '',
              originalLocation: inst.location || '',
              recallBatchId: batchId,
              recallOutcome: 'pending',
              recallReason: recallReason.trim(),
              recallImageUrl: recallImageUrl || null
            };

            const { error } = await supabase
              .from('instruments')
              .update({
                status: 'recalled',
                location: '⚠️ อายัด (Recall) - ต้องส่งคืนห้องล้างเท่านั้น',
                details: updatedDetails,
                last_updated: timeStr
              })
              .eq('id', inst.id);

            if (error) throw error;
          }

          // add logs for each recalled instrument
          for (const inst of selectedItems) {
            addLog(
              inst.item_name,
              "เรียกคืนวัสดุ",
              `อายัดบาร์โค้ดสำเร็จเนื่องจาก: ${recallReason.trim()} (รหัส ${inst.unique_barcode})`
            );
          }

          // update local state
          setInstruments(prev => prev.map(i => {
            if (ids.includes(i.id)) {
              const matched = selectedItems.find(x => x.id === i.id);
              return {
                ...i,
                status: 'recalled',
                location: '⚠️ อายัด (Recall) - ต้องส่งคืนห้องล้างเท่านั้น',
                details: {
                  ...(matched?.details || {}),
                  originalStatus: matched?.status || '',
                  originalLocation: matched?.location || '',
                  recallBatchId: batchId,
                  recallOutcome: 'pending',
                  recallReason: recallReason.trim(),
                  recallImageUrl: recallImageUrl || null
                },
                last_updated: timeStr
              };
            }
            return i;
          }));

          setRecallReason('');
          setRecallImageUrl('');
          setSelectedRecallIds([]);
          await handleRecallSearch();
          await fetchAllRecallBatches();

          setSterileProcessingState('success');
          setTimeout(() => {
            setSterileProcessingState(null);
          }, 2200);
        } catch (err) {
          console.error(err);
          setSterileProcessingState(null);
          alert("เกิดข้อผิดพลาดในการเรียกคืนวัสดุ: " + err.message);
        }
      }
    });
  };

  const handleRequestCancelRecall = async (item, reason) => {
    try {
      const updatedDetails = {
        ...(item.details || {}),
        cancelRecallRequest: {
          requestedBy: user?.email || '',
          requestedAt: new Date().toISOString(),
          reason: reason.trim(),
          status: 'pending'
        }
      };

      const { error } = await supabase
        .from('instruments')
        .update({ details: updatedDetails })
        .eq('id', item.id);

      if (error) throw error;

      addLog(
        item.item_name,
        "ขออนุมัติยกเลิกอายัด",
        `ส่งคำขออนุมัติยกเลิกอายัด รหัส ${item.unique_barcode} เนื่องจาก: ${reason.trim()}`
      );

      // Update local state
      setInstruments(prev => prev.map(i => i.id === item.id ? { ...i, details: updatedDetails } : i));

      alert("ส่งคำขอยกเลิกการอายัดไปยัง Admin เรียบร้อยแล้ว");
      await handleRecallSearch();
    } catch (err) {
      console.error(err);
      alert("เกิดข้อผิดพลาดในการส่งคำขอ: " + err.message);
    }
  };

  const handleApproveCancelRecall = async (item) => {
    if (activeUserRole !== 'admin') {
      alert("สิทธิ์การเข้าใช้งานไม่ถูกต้อง: เฉพาะผู้ดูแลระบบ (Admin) เท่านั้นที่สามารถดำเนินการได้");
      return;
    }
    const origStatus = item.details?.originalStatus || 'sterile-ready';
    const origLocation = item.details?.originalLocation || 'คลังพัสดุ';

    // Clear all recall metadata from details
    const updatedDetails = { ...(item.details || {}) };
    delete updatedDetails.cancelRecallRequest;
    delete updatedDetails.originalStatus;
    delete updatedDetails.originalLocation;
    delete updatedDetails.recallBatchId;
    delete updatedDetails.recallOutcome;
    delete updatedDetails.recallReason;

    try {
      const { error } = await supabase
        .from('instruments')
        .update({
          status: origStatus,
          location: origLocation,
          details: updatedDetails,
          last_updated: new Date().toISOString()
        })
        .eq('id', item.id);

      if (error) throw error;

      addLog(
        item.item_name,
        "อนุมัติยกเลิกอายัด",
        `อนุมัติยกเลิกการอายัดสำเร็จ รหัส ${item.unique_barcode} โดย Admin ${user?.email}`
      );

      // Update local state
      setInstruments(prev => prev.map(i => i.id === item.id ? { ...i, status: origStatus, location: origLocation, details: updatedDetails, last_updated: new Date().toISOString() } : i));

      alert("อนุมัติยกเลิกการอายัดสำเร็จ สถานะอุปกรณ์คืนค่าแล้ว");
      await handleRecallSearch();
      await fetchAllRecallBatches();
    } catch (err) {
      console.error(err);
      alert("เกิดข้อผิดพลาดในการอนุมัติ: " + err.message);
    }
  };

  const handleRejectCancelRecall = async (item) => {
    if (activeUserRole !== 'admin') {
      alert("สิทธิ์การเข้าใช้งานไม่ถูกต้อง: เฉพาะผู้ดูแลระบบ (Admin) เท่านั้นที่สามารถดำเนินการได้");
      return;
    }
    const updatedDetails = { ...(item.details || {}) };
    delete updatedDetails.cancelRecallRequest;

    try {
      const { error } = await supabase
        .from('instruments')
        .update({ details: updatedDetails })
        .eq('id', item.id);

      if (error) throw error;

      addLog(
        item.item_name,
        "ปฏิเสธยกเลิกอายัด",
        `ปฏิเสธคำขอยกเลิกอายัด รหัส ${item.unique_barcode} โดย Admin ${user?.email}`
      );

      // Update local state
      setInstruments(prev => prev.map(i => i.id === item.id ? { ...i, details: updatedDetails } : i));

      alert("ปฏิเสธคำขอยกเลิกการอายัดเรียบร้อยแล้ว");
      await handleRecallSearch();
    } catch (err) {
      console.error(err);
      alert("เกิดข้อผิดพลาดในการปฏิเสธคำขอ: " + err.message);
    }
  };

  const fetchAllRecallBatches = async () => {
    setIsFetchingAllBatches(true);
    try {
      const { data, error } = await supabase
        .from('instruments')
        .select('*')
        .not('details->>recallBatchId', 'is', null);

      if (error) throw error;

      const groups = {};
      (data || []).forEach(item => {
        const bId = item.details?.recallBatchId;
        if (bId) {
          if (!groups[bId]) {
            groups[bId] = [];
          }
          groups[bId].push(item);
        }
      });

      const rounds = Object.keys(groups).map(bId => {
        const items = groups[bId];
        const tsMatch = bId.match(/RECALL-(\d+)/);
        const timestamp = tsMatch ? parseInt(tsMatch[1], 10) : 0;

        const firstItem = items[0];
        const machine = firstItem?.details?.machine || '';
        const cycle = firstItem?.details?.cycle || '';
        const mfgDate = firstItem?.details?.mfgDate || '';
        const recallReason = firstItem?.details?.recallReason || '';
        const recallImageUrl = firstItem?.details?.recallImageUrl || '';
        const recallImageUrls = firstItem?.details?.recallImageUrls || [];

        return {
          batchId: bId,
          timestamp,
          items,
          machine,
          cycle,
          mfgDate,
          recallReason,
          recallImageUrl,
          recallImageUrls,
          total: items.length,
          pending: items.filter(i => i.status === 'recalled' && i.is_active).length,
          used: items.filter(i => i.details?.recallOutcome === 'used').length,
          collected: items.filter(i => i.details?.recallOutcome === 'not_used').length
        };
      });

      rounds.sort((a, b) => b.timestamp - a.timestamp);
      setAllRecallBatches(rounds);
    } catch (err) {
      console.error("Error fetching all recall batches:", err);
    } finally {
      setIsFetchingAllBatches(false);
    }
  };

  const handleRecallPromptOutcome = (outcome) => {
    if (!recallScanPromptItem) return;

    const updatedDetails = {
      ...(recallScanPromptItem.details || {}),
      recallOutcome: outcome // 'used' or 'not_used'
    };

    const updatedItem = {
      ...recallScanPromptItem,
      details: updatedDetails
    };

    setWashingSuccessList(prev => [updatedItem, ...prev]);
    setRecallScanPromptItem(null);
  };

  useEffect(() => {
    if (currentPage === 'recall') {
      fetchAllRecallBatches();
      setRecallRoundsPage(1);
    }
  }, [currentPage]);

  useEffect(() => {
    if (currentPage === 'recall') {
      handleRecallSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterRecallMfgDate, filterRecallPreparedBy, filterRecallCheckedBy, filterRecallMachine, filterRecallCycle]);

  const handleWashingScan = (scannedCode) => {
    if (scannedCode.startsWith('BOX-')) {
      setWashingErrorList(prev => [{ code: scannedCode, reason: "กล่องหมอต้องคืนเข้าห้อง Sterile โดยตรง ห้ามรับเข้าห้องล้าง!" }, ...prev]);
      return setBarcodeInput('');
    }
    if (washingSuccessList.find(i => i.unique_barcode === scannedCode)) return setBarcodeInput('');
    const instr = instruments.find(i => i.unique_barcode === scannedCode && i.is_active);
    if (!instr) {
      setWashingErrorList(prev => [{ code: scannedCode, reason: "ไม่พบรหัส/ถูกปิดไปแล้ว" }, ...prev]);
    } else {
      if (instr.status === 'recalled') {
        setRecallScanPromptItem(instr);
      } else {
        setWashingSuccessList(prev => [instr, ...prev]);
      }
    }
    setBarcodeInput('');
  };

  const handleConfirmWashing = async () => {
    if (washingSuccessList.length === 0) return;
    let currentDbSupplies = JSON.parse(JSON.stringify(supplies));

    for (const instr of washingSuccessList) {
      await supabase.from('instruments').update({
        is_active: false,
        status: 'finished',
        container_code: null,
        details: instr.details,
        last_updated: new Date().toISOString()
      }).eq('id', instr.id);

      const curLocIndex = currentDbSupplies.findIndex(s => s.master_id === instr.master_id && s.warehouse_name === instr.location);
      if (curLocIndex > -1 && currentDbSupplies[curLocIndex].item_amount > 0) {
        currentDbSupplies[curLocIndex].item_amount -= 1;
        await supabase.from('supplies').update({ item_amount: currentDbSupplies[curLocIndex].item_amount, last_updated: new Date().toISOString() }).eq('id', currentDbSupplies[curLocIndex].id);
      }

      const scannerName = user?.email ? user.email.split('@')[0] : 'system';
      const master = itemMaster.find(masterLot => masterLot.id === instr.master_id);
      if (master?.is_set) {
        for (const compId of master.components) {
          const wsIndex = currentDbSupplies.findIndex(s => s.master_id === compId && s.warehouse_name.includes("ห้องล้างเครื่องมือ"));
          if (wsIndex > -1) {
            currentDbSupplies[wsIndex].item_amount += 1;
            await supabase.from('supplies').update({ item_amount: currentDbSupplies[wsIndex].item_amount, last_updated: new Date().toISOString() }).eq('id', currentDbSupplies[wsIndex].id);
          }
        }
        addLog(instr.item_name, "ห้องล้างเครื่องมือ", `รับคืน (แยกชุด) รหัส ${instr.unique_barcode} โดย ${scannerName}`);
      } else {
        const wsIndex = currentDbSupplies.findIndex(s => s.master_id === instr.master_id && s.warehouse_name.includes("ห้องล้างเครื่องมือ"));
        if (wsIndex > -1) {
          currentDbSupplies[wsIndex].item_amount += 1;
          await supabase.from('supplies').update({ item_amount: currentDbSupplies[wsIndex].item_amount, last_updated: new Date().toISOString() }).eq('id', currentDbSupplies[wsIndex].id);
        }
        addLog(instr.item_name, "ห้องล้างเครื่องมือ", `รับคืน รหัส ${instr.unique_barcode} โดย ${scannerName}`);
      }
    }
    setSupplies(currentDbSupplies);
    setWashingSuccessList([]); setWashingErrorList([]);
    await fetchAllRecallBatches();
    alert("รับคืนพัสดุสำเร็จ!");
  };

  const handleDispenseScan = (val) => {
    if (!selectedOrder) { setScanErrorMessage("เลือก Order ก่อน!"); return; }

    const foundItemForRecallCheck = instruments.find(i => i.unique_barcode === val && i.is_active);
    if (foundItemForRecallCheck && foundItemForRecallCheck.status === 'recalled') {
      setScanErrorMessage("❌ อุปกรณ์นี้ถูกอายัด (Recall) ต้องส่งคืนห้องล้างเพื่อตรวจสอบเท่านั้น!");
      return;
    }

    if (val.startsWith('BOX-')) {
      const box = instruments.find(i => i.unique_barcode === val && i.is_active);
      if (!box) return setScanErrorMessage("ไม่พบรหัสกล่องนี้!");

      const itemsInBox = instruments.filter(i => i.container_code === val && i.is_active);
      if (itemsInBox.length === 0) return setScanErrorMessage("กล่องนี้ไม่มีหัวกรออยู่เลย!");

      const recalledItemsInBox = itemsInBox.filter(i => i.status === 'recalled');
      if (recalledItemsInBox.length > 0) {
        setScanErrorMessage(`❌ ในกล่องนี้มีอุปกรณ์ ${recalledItemsInBox.length} ชิ้นที่ถูกอายัด (Recall)! ต้องแยกส่งคืนห้องล้างเท่านั้น`);
        return;
      }

      let addedSomething = false;
      let tempSuccessList = [...sterileSuccessList];
      let boxAdded = false;

      const orderBoxItem = selectedOrder.items.find(it => it.name === box.item_name);
      if (orderBoxItem) {
        const currentScannedQty = tempSuccessList.filter(s => s.item_name === box.item_name).length;
        if (currentScannedQty < orderBoxItem.qty && !tempSuccessList.some(s => s.unique_barcode === box.unique_barcode)) {
          tempSuccessList.push(box);
          addedSomething = true;
          boxAdded = true;
          itemsInBox.forEach(item => {
            if (!tempSuccessList.some(s => s.unique_barcode === item.unique_barcode)) tempSuccessList.push(item);
          });
        }
      }

      if (!boxAdded) {
        itemsInBox.forEach(item => {
          const orderItem = selectedOrder.items.find(it => it.name === item.item_name);
          if (orderItem) {
            const currentScannedQty = tempSuccessList.filter(s => s.item_name === item.item_name).length;
            if (currentScannedQty < orderItem.qty && !tempSuccessList.some(s => s.unique_barcode === item.unique_barcode)) {
              tempSuccessList.push(item);
              addedSomething = true;
            }
          }
        });
        if (addedSomething && !tempSuccessList.some(s => s.unique_barcode === box.unique_barcode)) {
          tempSuccessList.push(box);
        }
      }

      if (!addedSomething) return setScanErrorMessage("ของในกล่องนี้ไม่ตรงกับ Order หรือจัดครบไปแล้ว!");
      setSterileSuccessList(tempSuccessList); setScanErrorMessage(''); return;
    }

    const instr = instruments.find(i => i.unique_barcode === val && i.is_active);
    if (!instr) { setScanErrorMessage("ไม่พบรหัส!"); return; }
    if (instr.status !== 'sterile-ready') {
      const loc = instr.location.replace('- ', '').trim();
      setScanErrorMessage(`ยังจ่ายไม่ได้! ของอยู่ที่ "${loc}" ต้องสแกนรับเข้าห้อง Sterile ก่อน`); return;
    }
    const orderItem = selectedOrder.items.find(it => it.name === instr.item_name);
    if (!orderItem) { setScanErrorMessage("ไม่มีใน Order!"); return; }
    if (sterileSuccessList.some(s => s.unique_barcode === val)) { setScanErrorMessage("⚠️ สแกนซ้ำ! บาร์โค้ดนี้ถูกสแกนลงตะกร้าไปแล้ว"); return; }
    const currentScannedQty = sterileSuccessList.filter(s => s.item_name === instr.item_name).length;
    if (currentScannedQty >= orderItem.qty) { setScanErrorMessage(`จัดครบแล้ว! (${orderItem.name} ต้องการแค่ ${orderItem.qty} ชิ้น)`); return; }
    setScanErrorMessage(''); setSterileSuccessList(prev => [...prev, instr]);
  };

  const handleSterileScan = (scannedCode, isContinuous = false) => {
    // 1. ตรวจสอบการสแกนซ้ำในรายการสำเร็จ
    const duplicate = sterileSuccessList.find(i => i.unique_barcode === scannedCode);
    if (duplicate) {
      setBarcodeInput('');
      return {
        status: 'duplicate',
        message: `โค้ดนี้สแกนไปแล้ว เมื่อเวลา ${duplicate.scanTime || 'ไม่ระบุ'}`
      };
    }

    const scanTimeStr = new Date().toLocaleTimeString('th-TH');

    // ฟังก์ชันย่อยสำหรับบันทึกปัญหา
    const logProblem = (msg) => {
      const problemItem = {
        unique_barcode: scannedCode,
        error_msg: msg,
        scanTime: scanTimeStr
      };
      setSterileProblemList(prev => [problemItem, ...prev]);
      setBarcodeInput('');
      if (!isContinuous) {
        showSterileError(msg);
      }
      return { status: 'error', message: msg };
    };

    const targetInstrForRecallCheck = instruments.find(i => i.unique_barcode === scannedCode && i.is_active);
    if (targetInstrForRecallCheck && targetInstrForRecallCheck.status === 'recalled') {
      return logProblem("❌ อุปกรณ์นี้ถูกอายัด (Recall) ต้องส่งคืนห้องล้างเพื่อตรวจสอบเท่านั้น!");
    }

    if (actionType === 'auto-pack') {
      const instr = instruments.find(i => i.unique_barcode === scannedCode && i.is_active);
      if (scannedCode.startsWith('BOX-')) {
        return logProblem("❌ ผิดพลาด: โหมดนี้ให้สแกนหัวกรอที่มีเป้าหมาย ไม่ต้องสแกนกล่องครับ");
      }
      if (!instr) {
        return logProblem("❌ ผิดพลาด: ไม่พบรหัสอุปกรณ์");
      }
      if (!instr.details?.targetBoxBarcode) {
        return logProblem("❌ ผิดพลาด: บาร์โค้ดนี้ไม่ได้ถูกผูกกล่องเป้าหมายไว้ตั้งแต่ตอนแพ็กซอง");
      }
      if (instr.status !== 'sterile-ready') {
        return logProblem("❌ ผิดพลาด: อุปกรณ์ต้องพร้อมจ่าย (รับเข้านึ่งเสร็จก่อน)");
      }

      const itemWithTime = { ...instr, scanTime: scanTimeStr };
      setSterileSuccessList(prev => [itemWithTime, ...prev]);
      setBarcodeInput('');
      return { status: 'success' };
    }

    const instr = instruments.find(i => i.unique_barcode === scannedCode && i.is_active);

    if (!instr) {
      return logProblem(`❌ ผิดพลาด: ไม่พบรหัส ${scannedCode} หรือถูกปิดไปแล้ว`);
    } else {
      if (actionType === 'receive-item') {
        if (scannedCode.startsWith('BOX-')) {
          const itemsInBox = instruments.filter(i => i.container_code === scannedCode && i.is_active);

          if (itemsInBox.length === 0) {
            const itemWithTime = { ...instr, scanTime: scanTimeStr };
            setSterileSuccessList(prev => {
              const nextList = [...prev];
              if (!nextList.some(s => s.unique_barcode === scannedCode)) {
                nextList.push(itemWithTime);
              }
              return nextList;
            });
            setBarcodeInput('');
            return { status: 'success' };
          }

          setSterileSuccessList(prev => {
            const nextList = [...prev];
            const itemWithTimeBox = { ...instr, scanTime: scanTimeStr };
            if (!nextList.some(s => s.unique_barcode === scannedCode)) {
              nextList.push(itemWithTimeBox);
            }
            itemsInBox.forEach(item => {
              if (!nextList.some(s => s.unique_barcode === item.unique_barcode)) {
                nextList.push({ ...item, scanTime: scanTimeStr });
              }
            });
            return nextList;
          });
          setBarcodeInput('');
          return { status: 'success' };
        } else {
          if (instr.status === 'sterile-ready') {
            return logProblem(`❌ ผิดพลาด: อุปกรณ์นี้อยู่ในสถานะพร้อมจ่ายอยู่แล้ว (รหัส: ${scannedCode})`);
          } else if (instr.status !== 'sterile-pending' && instr.status !== 'in-use') {
            return logProblem(`❌ ผิดพลาด: ไม่สามารถรับได้เนื่องจากสถานะไม่ถูกต้อง (รหัส: ${scannedCode}, สถานะ: ${instr.status})`);
          } else {
            const itemWithTime = { ...instr, scanTime: scanTimeStr };
            setSterileSuccessList(prev => [itemWithTime, ...prev]);
            setBarcodeInput('');
            return { status: 'success' };
          }
        }
      } else if (actionType === 'increase') {
        if (instr.status !== 'sterile-pending') {
          return logProblem(`❌ ผิดพลาด: ของไม่ได้กำลังนึ่ง (รหัส: ${scannedCode}, สถานะปัจจุบัน: ${instr.status})`);
        } else {
          const itemWithTime = { ...instr, scanTime: scanTimeStr };
          setSterileSuccessList(prev => [itemWithTime, ...prev]);
          setBarcodeInput('');
          return { status: 'success' };
        }
      } else if (actionType === 'return') {
        if (scannedCode.startsWith('BOX-')) {
          const itemsInBox = instruments.filter(i => i.container_code === scannedCode && i.is_active);
          setSterileSuccessList(prev => {
            const nextList = [...prev];
            const itemWithTimeBox = { ...instr, scanTime: scanTimeStr };
            if (!nextList.some(s => s.unique_barcode === scannedCode)) {
              nextList.push(itemWithTimeBox);
            }
            itemsInBox.forEach(item => {
              if (!nextList.some(s => s.unique_barcode === item.unique_barcode)) {
                nextList.push({ ...item, scanTime: scanTimeStr });
              }
            });
            return nextList;
          });
          setBarcodeInput('');
          return { status: 'success' };
        } else if (instr.status !== 'in-use') {
          return logProblem(`❌ ผิดพลาด: สถานะไม่ใช่ "อยู่ในคลินิก" (รหัส: ${scannedCode}, สถานะปัจจุบัน: ${instr.status})`);
        } else {
          const itemWithTime = { ...instr, scanTime: scanTimeStr };
          setSterileSuccessList(prev => [itemWithTime, ...prev]);
          setBarcodeInput('');
          return { status: 'success' };
        }
      } else if (actionType === 'bind-box') {
        if (scannedCode.startsWith('BOX-')) {
          if (sterileSuccessList.length > 0) {
            if (!window.confirm("คุณมีการสแกนหัวกรอค้างอยู่ การสแกนกล่องใหม่จะล้างรายการเดิม ต้องการดำเนินการต่อหรือไม่?")) {
              setBarcodeInput('');
              return { status: 'cancelled' };
            }
            setSterileSuccessList([]);
          }
          setTargetBox(instr);
          setBarcodeInput('');
          return { status: 'success' };
        } else {
          if (!targetBox) {
            return logProblem("❌ ผิดพลาด: กรุณาสแกนรหัสกล่อง (BOX-...) ก่อนครับ");
          } else if (instr.status !== 'sterile-ready') {
            return logProblem(`❌ ผิดพลาด: อุปกรณ์ต้องผ่านการนึ่ง (พร้อมจ่าย) แล้ว จึงจัดลงกล่องได้ (รหัส: ${scannedCode}, สถานะปัจจุบัน: ${instr.status})`);
          } else {
            const itemWithTime = { ...instr, scanTime: scanTimeStr };
            setSterileSuccessList(prev => [itemWithTime, ...prev]);
            setBarcodeInput('');
            return { status: 'success' };
          }
        }
      }
    }
    setBarcodeInput('');
    return { status: 'success' };
  };

  const handleConfirmSterileBasket = () => {
    if (sterileSuccessList.length === 0) return;
    if (sterileProcessingState) return;

    let confirmMsg = `ยืนยันดำเนินการใช่ไหม?`;
    if (actionType === 'receive-item') {
      confirmMsg = `ยืนยันรับของจำนวน ${sterileSuccessList.length} ใช่ไหม`;
    } else if (actionType === 'bind-box' || actionType === 'auto-pack') {
      confirmMsg = `ยืนยันจัดลงกล่องจำนวน ${sterileSuccessList.length} ใช่ไหม`;
    } else if (actionType === 'decrease' || currentPage === 'dispense') {
      confirmMsg = `ยืนยันจ่ายของจำนวน ${sterileSuccessList.length} ใช่ไหม`;
    }

    setSterileConfirmModal({
      message: confirmMsg,
      onConfirm: () => proceedConfirmSterileBasket()
    });
  };

  const proceedConfirmSterileBasket = async () => {

    if (actionType === 'auto-pack' || (actionType === 'bind-box' && targetBox)) {
      setSterileProcessingState('processing');
      try {
        const updatePromises = sterileSuccessList.map(instr => {
          const targetCode = actionType === 'auto-pack' ? instr.details.targetBoxBarcode : targetBox.unique_barcode;
          return supabase.from('instruments').update({
            container_code: targetCode,
            location: `   - อยู่ในกล่อง ${targetCode}`,
            last_updated: new Date().toISOString()
          }).eq('id', instr.id);
        });
        await Promise.all(updatePromises);

        setInstruments(prev => prev.map(i => {
          const scanned = sterileSuccessList.find(s => s.id === i.id);
          if (scanned) {
            const targetCode = actionType === 'auto-pack' ? scanned.details.targetBoxBarcode : targetBox.unique_barcode;
            return { ...i, container_code: targetCode, location: `   - อยู่ในกล่อง ${targetCode}` };
          }
          return i;
        }));

        for (const instr of sterileSuccessList) {
          const targetCode = actionType === 'auto-pack' ? instr.details.targetBoxBarcode : targetBox.unique_barcode;
          addLog(instr.item_name, "ห้อง sterile", `จัดลงกล่อง ${targetCode} รหัส ${instr.unique_barcode}`);
        }

        setSterileProcessingState('success');
        setTimeout(() => {
          setSterileSuccessList([]);
          setSterileProblemList([]);
          setTargetBox(null);
          setIsSterileModalOpen(false);
          setSterileProcessingState(null);
        }, 2200);
      } catch (err) {
        console.error(err);
        alert("เกิดข้อผิดพลาดในการจัดของลงกล่อง: " + err.message);
        setSterileProcessingState(null);
      }
      return;
    }

    if ((actionType === 'decrease' || currentPage === 'dispense') && !targetWarehouse) return alert("กรุณาเลือกห้องตรวจปลายทาง");

    let incompleteReason = null;
    let updatedOrderItems = null;

    if ((currentPage === 'dispense' || isDispenseModalOpen) && selectedOrder) {
      const missingItems = [];
      updatedOrderItems = selectedOrder.items.map(it => {
        const scannedQty = sterileSuccessList.filter(s => s.item_name === it.name).length;
        if (scannedQty < it.qty) missingItems.push(`- ${it.name} (ขาด ${it.qty - scannedQty} ชิ้น)`);
        return { ...it, dispensed_qty: scannedQty };
      });

      if (missingItems.length > 0) {
        const confirmMsg = `⚠️ ของยังไม่ครบตามคำสั่งซื้อ!\n\n${missingItems.join('\n')}\n\n-> กด OK หากต้องการระบุเหตุผลและยืนยันจ่ายของเท่าที่มี\n-> กด Cancel หากต้องการสแกนจัดของต่อ`;
        if (!window.confirm(confirmMsg)) return;
        incompleteReason = window.prompt("กรุณาระบุเหตุผลที่จ่ายของไม่ครบ (เพื่อแจ้งคลินิก):");
        if (!incompleteReason || incompleteReason.trim() === "") return alert("ต้องระบุเหตุผลด้วยครับ จึงจะจ่ายของไม่ครบได้");
      }
    }

    setSterileProcessingState('processing');
    try {
      setInstruments(prev => prev.map(i => {
        if (sterileSuccessList.some(s => s.id === i.id)) {
          if (actionType === 'increase' || actionType === 'return' || actionType === 'receive-item') return { ...i, status: 'sterile-ready', location: '   - ห้อง sterile', last_updated: new Date().toISOString() };
          else if (actionType === 'decrease' || currentPage === 'dispense') return { ...i, status: 'in-use', location: targetWarehouse, last_updated: new Date().toISOString() };
        }
        return i;
      }));

      let currentDbSupplies = JSON.parse(JSON.stringify(supplies));

      for (const instr of sterileSuccessList) {
        if (actionType === 'receive-item') {
          const isSteaming = instr.status === 'sterile-pending';
          const isReturn = instr.status === 'in-use';

          await supabase.from('instruments').update({ status: 'sterile-ready', location: '   - ห้อง sterile', last_updated: new Date().toISOString() }).eq('id', instr.id);
          if (!instr.unique_barcode.startsWith('BOX-')) {
            if (isSteaming) {
              const psIndex = currentDbSupplies.findIndex(s => s.master_id === instr.master_id && s.warehouse_name.includes("กำลัง sterile"));
              const stIndex = currentDbSupplies.findIndex(s => s.master_id === instr.master_id && s.warehouse_name.includes("ห้อง sterile"));
              if (psIndex > -1 && currentDbSupplies[psIndex].item_amount > 0) {
                currentDbSupplies[psIndex].item_amount -= 1;
                await supabase.from('supplies').update({ item_amount: currentDbSupplies[psIndex].item_amount, last_updated: new Date().toISOString() }).eq('id', currentDbSupplies[psIndex].id);
              }
              if (stIndex > -1) {
                currentDbSupplies[stIndex].item_amount += 1;
                await supabase.from('supplies').update({ item_amount: currentDbSupplies[stIndex].item_amount, last_updated: new Date().toISOString() }).eq('id', currentDbSupplies[stIndex].id);
              }
            } else if (isReturn) {
              const curIndex = currentDbSupplies.findIndex(s => s.master_id === instr.master_id && s.warehouse_name === instr.location);
              const stIndex = currentDbSupplies.findIndex(s => s.master_id === instr.master_id && s.warehouse_name.includes("ห้อง sterile"));
              if (curIndex > -1 && currentDbSupplies[curIndex].item_amount > 0) {
                currentDbSupplies[curIndex].item_amount -= 1;
                await supabase.from('supplies').update({ item_amount: currentDbSupplies[curIndex].item_amount, last_updated: new Date().toISOString() }).eq('id', currentDbSupplies[curIndex].id);
              }
              if (stIndex > -1) {
                currentDbSupplies[stIndex].item_amount += 1;
                await supabase.from('supplies').update({ item_amount: currentDbSupplies[stIndex].item_amount, last_updated: new Date().toISOString() }).eq('id', currentDbSupplies[stIndex].id);
              }
            }
          }
          if (isSteaming) {
            addLog(instr.item_name, "ห้อง sterile", `รับเข้านึ่งเสร็จ รหัส ${instr.unique_barcode}`);
          } else {
            addLog(instr.item_name, "ห้อง sterile", `รับคืน (ไม่ได้ใช้) รหัส ${instr.unique_barcode}`);
          }
        } else if (actionType === 'increase') {
          await supabase.from('instruments').update({ status: 'sterile-ready', location: '   - ห้อง sterile', last_updated: new Date().toISOString() }).eq('id', instr.id);
          if (!instr.unique_barcode.startsWith('BOX-')) {
            const psIndex = currentDbSupplies.findIndex(s => s.master_id === instr.master_id && s.warehouse_name.includes("กำลัง sterile"));
            const stIndex = currentDbSupplies.findIndex(s => s.master_id === instr.master_id && s.warehouse_name.includes("ห้อง sterile"));
            if (psIndex > -1 && currentDbSupplies[psIndex].item_amount > 0) {
              currentDbSupplies[psIndex].item_amount -= 1;
              await supabase.from('supplies').update({ item_amount: currentDbSupplies[psIndex].item_amount, last_updated: new Date().toISOString() }).eq('id', currentDbSupplies[psIndex].id);
            }
            if (stIndex > -1) {
              currentDbSupplies[stIndex].item_amount += 1;
              await supabase.from('supplies').update({ item_amount: currentDbSupplies[stIndex].item_amount, last_updated: new Date().toISOString() }).eq('id', currentDbSupplies[stIndex].id);
            }
          }
          addLog(instr.item_name, "ห้อง sterile", `รับเข้านึ่งเสร็จ รหัส ${instr.unique_barcode}`);

        } else if (actionType === 'decrease' || currentPage === 'dispense') {
          await supabase.from('instruments').update({ status: 'in-use', location: targetWarehouse, last_updated: new Date().toISOString() }).eq('id', instr.id);
          if (!instr.unique_barcode.startsWith('BOX-')) {
            const stIndex = currentDbSupplies.findIndex(s => s.master_id === instr.master_id && s.warehouse_name.includes("ห้อง sterile"));
            const dsIndex = currentDbSupplies.findIndex(s => s.master_id === instr.master_id && s.warehouse_name === targetWarehouse);
            if (stIndex > -1 && currentDbSupplies[stIndex].item_amount > 0) {
              currentDbSupplies[stIndex].item_amount -= 1;
              await supabase.from('supplies').update({ item_amount: currentDbSupplies[stIndex].item_amount, last_updated: new Date().toISOString() }).eq('id', currentDbSupplies[stIndex].id);
            }
            if (dsIndex > -1) {
              currentDbSupplies[dsIndex].item_amount += 1;
              await supabase.from('supplies').update({ item_amount: currentDbSupplies[dsIndex].item_amount, last_updated: new Date().toISOString() }).eq('id', currentDbSupplies[dsIndex].id);
            }
          }
          addLog(instr.item_name, targetWarehouse, `จ่ายออก รหัส ${instr.unique_barcode}`);

        } else if (actionType === 'return') {
          await supabase.from('instruments').update({ status: 'sterile-ready', location: '   - ห้อง sterile', last_updated: new Date().toISOString() }).eq('id', instr.id);
          if (!instr.unique_barcode.startsWith('BOX-')) {
            const curIndex = currentDbSupplies.findIndex(s => s.master_id === instr.master_id && s.warehouse_name === instr.location);
            const stIndex = currentDbSupplies.findIndex(s => s.master_id === instr.master_id && s.warehouse_name.includes("ห้อง sterile"));
            if (curIndex > -1 && currentDbSupplies[curIndex].item_amount > 0) {
              currentDbSupplies[curIndex].item_amount -= 1;
              await supabase.from('supplies').update({ item_amount: currentDbSupplies[curIndex].item_amount, last_updated: new Date().toISOString() }).eq('id', currentDbSupplies[curIndex].id);
            }
            if (stIndex > -1) {
              currentDbSupplies[stIndex].item_amount += 1;
              await supabase.from('supplies').update({ item_amount: currentDbSupplies[stIndex].item_amount, last_updated: new Date().toISOString() }).eq('id', currentDbSupplies[stIndex].id);
            }
          }
          addLog(instr.item_name, "ห้อง sterile", `รับคืน (ไม่ได้ใช้) รหัส ${instr.unique_barcode}`);
        }
      }

      setSupplies(currentDbSupplies);
      if ((currentPage === 'dispense' || isDispenseModalOpen) && selectedOrder) {
        const orderUpdate = { status: 'completed', items: updatedOrderItems };
        if (incompleteReason) orderUpdate.cancel_reason = `จ่ายของไม่ครบ: ${incompleteReason}`;
        setOrders(prev => prev.map(o => o.id === selectedOrder.id ? { ...o, ...orderUpdate } : o));
        await supabase.from('orders').update(orderUpdate).eq('id', selectedOrder.id);
      }

      setSterileProcessingState('success');
      setTimeout(() => {
        setSterileSuccessList([]);
        setSterileProblemList([]);
        setSelectedOrder(null);
        setTargetWarehouse('');
        setIsSterileModalOpen(false);
        setIsDispenseModalOpen(false);
        setSterileProcessingState(null);
      }, 2200);
    } catch (err) {
      console.error(err);
      alert("เกิดข้อผิดพลาดในการดำเนินการ: " + err.message);
      setSterileProcessingState(null);
    }
  };

  const handleCancelOrder = async (orderId, clinicName) => {
    const reason = window.prompt("กรุณาระบุเหตุผลที่ยกเลิก Order นี้:");
    if (!reason || reason.trim() === "") return alert("ต้องระบุเหตุผลในการยกเลิกครับ");

    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'cancelled', cancel_reason: reason } : o));
    const { error } = await supabase.from('orders').update({ status: 'cancelled', cancel_reason: reason }).eq('id', orderId);
    if (error) alert("เกิดข้อผิดพลาดในการยกเลิก: " + error.message);
    else addLog("Order", clinicName, `ยกเลิกใบสั่งของ: ${reason}`);

    if (selectedOrder && selectedOrder.id === orderId) { setSelectedOrder(null); setSterileSuccessList([]); setTargetWarehouse(''); }
    alert("ยกเลิก Order สำเร็จครับ");
  };

  const handleOrderQuickAdd = (masterLot) => {
    setOrderBasket(prev => {
      const exist = prev.find(x => x.name === masterLot.item_name);
      if (exist) return prev.map(x => x.name === masterLot.item_name ? { ...x, qty: x.qty + 1 } : x);
      return [...prev, { name: masterLot.item_name, qty: 1 }];
    });
  };

  const handleOrderQuickRemove = (it) => {
    setOrderBasket(prev => prev.map(x => x.name === it.name ? { ...x, qty: x.qty - 1 } : x).filter(x => x.qty > 0));
  };

  const handleSendOrder = async () => {
    if (!targetWarehouse) {
      alert("กรุณาระบุห้อง");
      return false;
    }
    if (orderBasket.length === 0) {
      alert("เลือกของลงตะกร้าก่อนครับ");
      return false;
    }

    const orderUser = orderedByName || user?.email || 'ไม่ระบุ';
    const formattedNote = `ordered_by:${orderUser}|note:${orderNote || ''}`;
    const newOrder = { id: Math.random().toString(), clinic_name: targetWarehouse, items: orderBasket, note: formattedNote, status: 'pending', timestamp: new Date().toISOString() };
    setOrders(prev => [newOrder, ...prev]);

    const { error } = await supabase.from('orders').insert([{ clinic_name: targetWarehouse, items: orderBasket, note: formattedNote, status: 'pending' }]);
    if (error) {
      alert("เกิดข้อผิดพลาดตอนส่งใบสั่งของ: " + error.message);
      return false;
    }

    setOrderBasket([]);
    setOrderNote('');
    setIsCartModalOpen(false);
    alert("ส่งใบสั่งของสำเร็จ!");
    return true;
  };

  const getTrackedInstruments = () => {
    const tracked = instruments.filter(i => i.is_active && (i.status === 'sterile-pending' || i.status === 'sterile-ready' || i.status === 'in-use' || i.status === 'recalled'));
    return tracked.reduce((acc, curr) => {
      if (!acc[curr.item_name]) acc[curr.item_name] = [];
      acc[curr.item_name].push(curr);
      return acc;
    }, {});
  };

  const handleTraceSearch = async () => {
    if (!traceInput) return;
    setTraceError('');

    let instrData = null;
    let logData = [];

    try {
      if (supabase) {
        const { data, error } = await supabase.from('instruments').select('*').eq('unique_barcode', traceInput).single();
        if (!error) instrData = data;

        const { data: logsData, error: logErr } = await supabase.from('logs').select('*').like('action', `%${traceInput}%`).order('timestamp', { ascending: true });
        if (!logErr) logData = logsData || [];
      }
    } catch (err) {
      console.error("Supabase trace query error, using local fallback:", err);
    }

    // Fallback to local memory arrays if database returned nothing
    if (!instrData) {
      instrData = instruments.find(i => i.unique_barcode === traceInput) || null;
    }
    if (logData.length === 0) {
      logData = logs.filter(l => l.action && l.action.includes(traceInput))
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    }

    if (!instrData && logData.length === 0) {
      setTraceError("❌ ไม่พบรหัสบาร์โค้ดนี้ในระบบ กรุณาตรวจสอบความถูกต้อง");
      setTraceData(null);
      setTraceLogs([]);
    } else {
      setTraceData(instrData);
      setTraceLogs(logData);
    }
  };

  const blankChunks = chunkArray(blankLabelsToPrint, 8);

  let scanBlankMaxAvail = 0;
  if (scanBlankItem) {
    scanBlankMaxAvail = scanBlankItem.is_set ? scanBlankItem.components.reduce((min, compId) => {
      const ws = supplies.find(s => s.master_id === compId && s.warehouse_name.includes("ห้องล้างเครื่องมือ"));
      return Math.min(min, ws?.item_amount || 0);
    }, 9999) : supplies.find(s => s.master_id === scanBlankItem.id && s.warehouse_name.includes("ห้องล้างเครื่องมือ"))?.item_amount || 0;

    if (currentPage !== 'tool-assembly' && targetBoxForPack && !scanBlankItem.is_set) {
      const tBarcode = targetBoxForPack.split('|')[0];
      const boxInst = instruments.find(i => i.unique_barcode === tBarcode);
      if (boxInst) {
        const boxTemplate = itemMaster.find(m => m.id === boxInst.master_id);
        const reqQty = boxTemplate?.components?.filter(id => id === scanBlankItem.id).length || 0;
        const assignedQty = instruments.filter(i => i.is_active && i.master_id === scanBlankItem.id && (i.container_code === tBarcode || i.details?.targetBoxBarcode === tBarcode)).length;
        const missingQty = Math.max(0, reqQty - assignedQty);
        scanBlankMaxAvail = Math.min(scanBlankMaxAvail, missingQty);
      }
    }
  }

  // 🛡️ Dedicated Risk App Header & Isolated View when on Risk Subdomain
  if (isRiskSubdomain()) {
    return (
      <div
        className="risk-system-container"
        style={{
          minHeight: '100vh',
          background: '#0f172a',
          fontFamily: "'Segoe UI', 'Noto Sans Thai', -apple-system, sans-serif",
          color: '#f8fafc'
        }}
      >
        {/* 🛡️ Dedicated Risk App Header */}
        <header
          style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            position: 'sticky',
            top: 0,
            zIndex: 1000
          }}
        >
          <div
            style={{
              maxWidth: '1400px',
              margin: '0 auto',
              padding: isMobile ? '8px 12px' : '8px 24px',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {/* User Profile / Logout */}
            {!isGuestMode && user && (
              <button
                type="button"
                data-testid="risk-header-profile-btn"
                onClick={() => setShowUserProfileModal(true)}
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  borderRadius: '20px',
                  padding: '4px 12px 4px 6px',
                  color: '#ffffff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.88rem'
                }}
                title="คลิกเพื่อจัดการโปรไฟล์"
              >
                {currentStaffProfile?.avatar_url || currentStaffProfile?.line_picture_url ? (
                  <img
                    src={currentStaffProfile.avatar_url || currentStaffProfile.line_picture_url}
                    alt="Profile"
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <span style={{ fontSize: '14px' }}>👤</span>
                )}
                <span>
                  {currentStaffProfile?.name ||
                    currentStaffProfile?.username ||
                    (user?.email ? user.email.split('@')[0] : 'ผู้ใช้งาน')}
                </span>
              </button>
            )}

            <button
              type="button"
              onClick={onLogout}
              style={{
                background: '#ef4444',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 14px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '0.88rem'
              }}
            >
              {isGuestMode ? '🚪 เข้าสู่ระบบ' : '🚪 ออกจากระบบ'}
            </button>
          </div>
        </header>

        {/* Main Risk Body: Only render RiskPortalStandalonePage or DentalRiskReportPage */}
        <main
          style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: isMobile ? '8px' : '16px'
          }}
        >
          {currentPage === 'dental-risk' ? (
            <DentalRiskReportPage
              user={user}
              userRole={userRole}
              isMobile={isMobile}
              onNavigate={(page) => {
                if (page === 'dental-risk-portal') setCurrentPage('dental-risk-portal');
                else setCurrentPage('dental-risk');
              }}
            />
          ) : (
            <RiskPortalStandalonePage
              onNavigateToStaffRisk={() => setCurrentPage('dental-risk')}
              user={user}
              userRole={userRole}
            />
          )}
        </main>

        {/* 👤 User Profile & Account Management Modal */}
        <UserProfileModal
          isOpen={showUserProfileModal}
          onClose={() => setShowUserProfileModal(false)}
          user={user}
          currentUserProfile={currentStaffProfile}
          onProfileUpdated={fetchUserProfiles}
        />
      </div>
    );
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
      minHeight: '100vh',
      fontFamily: "'Segoe UI', 'Noto Sans Thai', -apple-system, sans-serif",
      color: '#ffffff'
    }}>

      {/* 🌟 Hidden Printing Nodes */}
      <div style={{ position: 'absolute', top: '-9999px', left: '-9999px' }}>
        {printPreviewItem && (
          <div id="single-print-node" style={{ width: '200px', height: '400px', overflow: 'hidden', }}>
            <DentalLabel data={{ name: printPreviewItem.item_name, mfd: printPreviewItem.details?.mfgDate, exp: printPreviewItem.details?.expDate, prep: printPreviewItem.details?.preparedBy, tBox: printPreviewItem.details?.targetBoxName || '-', check: printPreviewItem.details?.checkedBy || '-', mac: printPreviewItem.details?.machine || '-', cyc: getCycleDisplayName(printPreviewItem.details?.cycle || '-'), id: printPreviewItem.unique_barcode }} previewMode={false} />
          </div>
        )}
        {selectedPrints.map(item => (
          <div key={item.id} id={`batch-print-node-${item.id}`} style={{ width: '200px', height: '400px', overflow: 'hidden' }}>
            <DentalLabel data={{ name: item.item_name, mfd: item.details?.mfgDate, exp: item.details?.expDate, prep: item.details?.preparedBy, tBox: item.details?.targetBoxName || '-', check: item.details?.checkedBy || '-', mac: item.details?.machine || '-', cyc: getCycleDisplayName(item.details?.cycle || '-'), id: item.unique_barcode }} previewMode={false} />
          </div>
        ))}
        {blankChunks.map((chunk, chunkIdx) => (
          <div key={chunkIdx} id={`blank-sheet-node-${chunkIdx}`} style={{ width: '200px', height: '400px', overflow: 'hidden' }}>
            <BlankLabelSheet items={chunk} />
          </div>
        ))}
      </div>

      {/* 🌟 Printing Modals */}
      {printPreviewItem && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
            <h3 style={{ color: '#2c3e50', marginBottom: '20px' }}>🔍 ตรวจสอบความถูกต้องก่อนพิมพ์</h3>
            <div style={{ backgroundColor: '#f0f0f0', padding: '40px', borderRadius: '8px', display: 'inline-block', border: '1px dashed #ccc' }}>
              <div style={{ width: '200px', height: '400px', overflow: 'hidden', position: 'relative', margin: '0 auto' }}>
                <DentalLabel data={{ name: printPreviewItem.item_name, mfd: printPreviewItem.details?.mfgDate, exp: printPreviewItem.details?.expDate, prep: printPreviewItem.details?.preparedBy, tBox: printPreviewItem.details?.targetBoxName || '-', check: printPreviewItem.details?.checkedBy || '-', mac: printPreviewItem.details?.machine || '-', cyc: getCycleDisplayName(printPreviewItem.details?.cycle || '-'), id: printPreviewItem.unique_barcode }} previewMode={true} />
              </div>
            </div>
            <div style={{ marginTop: '25px', display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button onClick={() => setPrintPreviewItem(null)} disabled={isPrinting} style={{ padding: '10px 20px', cursor: isPrinting ? 'wait' : 'pointer', fontSize: '16px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px' }}>ย้อนกลับ</button>
              <button onClick={handleConfirmPrint} disabled={isPrinting} style={{ padding: '10px 20px', cursor: isPrinting ? 'wait' : 'pointer', fontSize: '16px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold' }}>{isPrinting ? '⏳ กำลังส่งเข้าเครื่องพิมพ์...' : '🖨️ ยืนยันการพิมพ์'}</button>
            </div>
          </div>
        </div>
      )}

      {printedImagesPreview.length > 0 && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000, flexDirection: 'column' }}>
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.5)', maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ color: '#27ae60', marginTop: 0, marginBottom: '5px' }}>✅ ส่งเข้าเครื่องพิมพ์สำเร็จ</h2>
            <p style={{ color: '#666', fontSize: '0.95em', marginBottom: '20px' }}>ภาพฉลากทั้งหมดที่ถูกส่งไปยัง QZ Tray:</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', alignItems: 'center', marginBottom: '25px' }}>
              {printedImagesPreview.map((imgSrc, index) => (
                <div key={index} style={{ border: '2px dashed #ccc', padding: '5px', backgroundColor: '#f9f9f9' }}>
                  <img src={imgSrc} alt={`Printed Label ${index + 1}`} style={{ width: '200px', height: 'auto', border: '1px solid #ddd' }} />
                </div>
              ))}
            </div>
            <button onClick={() => setPrintedImagesPreview([])} style={{ padding: '12px 40px', fontSize: '16px', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>ตกลง / ปิดหน้าต่าง</button>
          </div>
        </div>
      )}

      {scanBlankItem && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2500 }}>
          <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.3)', maxWidth: '500px', width: '100%' }}>
            <h2 style={{ color: '#27ae60', marginTop: 0, marginBottom: '10px' }}>📷 สแกนฉลากเปล่าเพื่อผูกข้อมูล</h2>
            <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #ddd', textAlign: 'left', fontSize: '0.95em', color: '#444' }}>
              <div style={{ marginBottom: '8px' }}><strong>📦 วัสดุ:</strong> <span style={{ fontSize: '1.2em', color: '#2c3e50', fontWeight: 'bold' }}>{scanBlankItem.item_name}</span></div>
              {currentPage !== 'tool-assembly' && (
                <>
                  <div><strong>👤 ผู้เตรียม:</strong> {preparedBy} &nbsp;|&nbsp; <strong>ผู้ตรวจ:</strong> {checkedBy}</div>
                  <div><strong>⚙️ เครื่อง:</strong> {sterileMachine} &nbsp;|&nbsp; <strong>รอบ:</strong> {getCycleDisplayName(sterileCycle)}</div>
                </>
              )}
            </div>
            <div style={{ marginBottom: '15px', fontWeight: 'bold', color: '#d35400', fontSize: '1.1em' }}>
              จำนวนที่สแกนแล้ว: <span style={{ fontSize: '1.3em' }}>{scannedBlankList.length}</span> / <span style={{ color: '#e65100' }}>{scanBlankMaxAvail}</span> ชิ้น
            </div>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
              <input autoFocus type="text" value={blankBarcodeInput} onChange={(e) => {
                const val = e.target.value; setBlankBarcodeInput(val);
                if (val.length === 13 || val.length === 14) {
                  if (!validateUID(val)) { alert("❌ บาร์โค้ดไม่ถูกต้อง (Checksum ผิด)"); setBlankBarcodeInput(''); return; }
                  if (instruments.find(i => i.unique_barcode === val)) { alert("❌ บาร์โค้ดนี้ถูกผูกข้อมูลไปแล้ว!"); setBlankBarcodeInput(''); return; }
                  if (scannedBlankList.includes(val)) { alert("❌ คุณสแกนบาร์โค้ดนี้ไปแล้วในรอบนี้!"); setBlankBarcodeInput(''); return; }
                  if (scannedBlankList.length >= scanBlankMaxAvail) { alert("❌ จำนวนเกินกว่าที่สามารถจัดได้!"); setBlankBarcodeInput(''); return; }
                  setScannedBlankList(prev => [val, ...prev]); setBlankBarcodeInput('');
                }
              }} placeholder="สแกนที่นี่..." style={{ flex: 1, padding: '15px', fontSize: '1.5em', border: '3px solid #27ae60', borderRadius: '8px', textAlign: 'center' }} disabled={isBinding} />

              <button onClick={() => { setCameraTargetField('blank'); setIsCameraOpen(true); }} style={{ padding: '15px', background: '#2c3e50', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1.5em' }} title="เปิดกล้องมือถือ">
                📷
              </button>
            </div>

            {scannedBlankList.length > 0 && (
              <div style={{ maxHeight: '120px', overflowY: 'auto', background: '#ecf0f1', padding: '10px', borderRadius: '8px', marginBottom: '20px', fontSize: '0.9em', textAlign: 'left' }}>
                {scannedBlankList.map((code, idx) => (<div key={idx} style={{ padding: '3px 0', borderBottom: '1px dashed #ccc' }}>✅ รหัส: {code}</div>))}
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { setScanBlankItem(null); setBlankBarcodeInput(''); setScannedBlankList([]); }} disabled={isBinding} style={{ flex: 1, padding: '12px 20px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>ยกเลิก</button>
              <button onClick={handleConfirmBatchBind} disabled={isBinding || scannedBlankList.length === 0} style={{ flex: 2, padding: '12px 20px', backgroundColor: (isBinding || scannedBlankList.length === 0) ? '#ccc' : '#3498db', color: 'white', border: 'none', borderRadius: '5px', cursor: (isBinding || scannedBlankList.length === 0) ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}>{isBinding ? '⏳ กำลังบันทึก...' : `✅ ยืนยันผูกข้อมูล (${scannedBlankList.length} ชิ้น)`}</button>
            </div>
          </div>
        </div>
      )}

      {activeManagerField && managerDetails && (
        <div className="sterile-modal-overlay" onClick={() => { setActiveManagerField(null); setEditingOptionIndex(null); }} style={{ zIndex: 3000 }}>
          <div className="sterile-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px', background: 'linear-gradient(135deg, rgba(16, 20, 45, 0.98) 0%, rgba(30, 36, 70, 0.98) 100%)' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: '1.25em', fontWeight: 'bold' }}>⚙️ จัดการข้อมูล: {managerDetails.title}</h3>
              <button
                onClick={() => { setActiveManagerField(null); setEditingOptionIndex(null); }}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.75)',
                  fontSize: '1.1em',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            {/* Add new option input */}
            <div style={{ background: 'rgba(255, 255, 255, 0.02)', padding: '15px', borderRadius: '12px', marginBottom: '20px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9em', color: '#00e5ff', fontWeight: '600' }}>➕ เพิ่มรายการใหม่</h4>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  id="new-option-input"
                  placeholder={activeManagerField === 'cycle' && sterileMachine ? `ระบุ รอบ สำหรับ ${sterileMachine}...` : `ระบุ ${managerDetails.title}...`}
                  className="sterile-date-picker"
                  style={{ flex: 1, padding: '10px', fontSize: '0.95em' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      let val = e.target.value.trim();
                      if (!val) return;
                      if (activeManagerField === 'cycle' && sterileMachine) {
                        val = `${sterileMachine}:${val}`;
                      }
                      if (managerDetails.list.includes(val)) {
                        alert("มีข้อมูลนี้อยู่แล้ว");
                        return;
                      }
                      const updated = [...managerDetails.list, val];
                      managerDetails.onSave(updated, null, val);
                      e.target.value = '';
                    }
                  }}
                />
                <button
                  onClick={() => {
                    const input = document.getElementById('new-option-input');
                    let val = input ? input.value.trim() : '';
                    if (!val) return;
                    if (activeManagerField === 'cycle' && sterileMachine) {
                      val = `${sterileMachine}:${val}`;
                    }
                    if (managerDetails.list.includes(val)) {
                      alert("มีข้อมูลนี้อยู่แล้ว");
                      return;
                    }
                    const updated = [...managerDetails.list, val];
                    managerDetails.onSave(updated, null, val);
                    if (input) input.value = '';
                  }}
                  className="sterile-confirm-btn"
                  style={{ width: 'auto', padding: '10px 20px', fontSize: '0.9em', background: 'linear-gradient(135deg, #27ae60 0%, #1e8449 100%)', boxShadow: 'none' }}
                >
                  เพิ่ม
                </button>
              </div>
            </div>

            {/* Options list container */}
            <h4 style={{ margin: '0 0 8px 0', fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.6)', fontWeight: '600' }}>รายการปัจจุบัน</h4>
            <div style={{ maxHeight: '250px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', paddingRight: '4px' }}>
              {managerDetails.list.map((item, index) => {
                const isEditingThis = editingOptionIndex === index;
                return (
                  <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center', background: 'rgba(255,255,255,0.02)', padding: '8px 12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                    {isEditingThis ? (
                      <input
                        value={editingOptionValue}
                        onChange={(e) => setEditingOptionValue(e.target.value)}
                        className="sterile-date-picker"
                        style={{ flex: 1, padding: '6px 10px', fontSize: '0.95em', border: '1px solid #00bfa5' }}
                        autoFocus
                      />
                    ) : (
                      <span style={{ flex: 1, color: '#fff', fontSize: '1em', fontWeight: '500' }}>
                        {activeManagerField === 'cycle' && item.includes(':') ? (
                          <>
                            <span style={{ color: '#00e5ff', marginRight: '6px', fontSize: '0.85em', fontWeight: 'bold' }}>[{item.split(':')[0]}]</span>
                            {item.split(':').slice(1).join(':')}
                          </>
                        ) : item}
                      </span>
                    )}

                    {isEditingThis ? (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          onClick={() => {
                            let val = editingOptionValue.trim();
                            if (!val) return;
                            if (activeManagerField === 'cycle' && sterileMachine && !val.includes(':')) {
                              val = `${sterileMachine}:${val}`;
                            }
                            if (val !== item && managerDetails.list.includes(val)) {
                              alert("มีข้อมูลนี้อยู่แล้ว");
                              return;
                            }
                            const updated = [...managerDetails.list];
                            updated[index] = val;
                            managerDetails.onSave(updated, item, val);
                            setEditingOptionIndex(null);
                            setEditingOptionValue('');
                          }}
                          className="sterile-confirm-btn"
                          style={{ width: 'auto', padding: '6px 12px', fontSize: '0.82em', background: 'linear-gradient(135deg, #27ae60 0%, #1e8449 100%)', boxShadow: 'none' }}
                        >
                          💾 บันทึก
                        </button>
                        <button
                          onClick={() => {
                            setEditingOptionIndex(null);
                            setEditingOptionValue('');
                          }}
                          className="sterile-confirm-btn"
                          style={{ width: 'auto', padding: '6px 12px', fontSize: '0.82em', background: '#95a5a6', boxShadow: 'none' }}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          onClick={() => {
                            setEditingOptionIndex(index);
                            setEditingOptionValue(item);
                          }}
                          className="sterile-confirm-btn"
                          style={{ width: 'auto', padding: '6px 12px', fontSize: '0.82em', background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)', boxShadow: 'none' }}
                        >
                          ✏️ แก้ไข
                        </button>
                        <button
                          onClick={() => {
                            setDeleteConfirmTarget({ item, index, managerDetails });
                          }}
                          className="sterile-confirm-btn"
                          style={{ width: 'auto', padding: '6px 12px', fontSize: '0.82em', background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)', boxShadow: 'none' }}
                        >
                          🗑️ ลบ
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {managerDetails.list.length === 0 && (
                <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', padding: '20px', fontSize: '0.9em' }}>ไม่มีข้อมูลในรายการ</div>
              )}
            </div>

          </div>
        </div>
      )}

      {deleteConfirmTarget && (
        <div className="sterile-modal-overlay" style={{ zIndex: 3100 }} onClick={() => setDeleteConfirmTarget(null)}>
          <div className="sterile-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px', background: 'linear-gradient(135deg, rgba(16, 20, 45, 0.98) 0%, rgba(30, 36, 70, 0.98) 100%)', border: '1px solid rgba(255, 77, 77, 0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '10px', marginBottom: '20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '15px' }}>
              <span style={{ fontSize: '1.5em' }}>⚠️</span>
              <h3 style={{ margin: 0, color: '#ff4d4d', fontSize: '1.2em', fontWeight: 'bold' }}>ยืนยันการลบข้อมูล</h3>
            </div>

            <p style={{ color: '#fff', fontSize: '1em', lineHeight: '1.5em', textAlign: 'left', margin: '0 0 25px 0' }}>
              คุณต้องการลบรายการ <strong style={{ color: '#ff9494' }}>"{deleteConfirmTarget.item}"</strong> ใช่หรือไม่? <br />
              <span style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.5)' }}>* การดำเนินการนี้จะลบรายการออกจากตัวเลือกในระบบ</span>
            </p>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteConfirmTarget(null)}
                className="sterile-confirm-btn"
                style={{ width: 'auto', padding: '10px 20px', fontSize: '0.9em', background: 'rgba(255,255,255,0.08)', color: '#fff', boxShadow: 'none' }}
              >
                ยกเลิก
              </button>
              <button
                onClick={() => {
                  const { index, item, managerDetails } = deleteConfirmTarget;
                  const updated = managerDetails.list.filter((_, idx) => idx !== index);
                  managerDetails.onSave(updated, item, null);
                  setDeleteConfirmTarget(null);
                }}
                className="sterile-confirm-btn"
                style={{ width: 'auto', padding: '10px 20px', fontSize: '0.9em', background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)', boxShadow: 'none' }}
              >
                ยืนยันการลบ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== 📱 MOBILE HEADER ========== */}
      {isMobile ? (
        <div className="mobile-header">
          <div className="header-left">
            <button onClick={() => setCurrentPage('home')} className="icon-btn" title="หน้าหลัก">🏠</button>
            <span style={{ fontSize: '0.85em', fontWeight: '600' }}>CSSD</span>
          </div>
          <div className="header-actions">
            {!isGuestMode && (
              <button
                onClick={() => setShowUserProfileModal(true)}
                className="icon-btn"
                title="จัดการโปรไฟล์"
                style={{
                  padding: '4px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%'
                }}
              >
                {currentStaffProfile?.avatar_url || currentStaffProfile?.line_picture_url ? (
                  <img
                    src={currentStaffProfile.avatar_url || currentStaffProfile.line_picture_url}
                    alt="Profile"
                    style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  '👤'
                )}
              </button>
            )}
            <button onClick={fetchData} className="icon-btn" title="ซิงค์ข้อมูล">🔄</button>
            <button onClick={onLogout} className="icon-btn" title="ออกจากระบบ">🚪</button>
            <button onClick={() => setMobileMenuOpen(true)} className="hamburger-btn" title="เมนู">☰</button>
          </div>
        </div>
      ) : (
        /* ========== 🖥️ DESKTOP HEADER ========== */
        <>
          <div style={{ background: '#1abc9c', color: '#fff', padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {!isGuestMode ? (
              <button
                type="button"
                data-testid="desktop-user-profile-pill"
                onClick={() => setShowUserProfileModal(true)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  borderRadius: '20px',
                  padding: '4px 14px 4px 6px',
                  color: '#ffffff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  fontSize: '0.92em'
                }}
                title="คลิกเพื่อจัดการโปรไฟล์ เปลี่ยนรหัสผ่าน และเชื่อม LINE"
              >
                {currentStaffProfile?.avatar_url || currentStaffProfile?.line_picture_url ? (
                  <img
                    src={currentStaffProfile.avatar_url || currentStaffProfile.line_picture_url}
                    alt="Profile"
                    style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #fff' }}
                  />
                ) : (
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#ffffff', color: '#1abc9c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '13px' }}>
                    👤
                  </div>
                )}
                <span>
                  <strong>{currentStaffProfile?.name || currentStaffProfile?.username || (user?.email ? user.email.split('@')[0] : 'ผู้ใช้งาน')}</strong>
                </span>
                <span style={{ fontSize: '0.8em', opacity: 0.9 }}>⚙️</span>
              </button>
            ) : (
              <span>👤 ชื่อผู้ใช้ : <strong>Guest</strong></span>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              {!isGuestMode && (
                <button
                  type="button"
                  data-testid="desktop-open-profile-modal-btn"
                  onClick={() => setShowUserProfileModal(true)}
                  style={{ background: '#3498db', color: '#fff', border: 'none', borderRadius: '5px', padding: '6px 15px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                  ⚙️ จัดการโปรไฟล์ & รหัสผ่าน
                </button>
              )}
              <button onClick={fetchData} style={{ background: '#f39c12', color: '#fff', border: 'none', borderRadius: '5px', padding: '6px 15px', cursor: 'pointer', fontWeight: 'bold' }}>🔄 ซิงค์ข้อมูลล่าสุด</button>
              <button onClick={onLogout} style={{ background: '#c0392b', color: '#fff', border: 'none', borderRadius: '5px', padding: '6px 15px', cursor: 'pointer', fontWeight: 'bold' }}>{isGuestMode ? '🚪 ออกจากโหมดเกสท์' : '🚪 ออกจากระบบ'}</button>
            </div>
          </div>

          <nav style={{ background: '#2c3e50', padding: '10px', display: 'flex', justifyContent: 'center', gap: '12px', position: 'sticky', top: 0, zIndex: 1000, alignItems: 'center' }}>
            {/* 🏠 ปุ่มหน้าหลัก */}
            {!isGuestMode && (
              <button
                onClick={() => { setCurrentPage('home'); setNavDropdownOpen(false); }}
                style={{
                  padding: '8px 16px',
                  background: currentPage === 'home' ? '#1abc9c' : '#546e7a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                  fontSize: '0.92em',
                  boxShadow: currentPage === 'home' ? '0 0 10px rgba(26, 188, 156, 0.4)' : 'none'
                }}
              >
                🏠 หน้าหลัก
              </button>
            )}

            {/* 📊 ปุ่มแดชบอร์ดวงจรวัสดุ */}
            {hasPageAccess('durable-dashboard') && (
              <button
                onClick={() => { setCurrentPage('durable-dashboard'); setNavDropdownOpen(false); }}
                style={{
                  padding: '8px 16px',
                  background: currentPage === 'durable-dashboard' ? '#1abc9c' : '#546e7a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                  fontSize: '0.92em',
                  boxShadow: currentPage === 'durable-dashboard' ? '0 0 10px rgba(26, 188, 156, 0.4)' : 'none'
                }}
              >
                📊 แดชบอร์ดวงจรวัสดุ
              </button>
            )}

            {/* 📊 ปุ่มแดชบอร์ดสิ้นเปลือง 4 คลัง */}
            {hasPageAccess('consumable-dashboard') && (
              <button
                onClick={() => { setCurrentPage('consumable-dashboard'); setNavDropdownOpen(false); }}
                style={{
                  padding: '8px 16px',
                  background: currentPage === 'consumable-dashboard' ? '#0ea5e9' : '#546e7a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                  fontSize: '0.92em',
                  boxShadow: currentPage === 'consumable-dashboard' ? '0 0 10px rgba(14, 165, 233, 0.4)' : 'none'
                }}
              >
                📊 แดชบอร์ดสิ้นเปลือง
              </button>
            )}

            {/* 📄 ปุ่มรายงานเบิกจ่าย */}
            {hasPageAccess('dispense-report') && (
              <button
                onClick={() => { setCurrentPage('dispense-report'); setNavDropdownOpen(false); }}
                style={{
                  padding: '8px 16px',
                  background: currentPage === 'dispense-report' ? '#059669' : '#546e7a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                  fontSize: '0.92em',
                  boxShadow: currentPage === 'dispense-report' ? '0 0 10px rgba(5, 150, 105, 0.4)' : 'none'
                }}
              >
                📄 รายงานเบิกจ่าย
              </button>
            )}

            {/* 📥 ปุ่มรับเข้าสินค้าตามใบเสร็จ */}
            {hasPageAccess('receipt-stockin') && (
              <button
                onClick={() => { setCurrentPage('receipt-stockin'); setNavDropdownOpen(false); }}
                style={{
                  padding: '8px 16px',
                  background: currentPage === 'receipt-stockin' ? '#0ba360' : '#546e7a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                  fontSize: '0.92em',
                  boxShadow: currentPage === 'receipt-stockin' ? '0 0 10px rgba(11, 163, 96, 0.4)' : 'none'
                }}
              >
                📥 รับเข้าสินค้า/ใบเสร็จ
              </button>
            )}

            {/* 📋 ปุ่มสรุปตรวจรับ & QR */}
            {hasPageAccess('receipt-inspection') && (
              <button
                onClick={() => { setCurrentPage('receipt-inspection'); setNavDropdownOpen(false); }}
                style={{
                  padding: '8px 16px',
                  background: currentPage === 'receipt-inspection' ? '#0284c7' : '#546e7a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                  fontSize: '0.92em',
                  boxShadow: currentPage === 'receipt-inspection' ? '0 0 10px rgba(2, 132, 199, 0.4)' : 'none'
                }}
              >
                📋 สรุปตรวจรับ & QR
              </button>
            )}

            {/* 🗄️ ปุ่มคลังรูป & ซิงค์ R2 */}
            {hasPageAccess('storage-admin') && (
              <button
                onClick={() => { setCurrentPage('storage-admin'); setNavDropdownOpen(false); }}
                style={{
                  padding: '8px 16px',
                  background: currentPage === 'storage-admin' ? '#0284c7' : '#546e7a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                  fontSize: '0.92em',
                  boxShadow: currentPage === 'storage-admin' ? '0 0 10px rgba(2, 132, 199, 0.4)' : 'none'
                }}
              >
                🗄️ คลังรูป & ซิงค์ R2
              </button>
            )}

            {/* 🛡️ ปุ่มรายงานความเสี่ยงทันตกรรม */}
            {!isGuestMode && (
              <button
                onClick={() => {
                  handleRiskNavigation();
                  setNavDropdownOpen(false);
                }}
                style={{
                  padding: '8px 16px',
                  background: (currentPage === 'dental-risk' || currentPage === 'dental-risk-portal') ? '#ef4444' : '#546e7a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                  fontSize: '0.92em',
                  boxShadow: (currentPage === 'dental-risk' || currentPage === 'dental-risk-portal') ? '0 0 10px rgba(239, 68, 68, 0.4)' : 'none'
                }}
              >
                🛡️ รายงานความเสี่ยง
              </button>
            )}

            {/* 📂 Dropdown เลือกห้อง */}
            {(() => {
              const allNavRooms = [
                // Category 1: CSSD Operations
                { id: 'washing', name: '🧽 ห้องล้างเครื่องมือ', category: 'cssd' },
                { id: 'tool-assembly', name: '🔧 ห้องจัดเครื่องมือ', category: 'cssd' },
                { id: 'packaging', name: '🏷️ ห้องตรวจสอบเครื่องมือก่อนฆ่าเชื้อ', category: 'cssd' },
                { id: 'supply', name: '🏥 ห้อง Sterile', category: 'cssd' },
                { id: 'dispense', name: '📤 ห้องจ่ายเครื่องมือ', category: 'cssd' },

                // Category 2: Inventory Management
                { id: 'consumable-dashboard', name: '📊 แดชบอร์ดสิ้นเปลือง 4 คลัง', category: 'inventory' },
                { id: 'sub-warehouse-admin', name: '🏬 จัดการคลังย่อย (ผู้ดูแลคลัง)', category: 'inventory' },
                { id: 'dispense-report', name: '📄 รายงานการเบิกจ่ายประจำเดือน', category: 'inventory' },
                { id: 'consumable', name: '📦 วัสดุสิ้นเปลือง', category: 'inventory' },
                { id: 'durable', name: '🏢 วัสดุคงทน', category: 'inventory' },
                { id: 'handpiece', name: '🦷 หัวกรอทันตกรรม', category: 'inventory' },
                { id: 'receipt-stockin', name: '📥 รับเข้าสินค้า/ใบเสร็จ', category: 'inventory' },
                { id: 'receipt-inspection', name: '📋 สรุปตรวจรับ & QR', category: 'inventory' },

                // Category 3: General Systems
                { id: 'clinic-order', name: '🛒 สั่งเครื่องมือและวัสดุ', category: 'general' },
                { id: 'barcode-tracking', name: '🔍 ค้นหาบาร์โค้ด', category: 'general' },
                { id: 'trace', name: '📍 ประวัติ QR Code', category: 'general' },
                { id: 'sterile-locations', name: '📍 ตำแหน่งสิ่งของในห้อง sterile', category: 'general' },
                { id: 'storage-admin', name: '🗄️ คลังรูป & ซิงค์ R2', category: 'general' },
                { id: 'recall', name: '⚠️ เรียกคืนวัสดุ (Recall)', category: 'general' },
                { id: 'dental-risk', name: '🛡️ รายงานความเสี่ยงทันตกรรม', category: 'general' },

                // Category 4: Admin Settings
                { id: 'manage-users', name: '👤 จัดการผู้ใช้งาน', category: 'admin' },
                { id: 'register', name: '📝 ลงทะเบียนวัสดุใหม่', category: 'admin' },
                { id: 'set-config', name: '⚙️ ตั้งค่าชุดเครื่องมือ', category: 'admin' },
                { id: 'doctor-box', name: '🧰 กล่องจัดชุดทันตแพทย์', category: 'admin' },
                { id: 'qr-order-generator', name: '🖼️ สร้าง QR Code สั่งพัสดุรายห้อง', category: 'admin' }
              ];

              const navCategories = [
                { id: 'cssd', name: '🔧 งานห้องปฏิบัติการ CSSD' },
                { id: 'inventory', name: '📦 การจัดการคลังสต็อก' },
                { id: 'general', name: '🖥️ ระบบทั่วไป' },
                { id: 'admin', name: '👑 การดูแลระบบ (Admin)' }
              ];

              const activeRoom = allNavRooms.find(r => r.id === currentPage || (r.id === 'dental-risk' && currentPage === 'dental-risk-portal'));
              const triggerLabel = activeRoom ? activeRoom.name : 'เลือกห้องปฏิบัติงาน...';

              return (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  {isGuestMode ? (
                    <button
                      disabled={true}
                      style={{
                        padding: '8px 16px',
                        background: 'rgba(255, 255, 255, 0.08)',
                        color: '#cbd5e1',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: '5px',
                        cursor: 'not-allowed',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        minWidth: '240px',
                        justifyContent: 'space-between',
                        fontSize: '0.92em'
                      }}
                    >
                      <span>🔒 {guestRoom.trim()} (Guest Mode)</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => setNavDropdownOpen(!navDropdownOpen)}
                      style={{
                        padding: '8px 16px',
                        background: currentPage !== 'home' ? '#1abc9c' : '#34495e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        minWidth: '240px',
                        justifyContent: 'space-between',
                        transition: 'all 0.2s',
                        fontSize: '0.92em',
                        boxShadow: currentPage !== 'home' ? '0 0 10px rgba(26, 188, 156, 0.4)' : 'none'
                      }}
                    >
                      <span>📂 {triggerLabel}</span>
                      <span style={{ fontSize: '0.8em' }}>{navDropdownOpen ? '▲' : '▼'}</span>
                    </button>
                  )}

                  {navDropdownOpen && (
                    <>
                      {/* Click outside overlay */}
                      <div
                        onClick={() => setNavDropdownOpen(false)}
                        style={{
                          position: 'fixed',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          zIndex: 998,
                        }}
                      />

                      {/* Dropdown Menu */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          marginTop: '5px',
                          background: '#ffffff',
                          borderRadius: '12px',
                          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                          border: '1px solid #cbd5e1',
                          width: '300px',
                          maxHeight: '450px',
                          overflowY: 'auto',
                          zIndex: 999,
                          padding: '10px 0',
                          textAlign: 'left',
                        }}
                      >
                        {navCategories
                          .map(cat => {
                            const rooms = allNavRooms
                              .filter(r => r.category === cat.id)
                              .filter(room => {
                                if (room.id === 'dental-risk') {
                                  return !isGuestMode;
                                }
                                if (room.category === 'admin') {
                                  return userRole === 'admin' || isAdmin;
                                }
                                return hasPageAccess ? hasPageAccess(room.id) : true;
                              });
                            return { ...cat, rooms };
                          })
                          .filter(cat => cat.rooms.length > 0)
                          .map((cat, catIdx) => (
                            <div key={cat.id}>
                              {catIdx > 0 && <div style={{ height: '1px', background: '#e2e8f0', margin: '8px 0' }} />}
                              <div
                                style={{
                                  padding: '6px 16px',
                                  fontSize: '0.78em',
                                  fontWeight: 'bold',
                                  color: '#64748b',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                }}
                              >
                                {cat.name}
                              </div>
                              {cat.rooms.map(room => {
                                const isCurrent = currentPage === room.id || (room.id === 'dental-risk' && currentPage === 'dental-risk-portal');
                                return (
                                  <button
                                    key={room.id}
                                    onClick={() => {
                                      if (room.id === 'dental-risk') {
                                        handleRiskNavigation();
                                        setNavDropdownOpen(false);
                                        return;
                                      }
                                      setCurrentPage(room.id);
                                      resetForm();

                                      // Reset page specific states
                                      if (room.id === 'washing') {
                                        setBarcodeInput('');
                                        setWashingSuccessList([]);
                                        setWashingErrorList([]);
                                      } else if (room.id === 'supply') {
                                        setActionType('increase');
                                        setBarcodeInput('');
                                        setSterileSuccessList([]);
                                        setTargetBox(null);
                                      } else if (room.id === 'dispense') {
                                        setActionType('decrease');
                                        setBarcodeInput('');
                                        setSterileSuccessList([]);
                                        setSelectedOrder(null);
                                        setTargetWarehouse('');
                                      } else if (room.id === 'trace') {
                                        setTraceInput('');
                                        setTraceData(null);
                                        setTraceLogs([]);
                                        setTraceError('');
                                      } else if (room.id === 'qr-order-generator') {
                                        setSelectedQrRoom('');
                                      }

                                      setNavDropdownOpen(false);
                                    }}
                                    style={{
                                      width: '100%',
                                      padding: '10px 16px',
                                      background: isCurrent ? '#eff6ff' : 'transparent',
                                      color: isCurrent ? '#2563eb' : '#334155',
                                      border: 'none',
                                      textAlign: 'left',
                                      cursor: 'pointer',
                                      fontSize: '0.92em',
                                      fontWeight: isCurrent ? 'bold' : 'normal',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      transition: 'all 0.15s',
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isCurrent) {
                                        e.target.style.background = '#f8fafc';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!isCurrent) {
                                        e.target.style.background = 'transparent';
                                      }
                                    }}
                                  >
                                    {room.name}
                                  </button>
                                );
                              })}
                            </div>
                          ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </nav>
        </>
      )}

      {/* ========== 📱 MOBILE NAV DRAWER ========== */}
      {isMobile && (
        <>
          <div className={`mobile-nav-overlay ${mobileMenuOpen ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)} />
          <div className={`mobile-nav-drawer ${mobileMenuOpen ? 'active' : ''}`}>
            <div className="mobile-nav-close">
              <button onClick={() => setMobileMenuOpen(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1.5em', cursor: 'pointer', padding: '5px' }}>✕</button>
            </div>

            <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div
                onClick={() => {
                  if (!isGuestMode) {
                    setShowUserProfileModal(true);
                    setMobileMenuOpen(false);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: !isGuestMode ? 'pointer' : 'default',
                  background: !isGuestMode ? 'rgba(255,255,255,0.06)' : 'transparent',
                  padding: '8px 10px',
                  borderRadius: '10px'
                }}
                title={!isGuestMode ? 'แตะเพื่อจัดการโปรไฟล์' : ''}
              >
                {currentStaffProfile?.avatar_url || currentStaffProfile?.line_picture_url ? (
                  <img
                    src={currentStaffProfile.avatar_url || currentStaffProfile.line_picture_url}
                    alt="Profile"
                    style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #1abc9c' }}
                  />
                ) : (
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                    👤
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.75em', color: 'rgba(255,255,255,0.45)' }}>ผู้ใช้งาน {!isGuestMode && '(แตะเพื่อตั้งค่า ⚙️)'}</div>
                  <div style={{ fontSize: '0.88em', color: '#1abc9c', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {isGuestMode ? 'Guest' : (currentStaffProfile?.name || currentStaffProfile?.username || (user?.email ? user.email.split('@')[0] : ''))}
                  </div>
                </div>
              </div>
              {!isGuestMode && (
                <button
                  type="button"
                  data-testid="mobile-open-profile-modal-btn"
                  onClick={() => { setShowUserProfileModal(true); setMobileMenuOpen(false); }}
                  style={{
                    background: '#3498db',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.82em',
                    width: '100%',
                    textAlign: 'center',
                    minHeight: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  ⚙️ จัดการโปรไฟล์ & รหัสผ่าน
                </button>
              )}
            </div>

            {/* 🏠 หน้าหลัก */}
            {!isGuestMode && (
              <button className={currentPage === 'home' ? 'active-page' : ''} onClick={() => { setCurrentPage('home'); setMobileMenuOpen(false); }}>🏠 หน้าหลัก</button>
            )}

            {/* 📊 แดชบอร์ดวงจรวัสดุ */}
            {hasPageAccess('durable-dashboard') && (
              <button className={currentPage === 'durable-dashboard' ? 'active-page' : ''} onClick={() => { setCurrentPage('durable-dashboard'); setMobileMenuOpen(false); }}>📊 แดชบอร์ดวงจรวัสดุ</button>
            )}

            {/* 🔴 Admin only */}
            {isAdmin && (
              <>
                <div className="nav-section-title">จัดการระบบ (Admin)</div>
                <button className={currentPage === 'manage-users' ? 'active-page' : ''} onClick={() => { setCurrentPage('manage-users'); resetForm(); setMobileMenuOpen(false); }}>👤 จัดการผู้ใช้งาน</button>
                <button className={currentPage === 'register' ? 'active-page' : ''} onClick={() => { setCurrentPage('register'); resetForm(); setMobileMenuOpen(false); }}>📝 ลงทะเบียนวัสดุใหม่</button>
                <button className={currentPage === 'set-config' ? 'active-page' : ''} onClick={() => { setCurrentPage('set-config'); resetForm(); setMobileMenuOpen(false); }}>⚙️ ตั้งค่าชุด</button>
                <button className={currentPage === 'doctor-box' ? 'active-page' : ''} onClick={() => { setCurrentPage('doctor-box'); resetForm(); setMobileMenuOpen(false); }}>🧰 กล่องหมอ</button>
              </>
            )}

            {(hasPageAccess('consumable-dashboard') || hasPageAccess('dispense-report') || hasPageAccess('receipt-stockin') || hasPageAccess('receipt-inspection') || hasPageAccess('consumable') || hasPageAccess('durable') || hasPageAccess('handpiece') || hasPageAccess('packaging') || hasPageAccess('washing') || hasPageAccess('tool-assembly') || hasPageAccess('supply') || hasPageAccess('dispense') || hasPageAccess('recall')) && (
              <>
                <div className="nav-section-title">การทำงาน CSSD / คลัง</div>
                {hasPageAccess('consumable-dashboard') && <button className={currentPage === 'consumable-dashboard' ? 'active-page' : ''} onClick={() => { setCurrentPage('consumable-dashboard'); setMobileMenuOpen(false); }}>📊 แดชบอร์ดสิ้นเปลือง</button>}
                {hasPageAccess('sub-warehouse-admin') && <button className={currentPage === 'sub-warehouse-admin' ? 'active-page' : ''} onClick={() => { setCurrentPage('sub-warehouse-admin'); setMobileMenuOpen(false); }}>🏬 จัดการคลังย่อย</button>}
                {hasPageAccess('dispense-report') && <button className={currentPage === 'dispense-report' ? 'active-page' : ''} onClick={() => { setCurrentPage('dispense-report'); setMobileMenuOpen(false); }}>📄 รายงานเบิกจ่าย</button>}
                {hasPageAccess('receipt-stockin') && <button className={currentPage === 'receipt-stockin' ? 'active-page' : ''} onClick={() => { setCurrentPage('receipt-stockin'); setMobileMenuOpen(false); }}>📥 รับเข้าสินค้า/ใบเสร็จ</button>}
                {hasPageAccess('receipt-inspection') && <button className={currentPage === 'receipt-inspection' ? 'active-page' : ''} onClick={() => { setCurrentPage('receipt-inspection'); setMobileMenuOpen(false); }}>📋 สรุปตรวจรับ & QR</button>}
                {hasPageAccess('consumable') && <button className={currentPage === 'consumable' ? 'active-page' : ''} onClick={() => { setCurrentPage('consumable'); setMobileMenuOpen(false); }}>📦 สิ้นเปลือง</button>}
                {hasPageAccess('durable') && <button className={currentPage === 'durable' ? 'active-page' : ''} onClick={() => { setCurrentPage('durable'); setMobileMenuOpen(false); }}>🏢 คงทน</button>}
                {hasPageAccess('handpiece') && <button className={currentPage === 'handpiece' ? 'active-page' : ''} onClick={() => { setCurrentPage('handpiece'); setMobileMenuOpen(false); }}>🦷 หัวกรอ</button>}
                {hasPageAccess('packaging') && <button className={currentPage === 'packaging' ? 'active-page' : ''} onClick={() => { setCurrentPage('packaging'); setMobileMenuOpen(false); }}>🏷️ แพ็กซอง</button>}
                {hasPageAccess('washing') && <button className={currentPage === 'washing' ? 'active-page' : ''} onClick={() => { setCurrentPage('washing'); setBarcodeInput(''); setWashingSuccessList([]); setWashingErrorList([]); setMobileMenuOpen(false); }}>🧽 ห้องล้าง</button>}
                {hasPageAccess('tool-assembly') && <button className={currentPage === 'tool-assembly' ? 'active-page' : ''} onClick={() => { setCurrentPage('tool-assembly'); setMobileMenuOpen(false); }}>🔧 ห้องจัดเครื่องมือ</button>}
                {hasPageAccess('supply') && <button className={currentPage === 'supply' ? 'active-page' : ''} onClick={() => { setCurrentPage('supply'); setActionType('increase'); setBarcodeInput(''); setSterileSuccessList([]); setTargetBox(null); setMobileMenuOpen(false); }}>🏥 Sterile</button>}
                {hasPageAccess('dispense') && <button className={currentPage === 'dispense' ? 'active-page' : ''} onClick={() => { setCurrentPage('dispense'); setActionType('decrease'); setBarcodeInput(''); setSterileSuccessList([]); setSelectedOrder(null); setTargetWarehouse(''); setMobileMenuOpen(false); }}>📤 จ่ายตาม Order</button>}
                {hasPageAccess('recall') && <button className={currentPage === 'recall' ? 'active-page' : ''} onClick={() => { setCurrentPage('recall'); setMobileMenuOpen(false); }}>⚠️ เรียกคืนวัสดุ (Recall)</button>}
              </>
            )}

            {/* 🟢 ทุกคน */}
            {(hasPageAccess('clinic-order') || hasPageAccess('barcode-tracking') || hasPageAccess('trace') || hasPageAccess('sterile-locations') || hasPageAccess('storage-admin') || !isGuestMode) && (
              <>
                <div className="nav-section-title">ทั่วไป</div>
                {hasPageAccess('clinic-order') && <button className={currentPage === 'clinic-order' ? 'active-page' : ''} onClick={() => { setCurrentPage('clinic-order'); setMobileMenuOpen(false); }}>🛒 สั่งของ</button>}
                {hasPageAccess('barcode-tracking') && <button className={currentPage === 'barcode-tracking' ? 'active-page' : ''} onClick={() => { setCurrentPage('barcode-tracking'); setMobileMenuOpen(false); }}>🔍 บาร์โค้ดในระบบ</button>}
                {hasPageAccess('trace') && <button className={currentPage === 'trace' ? 'active-page' : ''} onClick={() => { setCurrentPage('trace'); setTraceInput(''); setTraceData(null); setTraceLogs([]); setTraceError(''); setMobileMenuOpen(false); }}>📍 ข้อมูล QR Code</button>}
                {hasPageAccess('sterile-locations') && <button className={currentPage === 'sterile-locations' ? 'active-page' : ''} onClick={() => { setCurrentPage('sterile-locations'); setMobileMenuOpen(false); }}>📍 ตำแหน่งสิ่งของในห้อง sterile</button>}
                {hasPageAccess('storage-admin') && <button className={currentPage === 'storage-admin' ? 'active-page' : ''} onClick={() => { setCurrentPage('storage-admin'); setMobileMenuOpen(false); }}>🗄️ คลังรูป & ซิงค์ R2</button>}
                {!isGuestMode && (
                  <button
                    className={(currentPage === 'dental-risk' || currentPage === 'dental-risk-portal') ? 'active-page' : ''}
                    onClick={() => {
                      handleRiskNavigation();
                      setMobileMenuOpen(false);
                    }}
                  >
                    🛡️ รายงานความเสี่ยง
                  </button>
                )}
              </>
            )}

            <div style={{ padding: '20px', borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: '10px' }}>
              <button onClick={() => { onLogout(); setMobileMenuOpen(false); }} style={{ width: '100%', padding: '12px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.95em', fontWeight: 'bold', cursor: 'pointer' }}>{isGuestMode ? '🚪 ออกจากโหมดเกสท์' : '🚪 ออกจากระบบ'}</button>
            </div>
          </div>
        </>
      )}

      {/* 🌟 PAGE: หน้าหลัก (Home Hub) */}
      {currentPage === 'home' && (
        <HomePage
          user={user}
          userRole={userRole}
          onSelectStation={(page) => {
            if (page === 'dental-risk') {
              handleRiskNavigation();
            } else {
              setCurrentPage(page);
            }
          }}
          hasPageAccess={hasPageAccess}
          isAdmin={isAdmin}
          isGuestMode={isGuestMode}
        />
      )}

      <div className="content-container" style={{ maxWidth: '1200px', margin: isMobile ? '10px auto' : '20px auto', padding: isMobile ? '0 8px' : '0 15px' }}>

        {/* 📊 PAGE: แดชบอร์ดวงจรวัสดุคงทน */}
        {currentPage === 'durable-dashboard' && (
          <DurableDashboard
            instruments={instruments}
            supplies={supplies}
            itemMaster={itemMaster}
            logs={logs}
          />
        )}

        {/* 📍 PAGE: ตำแหน่งสิ่งของในห้อง sterile */}
        {currentPage === 'sterile-locations' && (
          <SterileLocations
            itemMaster={itemMaster}
            supplies={supplies}
            instruments={instruments}
            onUpdateData={fetchData}
          />
        )}


        {/* 🌟 PAGE: ลงทะเบียนวัสดุรวม (Master Register) */}
        {currentPage === 'register' && (
          <RegisterItemPage
            isEditing={isEditing}
            handleRegisterSubmit={handleRegisterSubmit}
            regType={regType}
            setRegType={setRegType}
            regName={regName}
            handleNameChange={handleNameChange}
            isNameDuplicate={isNameDuplicate}
            nameSuggestions={nameSuggestions}
            regAliases={regAliases}
            setRegAliases={setRegAliases}
            durableType={setCategory}
            setDurableType={setSetCategory}
            cabinet={cabinet}
            setCabinet={setCabinet}
            shelf={shelf}
            setShelf={setShelf}
            row={row}
            setRow={setRow}
            regUnit={unit}
            setRegUnit={setUnit}
            regUnitSize={unitSize}
            setRegUnitSize={setUnitSize}
            lotNumber={lotNumber}
            setLotNumber={setLotNumber}
            expDate={expDate}
            setExpDate={setExpDate}
            initAmount={initialQty}
            setInitAmount={setInitialQty}
            minStock={minStock}
            setMinStock={setMinStock}
            globalMinStock={globalMinStock}
            setGlobalMinStock={setGlobalMinStock}
            pricePerUnit={pricePerUnit}
            setPricePerUnit={setPricePerUnit}
            barcode={barcode}
            setBarcode={setBarcode}
            company={company}
            setCompany={setCompany}
            receiveDate={receiveDate}
            setReceiveDate={setReceiveDate}
            deliveryNote={deliveryNote}
            setDeliveryNote={setDeliveryNote}
            poNumber={poNumber}
            setPoNumber={setPoNumber}
            orderDate={orderDate}
            setOrderDate={setOrderDate}
            approvalDate={approvalDate}
            setApprovalDate={setApprovalDate}
            imageUrl={imageUrl}
            setImageUrl={setImageUrl}
            zoomImageUrl={zoomImageUrl}
            setZoomImageUrl={setZoomImageUrl}
            otherImageUrl={otherImageUrl}
            setOtherImageUrl={setOtherImageUrl}
            documentImageUrl={documentImageUrl}
            setDocumentImageUrl={setDocumentImageUrl}
            isUploading={isUploading}
            isUploadingZoom={isUploadingZoom}
            isUploadingOther={isUploadingOther}
            isUploadingDocument={isUploadingDocument}
            handleImageUpload={handleImageUpload}
            setExpandedImage={setExpandedImage}
            resetForm={resetForm}
            editId={editId}
            itemMaster={itemMaster}
            supplies={supplies}
          />
        )}

        {/* PAGE: QR Order Generator */}
        {currentPage === 'qr-order-generator' && (
          <QROrderGeneratorPage
            selectedQrRoom={selectedQrRoom}
            setSelectedQrRoom={setSelectedQrRoom}
            WAREHOUSES_LIST={WAREHOUSES_LIST}
          />
        )}

        {/* 📥 PAGE: รับเข้าสินค้าตามใบเสร็จ (Stock-In Receipt) */}
        {currentPage === 'receipt-stockin' && (
          <StockInReceiptPage
            supabase={supabase}
            itemMaster={itemMaster}
            supplies={supplies}
            WAREHOUSES_LIST={WAREHOUSES_LIST}
            setSuccessModal={setSuccessModal}
            fetchData={fetchData}
            user={user}
            isMobile={isMobile}
            onNavigateToInspection={navigateToReceiptInspection}
          />
        )}

        {/* 📋 PAGE: สรุปตรวจรับ & QR (Receipt Inspection & Consumable QR) */}
        {currentPage === 'receipt-inspection' && (
          <ReceiptInspectionPage
            inspectionReceiptId={inspectionReceiptId}
            setInspectionReceiptId={setInspectionReceiptId}
            supabase={supabase}
            user={user}
            isMobile={isMobile}
            onNavigate={setCurrentPage}
          />
        )}

        {/* 🗄️ PAGE: ศูนย์จัดการคลังรูปภาพ & ซิงค์ R2 (Storage Admin & Migration Center) */}
        {currentPage === 'storage-admin' && (
          <StorageAdminPage
            supabase={supabase}
            setSuccessModal={setSuccessModal}
            user={user}
            isMobile={isMobile}
            fetchData={fetchData}
          />
        )}

        {/* 🛡️ PAGE: รายงานความเสี่ยงทันตกรรม (Dental Risk Management - Admin Only in CSSD) */}
        {currentPage === 'dental-risk' && isAdmin && (
          <DentalRiskReportPage
            user={user}
            userRole={userRole}
            isMobile={isMobile}
            onNavigate={(page, params) => {
              if (page === 'recall') {
                if (params?.barcode && typeof setFilterRecallBarcode === 'function') {
                  setFilterRecallBarcode(params.barcode);
                }
                if (params?.cycle && typeof setFilterRecallCycle === 'function') {
                  setFilterRecallCycle(params.cycle);
                }
                setCurrentPage('recall');
              } else if (page) {
                setCurrentPage(page);
              }
            }}
          />
        )}

        {/* 🌐 PAGE: พอร์ทัลด่วนรายงานความเสี่ยง (Dental Risk Standalone Portal - Admin View in CSSD) */}
        {currentPage === 'dental-risk-portal' && isAdmin && (
          <RiskPortalStandalonePage
            onNavigateToStaffRisk={() => setCurrentPage('dental-risk')}
            user={user}
            userRole={userRole}
          />
        )}

        {/* 📊 PAGE: แดชบอร์ดสิ้นเปลือง 4 คลัง (Consumable 4-Warehouse Dashboard) */}
        {currentPage === 'consumable-dashboard' && (
          <ConsumableDashboardPage
            itemMaster={itemMaster}
            supplies={supplies}
            logs={logs}
            onNavigate={handleNavigateToPage}
            onOpenTransfer={handleOpenTransferModal}
            getStatusConfig={getStatusConfig}
            parseImages={parseImages}
            setExpandedImage={setExpandedImage}
            user={user}
            isMobile={isMobile}
          />
        )}

        {/* 🏬 PAGE: ศูนย์จัดการคลังย่อย (Sub-Warehouse Caretaker Center) */}
        {currentPage === 'sub-warehouse-admin' && (
          <SubWarehouseAdminPage
            itemMaster={itemMaster}
            supplies={supplies}
            logs={logs}
            supabase={supabase}
            user={user}
            isMobile={isMobile}
            onNavigate={handleNavigateToPage}
            fetchData={fetchData}
            getStatusConfig={getStatusConfig}
            parseImages={parseImages}
            setExpandedImage={setExpandedImage}
          />
        )}

        {/* 📄 PAGE: รายงานการเบิกจ่ายประจำเดือน (Monthly Dispense Report) */}
        {currentPage === 'dispense-report' && (
          <DispenseReportPage
            supabase={supabase}
            itemMaster={itemMaster}
            supplies={supplies}
            logs={logs}
            orders={orders}
            onNavigate={handleNavigateToPage}
            user={user}
            isMobile={isMobile}
            initialReportMode={dispenseReportParams.mode}
            initialSelectedItemId={dispenseReportParams.itemId}
          />
        )}

        {/* PAGE: Admin Inventory (Consumable/Durable) */}
        {(currentPage === 'consumable' || currentPage === 'durable') && (
          <StockPage
            currentPage={currentPage}
            inventorySearchQuery={inventorySearchQuery}
            setInventorySearchQuery={setInventorySearchQuery}
            itemMaster={itemMaster}
            supplies={supplies}
            getStatusConfig={getStatusConfig}
            handleTogglePreview={handleTogglePreview}
            showPreview={showPreview}
            parseImages={parseImages}
            setExpandedImage={setExpandedImage}
            handleUpdateGlobalMinStock={handleUpdateGlobalMinStock}
            handleUpdateAmount={handleUpdateAmount}
            handleDeleteMaster={handleDeleteMaster}
            startEdit={startEdit}
            onOpenTransfer={handleOpenTransferModal}
            onNavigate={handleNavigateToPage}
          />
        )}

        {/* 🌟 PAGE: Handpiece (หัวกรอ) */}
        {currentPage === 'handpiece' && (
          <HandpiecePage
            itemMaster={itemMaster}
            supplies={supplies}
            handpieceSearchQuery={handpieceSearchQuery}
            setHandpieceSearchQuery={setHandpieceSearchQuery}
            handpieceFilter={handpieceFilter}
            setHandpieceFilter={setHandpieceFilter}
            selectedHandpieceName={selectedHandpieceName}
            setSelectedHandpieceName={setSelectedHandpieceName}
            getStatusConfig={getStatusConfig}
            handleTogglePreview={handleTogglePreview}
            showPreview={showPreview}
            parseImages={parseImages}
            setExpandedImage={setExpandedImage}
            handleUpdateAmount={handleUpdateAmount}
            handleDeleteMaster={handleDeleteMaster}
            startEdit={startEdit}
          />
        )}

        {/* 🌟 PAGE: Set Config (หน้าตั้งค่าชุด) */}
        {currentPage === 'set-config' && (
          <SetConfigPage
            setCategory={setCategory}
            setSetCategory={setSetCategory}
            selectedComponents={selectedComponents}
            setSelectedComponents={setSelectedComponents}
            newMasterItem={newMasterItem}
            setNewMasterItem={setNewMasterItem}
            handleImageUpload={handleImageUpload}
            isUploading={isUploading}
            imageUrl={imageUrl}
            itemMaster={itemMaster}
            supplies={supplies}
            resetForm={resetForm}
            handleSaveBox={handleSaveBox}
            isEditing={isEditing}
            getStatusConfig={getStatusConfig}
            startEdit={startEdit}
            handleDeleteMaster={handleDeleteMaster}
            handleUpdateAmount={handleUpdateAmount}
          />
        )}

        {/* 🌟 PAGE: Doctor Box (กล่องหัวกรอแบบแยกอิสระ) */}
        {currentPage === 'doctor-box' && (
          <DoctorBoxPage
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            newMasterItem={newMasterItem}
            setNewMasterItem={setNewMasterItem}
            cabinet={cabinet}
            setCabinet={setCabinet}
            shelf={shelf}
            setShelf={setShelf}
            row={row}
            setRow={setRow}
            boxItemsRequirement={selectedComponents}
            setBoxItemsRequirement={setSelectedComponents}
            selectedComponents={selectedComponents}
            setSelectedComponents={setSelectedComponents}
            isUploading={isUploading}
            handleImageUpload={handleImageUpload}
            imageUrl={imageUrl}
            setImageUrl={setImageUrl}
            resetForm={resetForm}
            handleSaveBox={handleSaveBox}
            itemMaster={itemMaster}
            supplies={supplies}
            instruments={instruments}
            startEdit={startEdit}
            handleDeleteMaster={handleDeleteMaster}
            handleDeleteDoctorBox={handleDeleteDoctorBox}
            handleUnbindFromBox={handleUnbindFromBox}
            isPrinting={isPrinting}
            generateUID={generateUID}
            setSetCategory={setSetCategory}
            setEditId={setEditId}
          />
        )}

        {/* 🌟 PAGE: Packaging */}
        {currentPage === 'packaging' && (
          <PackagingPage
            pkgType={pkgType}
            setPkgType={setPkgType}
            numBlankLabels={numBlankLabels}
            setNumBlankLabels={setNumBlankLabels}
            handlePrepareBlankLabels={handlePrepareBlankLabels}
            isPrinting={isPrinting}
            isSterileInfoComplete={isSterileInfoComplete}
            isAdmin={isAdmin}
            setActiveManagerField={setActiveManagerField}
            preparedBy={preparedBy}
            setPreparedBy={setPreparedBy}
            getFilteredPreparers={getFilteredPreparers}
            checkedBy={checkedBy}
            setCheckedBy={setCheckedBy}
            getFilteredCheckers={getFilteredCheckers}
            sterileMachine={sterileMachine}
            setSterileMachine={setSterileMachine}
            handleMachineChange={handleMachineChange}
            machinesList={machinesList}
            sterileCycle={sterileCycle}
            setSterileCycle={setSterileCycle}
            getAvailableCycles={getAvailableCycles}
            cyclesList={cyclesList}
            getCycleDisplayName={getCycleDisplayName}
            getAutoSelectedCycle={getAutoSelectedCycle}
            instruments={instruments}
            selectedInspectionItem={selectedInspectionItem}
            setSelectedInspectionItem={setSelectedInspectionItem}
            isUsingPreparedScan={isUsingPreparedScan}
            setIsUsingPreparedScan={setIsUsingPreparedScan}
            isBarcodeScanned={isBarcodeScanned}
            setIsBarcodeScanned={setIsBarcodeScanned}
            setCameraTargetField={setCameraTargetField}
            setIsCameraOpen={setIsCameraOpen}
            userProfiles={userProfiles}
            user={user}
            generateUID={generateUID}
            handleConfirmInspection={handleConfirmInspection}
            targetBoxForPack={targetBoxForPack}
            setTargetBoxForPack={setTargetBoxForPack}
            itemMaster={itemMaster}
            supplies={supplies}
            handleCreateUniqueCode={handleCreateUniqueCode}
            scanBlankItem={scanBlankItem}
            setScanBlankItem={setScanBlankItem}
            historyFilterDate={historyFilterDate}
            setHistoryFilterDate={setHistoryFilterDate}
            selectedPrints={selectedPrints}
            setSelectedPrints={setSelectedPrints}
            handleBatchPrint={handleBatchPrint}
            setPrintPreviewItem={setPrintPreviewItem}
            handleCancelPackaging={handleCancelPackaging}
            blankLabelsToPrint={blankLabelsToPrint}
            setBlankLabelsToPrint={setBlankLabelsToPrint}
            showPreview={showPreview}
            setShowPreview={setShowPreview}
            handleTogglePreview={handleTogglePreview}
            parseImages={parseImages}
            isSameDate={isSameDate}
            getSafeTime={getSafeTime}
            barcodeInputRef={barcodeInputRef}
            handleBlurToFocus={handleBlurToFocus}
            logs={logs}
          />
        )}

        {/* 🌟 PAGE: Tool Assembly (ห้องจัดเครื่องมือ) */}
        {currentPage === 'tool-assembly' && (
          <ToolAssemblyPage
            assemblyTab={assemblyTab}
            setAssemblyTab={setAssemblyTab}
            selectedForAssembly={selectedForAssembly}
            setSelectedForAssembly={setSelectedForAssembly}
            handleConfirmAssembly={handleConfirmAssembly}
            assemblyHistoryDate={assemblyHistoryDate}
            setAssemblyHistoryDate={setAssemblyHistoryDate}
            itemMaster={itemMaster}
            supplies={supplies}
            logs={logs}
            isSameDate={isSameDate}
            getStatusConfig={getStatusConfig}
            showPreview={showPreview}
            setShowPreview={setShowPreview}
            handleTogglePreview={handleTogglePreview}
            parseImages={parseImages}
            packagingItem={packagingItem}
            setPackagingItem={setPackagingItem}
            handlePrintBarcode={handleConfirmPrint}
            isPrinting={isPrinting}
            scanBlankItem={scanBlankItem}
            setScanBlankItem={setScanBlankItem}
            targetBoxForPack={targetBoxForPack}
            setTargetBoxForPack={setTargetBoxForPack}
            getSafeTime={getSafeTime}
            selectedSetTemplate={selectedSetTemplate}
            setSelectedSetTemplate={setSelectedSetTemplate}
            selectedSingleItem={selectedSingleItem}
            setSelectedSingleItem={setSelectedSingleItem}
            setExpandedImage={setExpandedImage}
            getOptimizedImageUrl={getOptimizedImageUrl}
          />
        )}

        {/* 🌟 PAGE: Washing (ห้องล้าง) */}
        {currentPage === 'washing' && (
          <WashingPage
            barcodeInputRef={barcodeInputRef}
            handleBlurToFocus={handleBlurToFocus}
            barcodeInput={barcodeInput}
            setBarcodeInput={setBarcodeInput}
            handleWashingScan={handleWashingScan}
            setCameraTargetField={setCameraTargetField}
            setIsCameraOpen={setIsCameraOpen}
            washingSuccessList={washingSuccessList}
            washingErrorList={washingErrorList}
            handleConfirmWashing={handleConfirmWashing}
            washingHistoryDate={washingHistoryDate}
            setWashingHistoryDate={setWashingHistoryDate}
            logs={logs}
            isSameDate={isSameDate}
          />
        )}

        {/* 🌟 PAGE: Sterile (ห้อง Sterile) */}
        {currentPage === 'supply' && (
          <SupplyPage
            actionType={actionType}
            setActionType={setActionType}
            sterileSuccessList={sterileSuccessList}
            setSterileSuccessList={setSterileSuccessList}
            sterileProblemList={sterileProblemList}
            setSterileProblemList={setSterileProblemList}
            targetBox={targetBox}
            setTargetBox={setTargetBox}
            barcodeInput={barcodeInput}
            setBarcodeInput={setBarcodeInput}
            scanErrorMessage={scanErrorMessage}
            setScanErrorMessage={setScanErrorMessage}
            isSterileModalOpen={isSterileModalOpen}
            setIsSterileModalOpen={setIsSterileModalOpen}
            isDispenseModalOpen={isDispenseModalOpen}
            setIsDispenseModalOpen={setIsDispenseModalOpen}
            selectedOrder={selectedOrder}
            setSelectedOrder={setSelectedOrder}
            targetWarehouse={targetWarehouse}
            setTargetWarehouse={setTargetWarehouse}
            handleCloseSterileModal={handleCloseSterileModal}
            preparedBy={preparedBy}
            setPreparedBy={setPreparedBy}
            checkedBy={checkedBy}
            setCheckedBy={setCheckedBy}
            sterileMachine={sterileMachine}
            setSterileMachine={setSterileMachine}
            sterileCycle={sterileCycle}
            setSterileCycle={setSterileCycle}
            barcodeInputRef={barcodeInputRef}
            handleBlurToFocus={handleBlurToFocus}
            handleSterileScan={handleSterileScan}
            handleDispenseScan={handleDispenseScan}
            setCameraTargetField={setCameraTargetField}
            setIsCameraOpen={setIsCameraOpen}
            handleConfirmSterileBasket={handleConfirmSterileBasket}
            instruments={instruments}
            supplies={supplies}
            itemMaster={itemMaster}
            instrumentTags={instrumentTags}
            orders={orders}
            handleCancelOrder={handleCancelOrder}
            showPreview={showPreview}
            setShowPreview={setShowPreview}
            handleTogglePreview={handleTogglePreview}
            logs={logs}
            sterileHistoryDate={sterileHistoryDate}
            setSterileHistoryDate={setSterileHistoryDate}
            isSameDate={isSameDate}
            getSafeTime={getSafeTime}
            parseOrderNote={parseOrderNote}
            getUserNameByEmail={getUserNameByEmail}
            getTimeline={getTimeline}
            sterileErrorAlert={sterileErrorAlert}
            setSterileErrorAlert={setSterileErrorAlert}
            selectedTimelineItem={selectedTimelineItem}
            setSelectedTimelineItem={setSelectedTimelineItem}
          />
        )}

        {/* 🌟 PAGE: Dispense (Picking System) */}
        {currentPage === 'dispense' && (
          <DispensePage
            scanErrorMessage={scanErrorMessage}
            setScanErrorMessage={setScanErrorMessage}
            selectedOrder={selectedOrder}
            setSelectedOrder={setSelectedOrder}
            sterileSuccessList={sterileSuccessList}
            setSterileSuccessList={setSterileSuccessList}
            itemMaster={itemMaster}
            showPreview={showPreview}
            setShowPreview={setShowPreview}
            handleTogglePreview={handleTogglePreview}
            parseImages={parseImages}
            barcodeInputRef={barcodeInputRef}
            handleBlurToFocus={handleBlurToFocus}
            barcodeInput={barcodeInput}
            setBarcodeInput={setBarcodeInput}
            handleDispenseScan={handleDispenseScan}
            setCameraTargetField={setCameraTargetField}
            setIsCameraOpen={setIsCameraOpen}
            targetWarehouse={targetWarehouse}
            setTargetWarehouse={setTargetWarehouse}
            WAREHOUSES_LIST={WAREHOUSES_LIST}
            handleConfirmDispense={handleConfirmSterileBasket}
            handleConfirmSterileBasket={handleConfirmSterileBasket}
            handleCancelOrder={handleCancelOrder}
            getUserNameByEmail={getUserNameByEmail}
            orders={orders}
            dispenseHistoryDate={dispenseHistoryDate}
            setDispenseHistoryDate={setDispenseHistoryDate}
            isSameDate={isSameDate}
            getSafeTime={getSafeTime}
            handlePrintBill={null}
            parseOrderNote={parseOrderNote}
            instruments={instruments}
            instrumentTags={instrumentTags}
          />
        )}


        {/* PAGE: Clinic Order */}
        {currentPage === 'clinic-order' && (
          <ClinicOrderPage
            isGuestMode={isGuestMode}
            targetWarehouse={targetWarehouse}
            setTargetWarehouse={setTargetWarehouse}
            WAREHOUSES_LIST={WAREHOUSES_LIST}
            clinicOrderSearchQuery={clinicOrderSearchQuery}
            setClinicOrderSearchQuery={setClinicOrderSearchQuery}
            itemMaster={itemMaster}
            instruments={instruments}
            supplies={supplies}
            orderBasket={orderBasket}
            setOrderBasket={setOrderBasket}
            orderNote={orderNote}
            setOrderNote={setOrderNote}
            orderedByName={orderedByName}
            setOrderedByName={setOrderedByName}
            user={user}
            handleSendOrder={handleSendOrder}
            handleOrderQuickAdd={handleOrderQuickAdd}
            handleOrderQuickRemove={handleOrderQuickRemove}
            orders={orders}
            isSameDate={isSameDate}
            getSafeTime={getSafeTime}
            getUserNameByEmail={getUserNameByEmail}
            clinicOrderHistoryDate={clinicOrderHistoryDate}
            setClinicOrderHistoryDate={setClinicOrderHistoryDate}
            handleCancelOrder={handleCancelOrder}
            parseOrderNote={parseOrderNote}
            showPreview={showPreview}
            handleTogglePreview={handleTogglePreview}
            parseImages={parseImages}
            isCartModalOpen={isCartModalOpen}
            setIsCartModalOpen={setIsCartModalOpen}
          />
        )}

        {/* 🌟 PAGE: Barcode Tracking (หน้าเช็คบาร์โค้ด) */}
        {currentPage === 'barcode-tracking' && (
          <BarcodeTrackingPage
            instruments={instruments}
            instrumentTags={instrumentTags}
            getTrackedInstruments={getTrackedInstruments}
          />
        )}

        {/* 🌟 PAGE: Trace (ค้นหาวัสดุ) */}
        {currentPage === 'trace' && (
          <TracePage
            barcodeInputRef={barcodeInputRef}
            handleBlurToFocus={handleBlurToFocus}
            traceInput={traceInput}
            setTraceInput={setTraceInput}
            setCameraTargetField={setCameraTargetField}
            setIsCameraOpen={setIsCameraOpen}
            handleTraceSearch={handleTraceSearch}
            traceError={traceError}
            traceData={traceData}
            traceLogs={traceLogs}
            itemMaster={itemMaster}
            instrumentTags={instrumentTags}
            parseImages={parseImages}
            getOptimizedImageUrl={getOptimizedImageUrl}
            getRecallImages={getRecallImages}
            setZoomedRecallImageUrl={setZoomedRecallImageUrl}
            highlightActionText={highlightActionText}
            parseOrderNote={parseOrderNote}
          />
        )}

        {/* 🌟 PAGE: เรียกคืนวัสดุที่มีปัญหา (Material Recall Page) */}
        {currentPage === 'recall' && (
          <RecallPage
            filterRecallItemName={filterRecallItemName}
            setFilterRecallItemName={setFilterRecallItemName}
            filterRecallBarcode={filterRecallBarcode}
            setFilterRecallBarcode={setFilterRecallBarcode}
            filterRecallMfgDate={filterRecallMfgDate}
            setFilterRecallMfgDate={setFilterRecallMfgDate}
            filterRecallPreparedBy={filterRecallPreparedBy}
            setFilterRecallPreparedBy={setFilterRecallPreparedBy}
            getFilteredPreparers={getFilteredPreparers}
            filterRecallCheckedBy={filterRecallCheckedBy}
            setFilterRecallCheckedBy={setFilterRecallCheckedBy}
            getFilteredCheckers={getFilteredCheckers}
            filterRecallMachine={filterRecallMachine}
            setFilterRecallMachine={setFilterRecallMachine}
            machinesList={machinesList}
            filterRecallCycle={filterRecallCycle}
            setFilterRecallCycle={setFilterRecallCycle}
            cyclesList={cyclesList}
            handleClearRecallFilters={handleClearRecallFilters}
            handleRecallSearch={handleRecallSearch}
            isRecallSearching={isRecallSearching}
            recallSearchResults={recallSearchResults}
            selectedRecallIds={selectedRecallIds}
            setSelectedRecallIds={setSelectedRecallIds}
            activeUserRole={activeUserRole}
            isAdmin={isAdmin}
            setCancelRecallPromptItem={setCancelRecallPromptItem}
            handleApproveCancelRecall={handleApproveCancelRecall}
            handleRejectCancelRecall={handleRejectCancelRecall}
            handleRecallAction={handleRecallAction}
            handleExecuteRecall={handleRecallAction}
            recallReason={recallReason}
            setRecallReason={setRecallReason}
            recallCameraInputRef={recallCameraInputRef}
            recallGalleryInputRef={recallGalleryInputRef}
            handleRecallImageChange={handleRecallImageChange}
            isRecallUploading={isRecallUploading}
            recallImageUrl={recallImageUrl}
            handleRemoveRecallImage={handleRemoveRecallImage}
            setShowRecallUploadPopup={setShowRecallUploadPopup}
            allRecallBatches={allRecallBatches}
            recallRoundsPage={recallRoundsPage}
            setRecallRoundsPage={setRecallRoundsPage}
            fetchAllRecallBatches={fetchAllRecallBatches}
            isFetchingAllBatches={isFetchingAllBatches}
            getRecallImages={getRecallImages}
            setZoomedRecallImageUrl={setZoomedRecallImageUrl}
            setSelectedDetailCountName={setSelectedDetailCountName}
            setDetailModalItems={setDetailModalItems}
            setActiveRecallBatchIdForUpload={setActiveRecallBatchIdForUpload}
            highlightActionText={highlightActionText}
          />
        )}

        {/* 🌟 PAGE: Manage Users (จัดการผู้ใช้งานและสิทธิ์) */}
        {currentPage === 'manage-users' && (
          <ManageUsersPage
            isAdmin={isAdmin}
            dbError={dbError}
            defaultSaveStatus={defaultSaveStatus}
            roleOptions={roleOptions}
            selectedDefaultRoleTab={selectedDefaultRoleTab}
            setSelectedDefaultRoleTab={setSelectedDefaultRoleTab}
            PAGES_LIST={isAdmin ? PAGES_LIST : PAGES_LIST.filter(p => p.id !== 'dental-risk')}
            roleDefaults={roleDefaults}
            handleUpdateRoleDefaultPages={handleUpdateRoleDefaultPages}
            newUserEmail={newUserEmail}
            setNewUserEmail={setNewUserEmail}
            newUserName={newUserName}
            setNewUserName={setNewUserName}
            newUserRole={newUserRole}
            setNewUserRole={setNewUserRole}
            handleCreateUser={handleAddUserProfile}
            handleAddUserProfile={handleAddUserProfile}
            userSearchQuery={userSearchQuery}
            setUserSearchQuery={setUserSearchQuery}
            userProfiles={userProfiles}
            getUserNameByEmail={getUserNameByEmail}
            handleUpdateUserRole={handleUpdateRole}
            handleUpdateRole={handleUpdateRole}
            handleToggleUserPageAccess={(email, pageId) => {
              const currentAllowedStr = (userProfiles.find(u => u.email === email)?.allowed_pages) || '';
              const currentAllowedList = currentAllowedStr ? currentAllowedStr.split(',') : [];
              let nextAllowed;
              if (currentAllowedList.includes(pageId)) {
                nextAllowed = currentAllowedList.filter(id => id !== pageId);
              } else {
                nextAllowed = [...currentAllowedList, pageId];
              }
              handleUpdateAllowedPages(email, nextAllowed.join(','));
            }}
            handleUpdateAllowedPages={handleUpdateAllowedPages}
            handleResetUserToRoleDefault={(email, role) => handleUpdateAllowedPages(email, roleDefaults[role] || '')}
            setResetPasswordUser={setResetPasswordUser}
            setNewPasswordValue={setNewPasswordValue}
            setDeleteConfirmUser={setDeleteConfirmUser}
            handleDeleteUserProfile={handleDeleteUserProfile}
            setSqlScriptToShow={setSqlScriptToShow}
            sqlScriptToShow={sqlScriptToShow}
            user={user}
            handleUpdateNameLocal={handleUpdateNameLocal}
            handleUpdateNameDb={handleUpdateNameDb}
            filteredProfiles={filteredProfiles}
          />
        )}

        {/* 🗑️ Modal ยืนยันการลบผู้ใช้งาน (Custom Confirmation Modal) */}
        {deleteConfirmUser && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '20px',
              width: '95%',
              maxWidth: '450px',
              padding: '30px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#fee2e2',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '0 auto 20px auto',
                fontSize: '28px',
                color: '#ef4444'
              }}>
                ⚠️
              </div>
              <h3 style={{
                margin: '0 0 10px 0',
                color: '#0f172a',
                fontSize: '1.4em',
                fontWeight: 'bold'
              }}>
                ยืนยันการลบผู้ใช้งาน
              </h3>
              <p style={{
                margin: '0 0 25px 0',
                color: '#475569',
                fontSize: '0.95em',
                lineHeight: '1.6'
              }}>
                คุณแน่ใจหรือไม่ที่จะลบผู้ใช้งาน <strong style={{ color: '#0f172a', wordBreak: 'break-all' }}>{deleteConfirmUser.email}</strong> ออกจากระบบ? การดำเนินการนี้ไม่สามารถย้อนกลับได้
              </p>
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center'
              }}>
                <button
                  onClick={() => setDeleteConfirmUser(null)}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    borderRadius: '12px',
                    border: '1px solid #cbd5e1',
                    backgroundColor: '#ffffff',
                    color: '#475569',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    minHeight: '45px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f1f5f9'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#ffffff'}
                >
                  ยกเลิก
                </button>
                <button
                  id="confirm-delete-user-btn"
                  onClick={handleExecuteDeleteUserProfile}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    borderRadius: '12px',
                    border: 'none',
                    backgroundColor: '#ef4444',
                    color: '#ffffff',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)',
                    minHeight: '45px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                >
                  ยืนยันการลบ
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 🔑 Modal เปลี่ยนรหัสผ่านผู้ใช้งาน (Custom Password Reset Modal) */}
        {resetPasswordUser && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '20px',
              width: '95%',
              maxWidth: '500px',
              padding: '30px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              textAlign: 'left'
            }}>
              {!sqlScriptToShow ? (
                <form onSubmit={handleResetPassword}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: '#eff6ff',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    margin: '0 auto 20px auto',
                    fontSize: '28px',
                    color: '#3b82f6'
                  }}>
                    🔑
                  </div>
                  <h3 style={{
                    margin: '0 0 10px 0',
                    color: '#0f172a',
                    fontSize: '1.4em',
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }}>
                    เปลี่ยนรหัสผ่านผู้ใช้งาน
                  </h3>
                  <p style={{
                    margin: '0 0 20px 0',
                    color: '#475569',
                    fontSize: '0.95em',
                    lineHeight: '1.6',
                    textAlign: 'center'
                  }}>
                    อีเมล: <strong style={{ color: '#0f172a', wordBreak: 'break-all' }}>{resetPasswordUser.email}</strong>
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '25px' }}>
                    <label style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#475569' }}>รหัสผ่านใหม่ (ต้องมีความยาวอย่างน้อย 6 ตัวอักษร)</label>
                    <input
                      type="password"
                      placeholder="กรอกรหัสผ่านใหม่..."
                      value={newPasswordValue}
                      onChange={(e) => setNewPasswordValue(e.target.value)}
                      style={{ padding: '12px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '1em', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                      minLength={6}
                      required
                    />
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'center'
                  }}>
                    <button
                      type="button"
                      onClick={() => { setResetPasswordUser(null); setNewPasswordValue(''); }}
                      disabled={isResettingPassword}
                      style={{
                        flex: 1,
                        padding: '12px 20px',
                        borderRadius: '12px',
                        border: '1px solid #cbd5e1',
                        backgroundColor: '#ffffff',
                        color: '#475569',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        minHeight: '45px'
                      }}
                      onMouseEnter={(e) => { if (!isResettingPassword) e.target.style.backgroundColor = '#f1f5f9'; }}
                      onMouseLeave={(e) => { if (!isResettingPassword) e.target.style.backgroundColor = '#ffffff'; }}
                    >
                      ยกเลิก
                    </button>
                    <button
                      type="submit"
                      disabled={isResettingPassword}
                      style={{
                        flex: 1,
                        padding: '12px 20px',
                        borderRadius: '12px',
                        border: 'none',
                        backgroundColor: '#3b82f6',
                        color: '#ffffff',
                        fontWeight: '600',
                        cursor: isResettingPassword ? 'not-allowed' : 'pointer',
                        opacity: isResettingPassword ? 0.7 : 1,
                        transition: 'all 0.2s',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                        minHeight: '45px'
                      }}
                      onMouseEnter={(e) => { if (!isResettingPassword) e.target.style.backgroundColor = '#2563eb'; }}
                      onMouseLeave={(e) => { if (!isResettingPassword) e.target.style.backgroundColor = '#3b82f6'; }}
                    >
                      {isResettingPassword ? 'กำลังบันทึก...' : 'บันทึกรหัสผ่าน'}
                    </button>
                  </div>
                </form>
              ) : (
                <div>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: '#fef3c7',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    margin: '0 auto 20px auto',
                    fontSize: '28px',
                    color: '#d97706'
                  }}>
                    ⚠️
                  </div>
                  <h3 style={{
                    margin: '0 0 10px 0',
                    color: '#0f172a',
                    fontSize: '1.4em',
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }}>
                    ต้องการติดตั้งฟังก์ชันใน Supabase
                  </h3>
                  <p style={{
                    margin: '0 0 20px 0',
                    color: '#475569',
                    fontSize: '0.95em',
                    lineHeight: '1.6',
                    textAlign: 'left'
                  }}>
                    พบข้อผิดพลาดเนื่องจากยังไม่มีฟังก์ชัน <code>admin_reset_password</code> ในฐานข้อมูล Supabase ของคุณ <br />
                    กรุณาทำการคัดลอก SQL ด้านล่างนี้ไปรันใน <strong>Supabase SQL Editor</strong> เพื่อสร้างฟังก์ชันดังกล่าว จากนั้นจึงจะสามารถใช้ปุ่มนี้เปลี่ยนรหัสผ่านให้กับผู้ใช้อื่นได้:
                  </p>

                  <div style={{ position: 'relative', marginBottom: '20px' }}>
                    <pre style={{
                      background: '#1e293b',
                      color: '#f8fafc',
                      padding: '15px',
                      borderRadius: '10px',
                      overflowX: 'auto',
                      fontSize: '0.85em',
                      lineHeight: '1.5',
                      margin: 0,
                      maxHeight: '200px',
                      fontFamily: 'Courier New, monospace'
                    }}>
                      {`CREATE OR REPLACE FUNCTION public.admin_reset_password(
  target_email text,
  new_password text
)
RETURNS text
LANGUAGE plpgsql
SECURITY DEFINER
SET search_path = auth, public
AS $$
DECLARE
  caller_email text;
BEGIN
  -- Get caller email
  caller_email := auth.jwt() ->> 'email';

  -- Check if caller is an admin
  IF NOT EXISTS (
    SELECT 1 FROM public.user_profiles
    WHERE email = caller_email AND role = 'admin'
  ) THEN
    RAISE EXCEPTION 'Unauthorized: Only admins can perform this action';
  END IF;

  -- Update target user password
  UPDATE auth.users
  SET encrypted_password = crypt(new_password, gen_salt('bf')),
      updated_at = now()
  WHERE email = target_email;

  IF NOT FOUND THEN
    RAISE EXCEPTION 'User with email % not found', target_email;
  END IF;

  RETURN 'Password reset successfully';
END;
$$;`}
                    </pre>
                    <button
                      onClick={() => {
                        const sqlText = `CREATE OR REPLACE FUNCTION public.admin_reset_password(
  target_email text,
  new_password text
)
RETURNS text
LANGUAGE plpgsql
SECURITY DEFINER
SET search_path = auth, public
AS $$
DECLARE
  caller_email text;
BEGIN
  -- Get caller email
  caller_email := auth.jwt() ->> 'email';

  -- Check if caller is an admin
  IF NOT EXISTS (
    SELECT 1 FROM public.user_profiles
    WHERE email = caller_email AND role = 'admin'
  ) THEN
    RAISE EXCEPTION 'Unauthorized: Only admins can perform this action';
  END IF;

  -- Update target user password
  UPDATE auth.users
  SET encrypted_password = crypt(new_password, gen_salt('bf')),
      updated_at = now()
  WHERE email = target_email;

  IF NOT FOUND THEN
    RAISE EXCEPTION 'User with email % not found', target_email;
  END IF;

  RETURN 'Password reset successfully';
END;
$$;`;
                        navigator.clipboard.writeText(sqlText);
                        alert("คัดลอก SQL Script เรียบร้อยแล้ว!");
                      }}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '10px',
                        padding: '6px 12px',
                        background: '#3b82f6',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.8em',
                        fontWeight: 'bold'
                      }}
                    >
                      📋 คัดลอก SQL
                    </button>
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={() => { setSqlScriptToShow(false); setResetPasswordUser(null); }}
                      style={{
                        flex: 1,
                        padding: '12px 20px',
                        borderRadius: '12px',
                        border: 'none',
                        backgroundColor: '#64748b',
                        color: '#ffffff',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        minHeight: '45px',
                        textAlign: 'center'
                      }}
                    >
                      ปิดหน้าต่าง
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {isCameraOpen && (
          <CameraScanner
            onScanSuccess={handleCameraScanSuccess}
            onClose={() => {
              setIsCameraOpen(false);
              if (cameraTargetField === 'direct-card-inspection') {
                setSelectedInspectionItem(null);
              }
            }}
            initialMode={cameraTargetField === 'direct-card-inspection' ? 'single' : undefined}
          />
        )}

        {/* 🌟 Custom Success Modal */}
        {successModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '20px',
              width: '95%',
              maxWidth: '450px',
              padding: '30px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#dcfce7',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '0 auto 20px auto',
                fontSize: '28px',
                color: '#22c55e'
              }}>
                ✓
              </div>
              <h3 style={{
                margin: '0 0 10px 0',
                color: '#0f172a',
                fontSize: '1.4em',
                fontWeight: 'bold'
              }}>
                {successModal.title}
              </h3>
              <p style={{
                margin: '0 0 25px 0',
                color: '#475569',
                fontSize: '0.95em',
                lineHeight: '1.6',
                whiteSpace: 'pre-line'
              }}>
                {successModal.message}
              </p>
              {!successModal.hideButton && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  <button
                    onClick={() => {
                      const closeFn = successModal.onClose;
                      setSuccessModal(null);
                      if (closeFn) closeFn();
                    }}
                    style={{
                      padding: '12px 30px',
                      borderRadius: '12px',
                      border: 'none',
                      backgroundColor: '#22c55e',
                      color: '#ffffff',
                      fontWeight: '600',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(34, 197, 94, 0.2)',
                      minWidth: '120px',
                      textAlign: 'center'
                    }}
                  >
                    ตกลง
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 🌟 Camera Scan Success Modal with QR Card */}
        {cameraScanSuccessData && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '24px',
              width: '95%',
              maxWidth: '430px',
              padding: '24px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              {/* Header and Checkmark */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: '#dcfce7',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '20px',
                  color: '#22c55e'
                }}>
                  ✓
                </div>
                <h3 style={{ margin: 0, color: '#0f172a', fontSize: '1.3em', fontWeight: 'bold' }}>
                  สแกนสำเร็จ!
                </h3>
              </div>

              {/* Status Transition Label (White Label) */}
              {(() => {
                const traceData = cameraScanSuccessData;
                const statusMap = {
                  'sterile-pending': 'non-sterile',
                  'sterile-ready': 'sterile',
                  'in-use': 'in-use',
                  'recalled': 'recalled',
                  'finished': 'finished'
                };
                const currentStatus = statusMap[traceData.status] || traceData.status || 'unknown';

                let transitionStr = '';
                if (currentPage === 'washing') {
                  transitionStr = `${currentStatus} ➔ washing`;
                } else if (currentPage === 'supply') {
                  if (isDispenseModalOpen) {
                    transitionStr = `${currentStatus} ➔ in-use`;
                  } else {
                    const mach = (sterileMachine || 'autoclave').toLowerCase();
                    transitionStr = `non-sterile ➔ sterile ${mach}`;
                  }
                } else if (currentPage === 'dispense') {
                  transitionStr = `${currentStatus} ➔ in-use`;
                } else if (currentPage === 'trace') {
                  transitionStr = `status: ${currentStatus}`;
                } else {
                  transitionStr = `status: ${currentStatus}`;
                }

                return (
                  <div style={{
                    backgroundColor: '#ffffff',
                    border: '1.5px solid #cbd5e1',
                    borderRadius: '9999px',
                    padding: '8px 20px',
                    fontSize: '0.9em',
                    fontWeight: '800',
                    color: '#334155',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'monospace, sans-serif',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {transitionStr}
                  </div>
                );
              })()}

              {/* Card representation of the QR information */}
              {(() => {
                const traceData = cameraScanSuccessData;
                const mItem = itemMaster.find(m => m.id === traceData.master_id);
                const details = traceData.details || {};
                const tag = instrumentTags.find(t => String(t.id) === String(details.tag_id));

                return (
                  <div style={{
                    width: '100%',
                    maxWidth: '350px',
                    background: traceData.status === 'recalled'
                      ? 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)'
                      : traceData.status === 'sterile-ready'
                        ? 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)'
                        : 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
                    border: traceData.status === 'recalled'
                      ? '6px solid #c0392b'
                      : traceData.status === 'sterile-ready'
                        ? '6px solid #27ae60'
                        : '6px solid #f39c12',
                    borderRadius: '20px',
                    padding: '10px',
                    boxShadow: traceData.status === 'recalled'
                      ? '0 15px 35px rgba(0,0,0,0.3), 0 0 25px rgba(192, 57, 43, 0.4)'
                      : traceData.status === 'sterile-ready'
                        ? '0 15px 35px rgba(0,0,0,0.3), 0 0 25px rgba(39, 174, 96, 0.4)'
                        : '0 15px 35px rgba(0,0,0,0.3), 0 0 25px rgba(243, 156, 18, 0.4)',
                    color: '#2c3e50',
                    fontFamily: "'Segoe UI', Tahoma, sans-serif",
                    position: 'relative'
                  }}>
                    {/* Inside Card body */}
                    <div style={{
                      background: traceData.status === 'recalled'
                        ? '#ffebee'
                        : traceData.status === 'sterile-ready'
                          ? '#e8f5e9'
                          : '#fcfcf4',
                      border: traceData.status === 'recalled'
                        ? '2px solid #c0392b'
                        : traceData.status === 'sterile-ready'
                          ? '2px solid #27ae60'
                          : '2px solid #7f8c8d',
                      borderRadius: '12px',
                      padding: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                      minHeight: '420px',
                      boxShadow: 'inset 0 0 15px rgba(0,0,0,0.05)'
                    }}>
                      {/* Header: Name and HP/Type */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #7f8c8d', paddingBottom: '6px' }}>
                        <span style={{ fontWeight: '900', fontSize: '1.1em', letterSpacing: '0.5px', color: '#1a252f' }}>
                          {traceData.item_name ? (traceData.item_name.length > 20 ? traceData.item_name.slice(0, 18) + '...' : traceData.item_name) : 'ไม่ทราบชื่อ'}
                        </span>
                        <span style={{ fontWeight: 'bold', fontSize: '1.05em', color: '#c0392b', display: 'flex', alignItems: 'center', gap: '2px' }}>
                          {traceData.status === 'sterile-ready' ? '⚡ Sterile' : '☣️ Non-Sterile'}
                        </span>
                      </div>

                      {/* Image Window (Pokemon style) */}
                      <div style={{
                        border: '3px solid #bdc3c7',
                        borderRadius: '6px',
                        background: '#fff',
                        padding: '6px',
                        textAlign: 'center',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.06), inset 0 0 10px rgba(0,0,0,0.05)',
                        position: 'relative'
                      }}>
                        {mItem && mItem.image_url ? (() => {
                          const { full, zoom } = parseImages(mItem.image_url);
                          return (
                            <div style={{ display: 'flex', width: '100%', height: '140px', alignItems: 'center', position: 'relative' }}>
                              <img
                                src={getOptimizedImageUrl(full)}
                                alt={traceData.item_name}
                                style={{
                                  width: zoom ? '70%' : '100%',
                                  height: '100%',
                                  objectFit: 'contain',
                                  borderRadius: '4px',
                                  display: 'block',
                                  marginRight: zoom ? 'auto' : '0'
                                }}
                              />
                              {zoom && (
                                <img
                                  src={getOptimizedImageUrl(zoom)}
                                  alt={`${traceData.item_name} zoom`}
                                  style={{
                                    position: 'absolute',
                                    bottom: '0px',
                                    right: '0px',
                                    width: '75px',
                                    height: '75px',
                                    objectFit: 'contain',
                                    border: '3px solid #bdc3c7',
                                    borderRadius: '8px',
                                    background: '#fff',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                                    zIndex: 10
                                  }}
                                />
                              )}
                            </div>
                          );
                        })() : tag && tag.image_url ? (
                          <img
                            src={getOptimizedImageUrl(tag.image_url)}
                            alt={tag.name}
                            style={{ width: '100%', height: '140px', objectFit: 'contain', borderRadius: '4px' }}
                          />
                        ) : (
                          <div style={{ height: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3em', background: '#eceff1', borderRadius: '4px', color: '#b0bec5' }}>
                            🛠️
                          </div>
                        )}

                        {/* RECALLED (Biohazard) Overlay */}
                        {traceData.status === 'recalled' && (
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            background: '#ffffff',
                            border: '1.5px solid #c0392b',
                            padding: '4px',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '1px 1px 4px rgba(0,0,0,0.15)',
                            zIndex: 10
                          }}>
                            <svg width="46" height="46" viewBox="0 0 576 512" style={{ display: 'block', fill: '#c0392b' }}>
                              <path d="M287.9 112c18.6 0 36.2 3.8 52.8 9.6 13.3-10.3 23.6-24.3 29.5-40.7-25.2-10.9-53-17-82.2-17-29.1 0-56.9 6-82.1 16.9 5.9 16.4 16.2 30.4 29.5 40.7 16.5-5.7 34-9.5 52.5-9.5zM163.6 438.7c12-11.8 20.4-26.4 24.5-42.4-32.9-26.4-54.8-65.3-58.9-109.6-8.5-2.8-17.2-4.6-26.4-4.6-7.6 0-15.2 1-22.5 3.1 4.1 62.8 35.8 118 83.3 153.5zm224.2-42.6c4.1 16 12.5 30.7 24.5 42.5 47.4-35.5 79.1-90.7 83-153.5-7.2-2-14.7-3-22.2-3-9.2 0-18 1.9-26.6 4.7-4.1 44.2-26 82.9-58.7 109.3zm113.5-205c-17.6-10.4-36.3-16.6-55.3-19.9 6-17.7 10-36.4 10-56.2 0-41-14.5-80.8-41-112.2-2.5-3-6.6-3.7-10-1.8-3.3 1.9-4.8 6-3.6 9.7 4.5 13.8 6.6 26.3 6.6 38.5 0 67.8-53.8 122.9-120 122.9S168 117 168 49.2c0-12.1 2.2-24.7 6.6-38.5 1.2-3.7-.3-7.8-3.6-9.7-3.4-1.9-7.5-1.2-10 1.8C134.6 34.2 120 74 120 115c0 19.8 3.9 38.5 10 56.2-18.9 3.3-37.7 9.5-55.3 19.9-34.6 20.5-61 53.3-74.3 92.4-1.3 3.7.2 7.7 3.5 9.8 3.3 2 7.5 1.3 10-1.6 9.4-10.8 19-19.1 29.2-25.1 57.3-33.9 130.8-13.7 163.9 45 33.1 58.7 13.4 134-43.9 167.9-10.2 6.1-22 10.4-35.8 13.4-3.7.8-6.4 4.2-6.4 8.1.1 4 2.7 7.3 6.5 8 39.7 7.8 80.6.8 115.2-19.7 18-10.6 32.9-24.5 45.3-40.1 12.4 15.6 27.3 29.5 45.3 40.1 34.6 20.5 75.5 27.5 115.2 19.7 3.8-.7 6.4-4 6.5-8 0-3.9-2.6-7.3-6.4-8.1-13.9-2.9-25.6-7.3-35.8-13.4-57.3-33.9-77-109.2-43.9-167.9s106.6-78.9 163.9-45c10.2 6.1 19.8 14.3 29.2 25.1 2.5 2.9 6.7 3.6 10 1.6s4.8-6.1 3.5-9.8c-13.1-39.1-39.5-72-74.1-92.4zm-213.4 129c-26.5 0-48-21.5-48-48s21.5-48 48-48 48 21.5 48 48-21.5 48-48 48z" />
                            </svg>
                          </div>
                        )}

                        {/* NON-STERILE Overlay */}
                        {traceData.status !== 'sterile-ready' && traceData.status !== 'recalled' && (
                          <div style={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            background: '#ffffff',
                            border: '1.5px solid #000000',
                            padding: '2px 8px',
                            fontSize: '0.72em',
                            fontWeight: 'bold',
                            color: '#000000',
                            letterSpacing: '0.5px',
                            fontFamily: 'Arial, sans-serif',
                            boxShadow: '1px 1px 3px rgba(0,0,0,0.15)',
                            zIndex: 10
                          }}>
                            NON-STERILE
                          </div>
                        )}

                        {/* STERILE Overlay */}
                        {traceData.status === 'sterile-ready' && (() => {
                          const isEO = details.machine && (
                            details.machine.toUpperCase().includes('EO') ||
                            details.machine.toLowerCase().includes('ethylene') ||
                            details.machine.toLowerCase().includes('gas')
                          );

                          return (
                            <div style={{
                              position: 'absolute',
                              top: '12px',
                              right: '12px',
                              background: '#ffffff',
                              border: '1.5px solid #000000',
                              display: 'flex',
                              alignItems: 'center',
                              padding: '2px 0',
                              fontSize: '0.72em',
                              fontWeight: 'bold',
                              color: '#000000',
                              boxShadow: '1px 1px 3px rgba(0,0,0,0.15)',
                              fontFamily: 'Arial, sans-serif',
                              zIndex: 10
                            }}>
                              <div style={{ padding: '0 8px', letterSpacing: '0.5px' }}>STERILE</div>
                              <div style={{ width: '1px', height: '14px', background: '#000000' }} />
                              {isEO ? (
                                <div style={{ padding: '0 8px', letterSpacing: '0.5px' }}>EO</div>
                              ) : (
                                <div style={{ padding: '0 6px', display: 'flex', alignItems: 'center' }}>
                                  <svg width="10" height="18" viewBox="0 0 12 20" style={{ display: 'block' }}>
                                    <path
                                      d="M6,1.5 C4.9,1.5 4,2.4 4,3.5 L4,11 C2.8,11.9 2,13.3 2,15 C2,17.2 3.8,19 6,19 C8.2,19 10,17.2 10,15 C10,13.3 9.2,11.9 8,11 L8,3.5 C8,2.4 7.1,1.5 6,1.5 Z"
                                      fill="#ffffff"
                                      stroke="#000000"
                                      strokeWidth="1.2"
                                      strokeLinejoin="round"
                                    />
                                    <circle cx="6" cy="15" r="2" fill="#000000" />
                                    <line x1="6" y1="9" x2="6" y2="14" stroke="#000000" strokeWidth="1.2" strokeLinecap="round" />
                                  </svg>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Card Stats (Storage location if any) */}
                      {mItem && (mItem.cabinet || mItem.shelf || mItem.row) && (
                        <div style={{ background: '#e3f2fd', padding: '6px 10px', borderRadius: '6px', fontSize: '0.82em', border: '1px solid #90caf9', color: '#1e88e5', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <span>📍</span>
                          <span><strong>พิกัดจัดเก็บ:</strong> ตู้ {mItem.cabinet || '-'} | ชั้น {mItem.shelf || '-'} | แถว {mItem.row || '-'}</span>
                        </div>
                      )}

                      {/* Details */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.85em', color: '#2c3e50', flex: 1, marginTop: '5px' }}>
                        {/* Pack details */}
                        <div style={{ borderBottom: '1px dashed #bdc3c7', paddingBottom: '6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold' }}>
                            <span style={{ fontSize: '1.2em' }}>⚙️</span>
                            <span>รายละเอียดเครื่องมือ</span>
                          </div>
                          <div style={{ paddingLeft: '22px', fontSize: '0.92em', color: '#555', lineHeight: '1.4' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', marginBottom: '3px' }}>
                              <span>•</span>
                              <span style={{ display: 'inline-flex', alignItems: 'center' }} title="วันผลิต (Manufacturing Date)">
                                <svg width="15" height="15" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#2c3e50', marginRight: '4px' }}>
                                  <path d="M 2,24 L 2,14 L 6,10 L 10,14 L 14,10 L 18,14 L 22,10 L 22,4 L 26,4 L 26,24 Z" fill="rgba(44, 62, 80, 0.15)" />
                                </svg>
                              </span>
                              <span>{details.mfgDate || '-'}</span>
                              <span style={{ color: '#cbd5e1', margin: '0 6px' }}>/</span>
                              <span style={{ fontSize: '1.1em', display: 'inline-flex', alignItems: 'center' }} title="วันหมดอายุ (Expiration Date)">⌛</span>
                              <span style={{ color: '#d35400', fontWeight: 'bold' }}>{details.expDate || '-'}</span>
                            </div>
                            <div>• <strong>ผู้เตรียมเครื่องมือ:</strong> {details.preparedBy || '-'}</div>
                            <div>• <strong>ผู้ตรวจเครื่องมือ:</strong> {details.checkedBy || '-'}</div>
                            <div>• <strong>รูปแบบบรรจุ:</strong> {details.packaging || '-'}</div>
                          </div>
                        </div>

                        {/* Autoclave details & QR code side-by-side */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold' }}>
                              <span style={{ fontSize: '1.2em' }}>🔥</span>
                              <span>STERILIZATION DATA</span>
                            </div>
                            <div style={{ paddingLeft: '22px', fontSize: '0.92em', color: '#555', lineHeight: '1.4' }}>
                              <div>• <strong>เครื่อง:</strong> {details.machine || '-'}</div>
                              <div>• <strong>รอบ:</strong> รอบที่ {details.cycle || '-'}</div>
                            </div>
                          </div>
                          <div style={{ background: '#ffffff', padding: '2px', border: '1px solid #7f8c8d', borderRadius: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <QRCodeCanvas value={traceData.unique_barcode} size={72} level="M" />
                          </div>
                        </div>
                      </div>

                      {/* Card Footer */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', fontSize: '0.72em', color: '#7f8c8d', marginTop: 'auto' }}>
                        <span>กลุ่มงานทันตกรรมโรงพยาบาลฝาง</span>
                      </div>
                    </div>

                    {/* Recalled diagonal warning stamp */}
                    {traceData.status === 'recalled' && (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%) rotate(-12deg)',
                        background: 'rgba(255, 255, 255, 0.75)',
                        border: '4px solid rgba(192, 57, 43, 0.75)',
                        padding: '10px 15px',
                        color: 'rgba(192, 57, 43, 0.85)',
                        fontWeight: '900',
                        fontSize: '1.05em',
                        textAlign: 'center',
                        borderRadius: '8px',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                        whiteSpace: 'nowrap',
                        zIndex: 100,
                        pointerEvents: 'none',
                        fontFamily: "'Segoe UI', 'Noto Sans Thai', sans-serif"
                      }}>
                        เครื่องมือมีปัญหา ส่งคืนเพื่อตรวจสอบ
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Close Button */}
              <button
                onClick={() => setCameraScanSuccessData(null)}
                style={{
                  marginTop: '20px',
                  padding: '12px 30px',
                  borderRadius: '12px',
                  border: 'none',
                  backgroundColor: '#22c55e',
                  color: '#ffffff',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(34, 197, 94, 0.2)',
                  minWidth: '120px',
                  textAlign: 'center'
                }}
              >
                ตกลง
              </button>
            </div>
          </div>
        )}

        {/* 🔑 Modal เปลี่ยนรหัสผ่านของตนเอง (Self Password Reset Modal) */}
        {showSelfResetPasswordModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}>
            <form onSubmit={handleUpdateSelfPassword} style={{
              background: '#ffffff',
              borderRadius: '20px',
              width: '95%',
              maxWidth: '450px',
              padding: '30px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              textAlign: 'left'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#eff6ff',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '0 auto 20px auto',
                fontSize: '28px',
                color: '#3b82f6'
              }}>
                🔑
              </div>
              <h3 style={{
                margin: '0 0 10px 0',
                color: '#0f172a',
                fontSize: '1.4em',
                fontWeight: 'bold',
                textAlign: 'center'
              }}>
                เปลี่ยนรหัสผ่านใหม่
              </h3>
              <p style={{
                margin: '0 0 20px 0',
                color: '#64748b',
                fontSize: '0.9em',
                lineHeight: '1.5',
                textAlign: 'center'
              }}>
                สำหรับชื่อผู้ใช้งาน: <strong style={{ color: '#0f172a' }}>{user?.email ? user.email.split('@')[0] : ''}</strong>
              </p>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85em', fontWeight: 'bold', color: '#475569' }}>
                  รหัสผ่านปัจจุบัน
                </label>
                <input
                  type="password"
                  placeholder="ป้อนรหัสผ่านปัจจุบัน"
                  value={selfCurrentPasswordValue}
                  onChange={(e) => setSelfCurrentPasswordValue(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: '2px solid #cbd5e1',
                    fontSize: '16px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  required
                  autoFocus
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85em', fontWeight: 'bold', color: '#475569' }}>
                  รหัสผ่านใหม่ (อย่างน้อย 6 ตัวอักษร)
                </label>
                <input
                  type="password"
                  placeholder="ป้อนรหัสผ่านใหม่"
                  value={selfNewPasswordValue}
                  onChange={(e) => setSelfNewPasswordValue(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: '2px solid #cbd5e1',
                    fontSize: '16px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  required
                  minLength={6}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85em', fontWeight: 'bold', color: '#475569' }}>
                  ยืนยันรหัสผ่านใหม่
                </label>
                <input
                  type="password"
                  placeholder="ป้อนรหัสผ่านใหม่อีกครั้ง"
                  value={selfConfirmNewPasswordValue}
                  onChange={(e) => setSelfConfirmNewPasswordValue(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: '2px solid #cbd5e1',
                    fontSize: '16px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  required
                  minLength={6}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowSelfResetPasswordModal(false);
                    setSelfCurrentPasswordValue('');
                    setSelfNewPasswordValue('');
                    setSelfConfirmNewPasswordValue('');
                  }}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    borderRadius: '12px',
                    border: 'none',
                    backgroundColor: '#e2e8f0',
                    color: '#475569',
                    fontWeight: '600',
                    cursor: 'pointer',
                    minHeight: '45px',
                    textAlign: 'center'
                  }}
                  disabled={isUpdatingSelfPassword}
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    borderRadius: '12px',
                    border: 'none',
                    backgroundColor: '#3b82f6',
                    color: '#ffffff',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                    minHeight: '45px',
                    textAlign: 'center'
                  }}
                  disabled={isUpdatingSelfPassword}
                >
                  {isUpdatingSelfPassword ? 'กำลังบันทึก...' : 'บันทึกรหัสผ่าน'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* 👤 User Profile & Account Management Modal */}
        <UserProfileModal
          isOpen={showUserProfileModal}
          onClose={() => setShowUserProfileModal(false)}
          user={user}
          currentUserProfile={currentStaffProfile}
          onProfileUpdated={fetchUserProfiles}
        />

        {/* Sterile Page Custom Processing/Success Popup Overlay */}
        {sterileProcessingState && (
          <div className="sterile-processing-overlay">
            <div className="sterile-processing-card">
              {sterileProcessingState === 'processing' ? (
                <>
                  <div className="sterile-spinner" />
                  <p className="sterile-processing-text">กำลังดำเนินการ...</p>
                </>
              ) : (
                <>
                  <div className="sterile-success-icon">
                    <span className="sterile-success-check">✓</span>
                  </div>
                  <p className="sterile-processing-text">สำเร็จ</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* 🌟 Custom Sterile Confirmation Modal */}
        {sterileConfirmModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
          }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.08)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '24px',
              width: '95%',
              maxWidth: '400px',
              padding: '30px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              color: '#ffffff',
              textAlign: 'center',
              fontFamily: "'Segoe UI', 'Noto Sans Thai', sans-serif"
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: 'rgba(52, 152, 219, 0.15)',
                border: '2px solid #3498db',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '0 auto 20px auto',
                fontSize: '28px',
                color: '#3498db',
                boxShadow: '0 0 15px rgba(52, 152, 219, 0.3)'
              }}>
                ❓
              </div>
              <h3 style={{
                margin: '0 0 15px 0',
                color: '#ffffff',
                fontSize: '1.4em',
                fontWeight: 'bold',
                textShadow: '0 2px 10px rgba(0,0,0,0.2)'
              }}>
                ยืนยันการดำเนินการ
              </h3>
              <p style={{
                margin: '0 0 25px 0',
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '1em',
                lineHeight: '1.6'
              }}>
                {sterileConfirmModal.message}
              </p>
              <div style={{
                display: 'flex',
                gap: '15px',
                justifyContent: 'center'
              }}>
                <button
                  onClick={() => setSterileConfirmModal(null)}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    color: '#ffffff',
                    fontWeight: '600',
                    cursor: 'pointer',
                    flex: 1,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)'}
                >
                  ยกเลิก
                </button>
                <button
                  onClick={() => {
                    const confirmFn = sterileConfirmModal.onConfirm;
                    setSterileConfirmModal(null);
                    confirmFn();
                  }}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '12px',
                    border: 'none',
                    backgroundColor: '#27ae60',
                    color: '#ffffff',
                    fontWeight: '600',
                    cursor: 'pointer',
                    flex: 1,
                    boxShadow: '0 4px 12px rgba(39, 174, 96, 0.3)',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#2ecc71'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#27ae60'}
                >
                  ตกลง
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ⚠️ Modal: ยืนยันการใช้งานวัสดุอายัดที่ห้องล้าง */}
        {recallScanPromptItem && (
          <div className="sterile-modal-overlay" style={{ zIndex: 9999 }}>
            <div className="sterile-modal-content" style={{ maxWidth: '500px', textAlign: 'center' }}>
              <div style={{ fontSize: '3em', marginBottom: '15px' }}>⚠️</div>
              <h3 style={{ color: '#e74c3c', fontSize: '1.4em', fontWeight: 'bold', margin: '0 0 10px 0' }}>ตรวจพบวัสดุที่ถูกอายัด/เรียกคืน</h3>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.95em', margin: '0 0 20px 0', lineHeight: '1.5' }}>
                วัสดุรหัส <strong>{recallScanPromptItem.unique_barcode}</strong> ({recallScanPromptItem.item_name})
                ถูกอายัดในกระบวนการ CSSD ก่อนหน้านี้
                <br /><br />
                <strong>เพื่อประเมินผลการเรียกคืน กรุณายืนยัน:</strong>
                <br />
                วัสดุชิ้นนี้ถูกนำไปแกะห่อ/ใช้งานที่ห้องคลินิกแล้วหรือยัง?
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  onClick={() => handleRecallPromptOutcome('used')}
                  style={{
                    padding: '14px',
                    borderRadius: '10px',
                    border: 'none',
                    backgroundColor: '#e74c3c',
                    color: '#fff',
                    fontWeight: 'bold',
                    fontSize: '1em',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#c0392b'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#e74c3c'}
                >
                  🔴 ใช้งานแล้ว (เก็บคืนไม่ทัน / ซองถูกแกะ)
                </button>
                <button
                  onClick={() => handleRecallPromptOutcome('not_used')}
                  style={{
                    padding: '14px',
                    borderRadius: '10px',
                    border: 'none',
                    backgroundColor: '#2ecc71',
                    color: '#fff',
                    fontWeight: 'bold',
                    fontSize: '1em',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#27ae60'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#2ecc71'}
                >
                  🟢 ยังไม่ได้ใช้งาน (สะอาด / เก็บคืนสำเร็จ)
                </button>
                <button
                  onClick={() => setRecallScanPromptItem(null)}
                  style={{
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    backgroundColor: 'transparent',
                    color: 'rgba(255,255,255,0.7)',
                    fontWeight: '600',
                    fontSize: '0.9em',
                    cursor: 'pointer',
                    marginTop: '5px'
                  }}
                >
                  ยกเลิกการสแกนชิ้นนี้
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 📋 Modal: แสดงรายละเอียดของพัสดุในแต่ละจำนวน */}
        {selectedDetailCountName && (
          <div className="sterile-modal-overlay" style={{ zIndex: 9998 }}>
            <div className="sterile-modal-content" style={{ maxWidth: '700px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '15px' }}>
                <h3 style={{ margin: 0, fontSize: '1.3em', color: '#fff', fontWeight: 'bold' }}>
                  🔍 รายละเอียด: {selectedDetailCountName}
                </h3>
                <button
                  onClick={() => setSelectedDetailCountName(null)}
                  style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1.5em', cursor: 'pointer' }}
                >
                  ✕
                </button>
              </div>

              {detailModalItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', color: 'rgba(255,255,255,0.5)' }}>ไม่มีรายการพัสดุสำหรับตัวเลขนี้</div>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9em', color: '#fff' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)' }}>
                        <th style={{ padding: '10px' }}>รหัสบาร์โค้ด</th>
                        <th style={{ padding: '10px' }}>ชื่อวัสดุ</th>
                        <th style={{ padding: '10px' }}>สถานะเรียกคืน</th>
                        <th style={{ padding: '10px' }}>สถานะปัจจุบัน</th>
                        <th style={{ padding: '10px' }}>เครื่องนึ่ง / รอบ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailModalItems.map(item => {
                        const details = item.details || {};
                        const outcome = details.recallOutcome;
                        let outcomeUI = '';
                        if (outcome === 'pending') {
                          outcomeUI = <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>⏳ รอดำเนินการ</span>;
                        } else if (outcome === 'used') {
                          outcomeUI = <span style={{ color: '#ef4444', fontWeight: 'bold' }}>🔴 ใช้งานแล้ว (เก็บไม่ทัน)</span>;
                        } else if (outcome === 'not_used') {
                          outcomeUI = <span style={{ color: '#10b981', fontWeight: 'bold' }}>🟢 ยังไม่ได้ใช้งาน (เก็บคืนได้)</span>;
                        } else {
                          outcomeUI = <span style={{ color: 'rgba(255,255,255,0.5)' }}>-</span>;
                        }

                        return (
                          <tr key={item.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <td style={{ padding: '10px', fontWeight: 'bold' }}>{item.unique_barcode}</td>
                            <td style={{ padding: '10px' }}>{item.item_name}</td>
                            <td style={{ padding: '10px' }}>{outcomeUI}</td>
                            <td style={{ padding: '10px' }}>
                              {item.status === 'recalled' ? '⚠️ อายัด (Recall)' : item.status}
                            </td>
                            <td style={{ padding: '10px' }}>
                              {details.machine || '-'} / {details.cycle || '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setSelectedDetailCountName(null)}
                  style={{
                    background: '#3b82f6',
                    color: '#fff',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  ปิดหน้าต่าง
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 🔓 Modal: ขอยกเลิกการอายัดวัสดุ/เครื่องมือ (Cancel Recall Request Modal) */}
        {cancelRecallPromptItem && (
          <div className="sterile-modal-overlay" style={{ zIndex: 10005 }}>
            <div className="sterile-modal-content" style={{ maxWidth: '450px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '15px' }}>
                <h3 style={{ margin: 0, fontSize: '1.25em', color: '#fff', fontWeight: 'bold' }}>
                  🔓 ส่งคำขอยกเลิกการอายัดวัสดุ
                </h3>
                <button
                  onClick={() => setCancelRecallPromptItem(null)}
                  style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1.5em', cursor: 'pointer' }}
                >
                  ✕
                </button>
              </div>

              <p style={{ fontSize: '0.92em', color: 'rgba(255,255,255,0.85)', lineHeight: '1.5', margin: '0 0 20px 0', textAlign: 'left' }}>
                • <strong>บาร์โค้ด:</strong> {cancelRecallPromptItem.item.unique_barcode}<br />
                • <strong>ชื่อเครื่องมือ:</strong> {cancelRecallPromptItem.item.item_name}
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '25px', textAlign: 'left' }}>
                <label style={{ fontWeight: 'bold', color: '#fff', fontSize: '0.9em' }}>
                  เหตุผลความจำเป็นในการยกเลิกอายัด <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <textarea
                  placeholder="ระบุเหตุผล เช่น ผ่านกระบวนการนึ่งทดสอบซ้ำผ่านเกณฑ์แล้ว, ตรวจสอบสภาพห่อบรรจุสมบูรณ์ดี..."
                  value={cancelRecallPromptItem.reason}
                  onChange={(e) => setCancelRecallPromptItem(prev => ({ ...prev, reason: e.target.value }))}
                  style={{
                    width: '100%',
                    height: '100px',
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid rgba(255,255,255,0.15)',
                    background: 'rgba(255,255,255,0.06)',
                    color: '#fff',
                    fontSize: '0.92em',
                    boxSizing: 'border-box',
                    resize: 'none',
                    outline: 'none'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setCancelRecallPromptItem(null)}
                  style={{
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    color: '#fff',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '0.9em'
                  }}
                >
                  ยกเลิก
                </button>
                <button
                  disabled={!cancelRecallPromptItem.reason.trim()}
                  onClick={() => {
                    handleRequestCancelRecall(cancelRecallPromptItem.item, cancelRecallPromptItem.reason);
                    setCancelRecallPromptItem(null);
                  }}
                  style={{
                    background: cancelRecallPromptItem.reason.trim() ? '#2980b9' : 'rgba(255,255,255,0.05)',
                    color: cancelRecallPromptItem.reason.trim() ? '#fff' : 'rgba(255,255,255,0.2)',
                    border: 'none',
                    padding: '10px 22px',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: cancelRecallPromptItem.reason.trim() ? 'pointer' : 'not-allowed',
                    fontSize: '0.9em'
                  }}
                >
                  ส่งคำขอให้ Admin อนุมัติ
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 📸 Modal: เลือกวิธีอัปโหลดภาพวัสดุที่มีปัญหา */}
        {showRecallUploadPopup && (
          <div className="sterile-modal-overlay" style={{ zIndex: 10006 }}>
            <div className="sterile-modal-content" style={{ maxWidth: '360px', textAlign: 'center' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '15px' }}>
                <h3 style={{ margin: 0, fontSize: '1.15em', color: '#fff', fontWeight: 'bold' }}>
                  📸 อัปโหลดรูปภาพวัสดุมีปัญหา
                </h3>
                <button
                  onClick={() => setShowRecallUploadPopup(false)}
                  style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1.3em', cursor: 'pointer' }}
                >
                  ✕
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '10px' }}>
                <button
                  onClick={() => {
                    if (recallCameraInputRef.current) {
                      recallCameraInputRef.current.click();
                    }
                  }}
                  style={{
                    padding: '14px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: '#fff',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '0.95em',
                    boxShadow: '0 4px 6px rgba(29, 78, 216, 0.15)'
                  }}
                >
                  📷 ถ่ายรูปจากกล้อง
                </button>

                <button
                  onClick={() => {
                    if (recallGalleryInputRef.current) {
                      recallGalleryInputRef.current.click();
                    }
                  }}
                  style={{
                    padding: '14px',
                    borderRadius: '10px',
                    border: '1px solid rgba(255,255,255,0.15)',
                    background: 'rgba(255,255,255,0.06)',
                    color: '#fff',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '0.95em'
                  }}
                >
                  📁 เลือกรูปที่มีอยู่แล้ว
                </button>

                <button
                  onClick={() => setShowRecallUploadPopup(false)}
                  style={{
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid #dc2626',
                    background: 'rgba(220, 38, 38, 0.1)',
                    color: '#ef4444',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '0.9em',
                    marginTop: '8px'
                  }}
                >
                  ยกเลิก
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 🖼️ Modal: ขยายรูปถ่ายวัสดุมีปัญหา (Lightbox) */}
        {zoomedRecallImageUrl && (
          <div 
            className="sterile-modal-overlay" 
            style={{ zIndex: 10007, backgroundColor: 'rgba(0,0,0,0.85)' }}
            onClick={() => setZoomedRecallImageUrl('')}
          >
            <div 
              style={{ 
                position: 'relative', 
                maxWidth: '90%', 
                maxHeight: '90%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setZoomedRecallImageUrl('')}
                style={{ 
                  position: 'absolute', 
                  top: '-40px', 
                  right: 0, 
                  background: 'none', 
                  border: 'none', 
                  color: '#fff', 
                  fontSize: '2em', 
                  cursor: 'pointer' 
                }}
              >
                ✕
              </button>
              <img 
                src={zoomedRecallImageUrl} 
                alt="รูปขยายวัสดุมีปัญหา" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '80vh', 
                  borderRadius: '12px', 
                  border: '3px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.5)'
                }} 
              />
              <div style={{ marginTop: '15px', color: '#fff', fontSize: '0.95em', fontWeight: 'bold' }}>
                📸 รูปภาพวัสดุที่มีปัญหา
              </div>
            </div>
          </div>
        )}

        {/* 🌟 Modal: รายละเอียดแม่แบบเซ็ตเครื่องมือ (Set Template Details Modal Popup) */}
        {selectedSetTemplate && (() => {
          const componentStatuses = selectedSetTemplate.components.map(compId => {
            const compItem = itemMaster.find(i => i.id === compId);
            const compName = compItem?.item_name || 'ไม่ทราบชื่อ';
            const compImg = compItem?.image_url;
            const ws = supplies.find(s => s.master_id === compId && s.warehouse_name.includes("ห้องล้างเครื่องมือ"));
            const availQty = ws?.item_amount || 0;
            const cabinet = compItem?.cabinet || '';
            const shelf = compItem?.shelf || '';
            const row = compItem?.row || '';
            const unitSize = compItem?.unit_size || '';
            const unit = compItem?.unit || 'ชิ้น';
            const category = compItem?.category || '';
            return { compId, compName, compImg, availQty, cabinet, shelf, row, unitSize, unit, category };
          });

          const minAvailable = componentStatuses.reduce((min, c) => Math.min(min, c.availQty), 9999);
          const isFullyAvailable = minAvailable > 0 && minAvailable !== 9999;
          const assemblyCount = isFullyAvailable ? minAvailable : 0;

          const { full, zoom } = parseImages(selectedSetTemplate.image_url);
          const hasImages = full || zoom;

          const locationText = (selectedSetTemplate.cabinet || selectedSetTemplate.shelf || selectedSetTemplate.row)
            ? `ตู้: ${selectedSetTemplate.cabinet || '-'} | ชั้น: ${selectedSetTemplate.shelf || '-'} | แถว: ${selectedSetTemplate.row || '-'}`
            : 'ไม่ระบุพิกัด';

          return (
            <div
              className="sterile-modal-overlay"
              onClick={() => setSelectedSetTemplate(null)}
              style={{ zIndex: 10002 }}
            >
              <div
                className="sterile-modal-content"
                style={{
                  maxWidth: '750px',
                  width: '95%',
                  background: 'linear-gradient(135deg, #1e1b4b 0%, #311042 50%, #111827 100%)',
                  color: '#ffffff',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  boxShadow: '0 25px 60px rgba(0,0,0,0.6)',
                  padding: 0,
                  borderRadius: '24px',
                  overflow: 'hidden'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div style={{
                  padding: '20px 24px',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'rgba(0,0,0,0.2)'
                }}>
                  <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px', color: '#f472b6' }}>
                    <span>📦</span>
                    <span style={{ fontSize: '1.2em', fontWeight: 'bold' }}>{selectedSetTemplate.item_name}</span>
                  </h3>
                  <button
                    onClick={() => setSelectedSetTemplate(null)}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: 'none',
                      color: '#ffffff',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                    onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
                  >
                    ✕
                  </button>
                </div>

                {/* Modal Body */}
                <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px', maxHeight: '75vh', overflowY: 'auto' }}>
                  
                  {/* Stats */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div style={{ background: 'rgba(255,255,255,0.04)', padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div style={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>📍 พิกัดเก็บเดิม</div>
                      <div style={{ fontWeight: '600', fontSize: '0.95em' }}>{locationText}</div>
                    </div>
                    <div style={{ 
                      background: isFullyAvailable ? 'rgba(16, 185, 129, 0.1)' : 'rgba(255,255,255,0.04)', 
                      padding: '12px 16px', 
                      borderRadius: '12px', 
                      border: `1px solid ${isFullyAvailable ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255,255,255,0.08)'}`,
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center' 
                    }}>
                      <div>
                        <div style={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>สถานะการจัดเซ็ต</div>
                        <div style={{ fontWeight: '700', fontSize: '1.1em', color: isFullyAvailable ? '#2ecc71' : '#cbd5e1' }}>
                          จัดได้: {assemblyCount} ชุด
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Images */}
                  {hasImages && (
                    <div style={{
                      background: 'rgba(0,0,0,0.2)',
                      borderRadius: '16px',
                      padding: '16px',
                      border: '1px solid rgba(255, 255, 255, 0.08)'
                    }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9em', color: 'rgba(255,255,255,0.6)', fontWeight: 'bold' }}>📸 รูปภาพชุดเครื่องมืออ้างอิง</h4>
                      <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        {full && (
                          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <span style={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.5)' }}>รูปเต็ม</span>
                            <img 
                              src={getOptimizedImageUrl(full)} 
                              alt="Full view" 
                              style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', objectFit: 'contain', cursor: 'pointer', background: '#fff' }} 
                              onClick={() => setExpandedImage(getOptimizedImageUrl(full))}
                            />
                          </div>
                        )}
                        {zoom && (
                          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <span style={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.5)' }}>รูปซูม</span>
                            <img 
                              src={getOptimizedImageUrl(zoom)} 
                              alt="Zoom view" 
                              style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', objectFit: 'contain', cursor: 'pointer', background: '#fff' }} 
                              onClick={() => setExpandedImage(getOptimizedImageUrl(zoom))}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Required Components List */}
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.15)',
                    borderRadius: '16px',
                    padding: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.08)'
                  }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95em', color: '#f472b6', fontWeight: 'bold' }}>
                      🔍 ชิ้นส่วนที่ต้องใช้ ({componentStatuses.length} รายการ)
                    </h4>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                      gap: '12px' 
                    }}>
                      {componentStatuses.map((c, i) => {
                        const { full: cFull, zoom: cZoom } = parseImages(c.compImg);
                        const cDisplayImg = cFull || cZoom;
                        
                        const cLocationText = (c.cabinet || c.shelf || c.row)
                          ? `ตู้: ${c.cabinet || '-'} ชั้น: ${c.shelf || '-'} แถว: ${c.row || '-'}`
                          : 'ไม่ระบุพิกัด';

                        return (
                          <div 
                            key={i} 
                            className="handpiece-card single-card" 
                            style={{ 
                              background: c.availQty > 0 ? 'linear-gradient(135deg, rgba(39, 174, 96, 0.08) 0%, rgba(46, 204, 113, 0.03) 100%)' : 'linear-gradient(135deg, rgba(231, 76, 60, 0.08) 0%, rgba(192, 41, 43, 0.03) 100%)',
                              border: `1px solid ${c.availQty > 0 ? 'rgba(39, 174, 96, 0.25)' : 'rgba(231, 76, 60, 0.25)'}`,
                              padding: '12px',
                              display: 'flex',
                              gap: '12px',
                              alignItems: 'center',
                              borderRadius: '12px',
                              cursor: 'default'
                            }}
                          >
                            {/* Image */}
                            <div style={{ flexShrink: 0 }}>
                              {cDisplayImg ? (
                                <div 
                                  style={{
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    border: '1px solid rgba(255, 255, 255, 0.15)',
                                    background: '#ffffff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s ease',
                                  }}
                                  onClick={() => setExpandedImage(getOptimizedImageUrl(cDisplayImg))}
                                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                  onMouseLeave={(e) => e.currentTarget.style.transform = 'none'}
                                >
                                  <img src={getOptimizedImageUrl(cDisplayImg)} alt={c.compName} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                </div>
                              ) : (
                                <div style={{
                                  width: '80px',
                                  height: '80px',
                                  borderRadius: '8px',
                                  border: '1px dashed rgba(255,255,255,0.2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'rgba(255, 255, 255, 0.02)',
                                }}>
                                  <span style={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.3)' }}>ไม่มีรูป</span>
                                </div>
                              )}
                            </div>

                            {/* Info */}
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                                <span>{c.availQty > 0 ? '✔️' : '❌'}</span>
                                <span style={{ 
                                  fontWeight: 'bold', 
                                  color: c.availQty > 0 ? '#a7f3d0' : '#fca5a5',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.9em'
                                }} title={c.compName}>
                                  {c.compName}
                                </span>
                              </div>
                              <div style={{ fontSize: '0.75em', color: 'rgba(255,255,255,0.5)' }}>
                                หมวดหมู่: {c.category || 'ไม่ระบุ'} {c.unitSize ? `(${c.unitSize})` : ''}
                              </div>
                              <div style={{ fontSize: '0.75em', color: 'rgba(255,255,255,0.6)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={cLocationText}>
                                📍 {cLocationText}
                              </div>
                              <div style={{ marginTop: '2px' }}>
                                <span style={{ 
                                  fontSize: '0.75em', 
                                  fontWeight: 'bold', 
                                  padding: '1px 6px', 
                                  borderRadius: '10px',
                                  background: c.availQty > 0 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                  color: c.availQty > 0 ? '#10b981' : '#f87171',
                                }}>
                                  ห้องล้าง: {c.availQty} {c.unit}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                </div>
              </div>
            </div>
          );
        })()}

        {/* 🌟 Modal: รายละเอียดอุปกรณ์เดี่ยว (Single Item Details Modal Popup) */}
        {selectedSingleItem && (() => {
          const { full, zoom } = parseImages(selectedSingleItem.image_url);
          const hasImages = full || zoom;

          const locationText = (selectedSingleItem.cabinet || selectedSingleItem.shelf || selectedSingleItem.row)
            ? `ตู้: ${selectedSingleItem.cabinet || '-'} | ชั้น: ${selectedSingleItem.shelf || '-'} | แถว: ${selectedSingleItem.row || '-'}`
            : 'ไม่ระบุพิกัด';

          return (
            <div
              className="sterile-modal-overlay"
              onClick={() => setSelectedSingleItem(null)}
              style={{ zIndex: 10003 }}
            >
              <div
                className="sterile-modal-content"
                style={{
                  maxWidth: '550px',
                  width: '90%',
                  background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #020617 100%)',
                  color: '#ffffff',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  boxShadow: '0 25px 60px rgba(0,0,0,0.6)',
                  padding: 0,
                  borderRadius: '20px',
                  overflow: 'hidden'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'rgba(0,0,0,0.2)'
                }}>
                  <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', color: '#67e8f9' }}>
                    <span>🦷</span>
                    <span style={{ fontSize: '1.15em', fontWeight: 'bold' }}>{selectedSingleItem.item_name}</span>
                  </h3>
                  <button
                    onClick={() => setSelectedSingleItem(null)}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: 'none',
                      color: '#ffffff',
                      borderRadius: '50%',
                      width: '30px',
                      height: '30px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                    onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
                  >
                    ✕
                  </button>
                </div>

                {/* Body */}
                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', maxHeight: '70vh', overflowY: 'auto' }}>
                  {/* General Info */}
                  <div style={{ background: 'rgba(255,255,255,0.03)', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9em' }}>
                      <span style={{ color: 'rgba(255,255,255,0.5)' }}>หมวดหมู่:</span>
                      <span style={{ fontWeight: 'bold' }}>{selectedSingleItem.category || 'ไม่ระบุ'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9em' }}>
                      <span style={{ color: 'rgba(255,255,255,0.5)' }}>ขนาด/มิติ:</span>
                      <span style={{ fontWeight: 'bold' }}>{selectedSingleItem.unit_size || '-'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9em' }}>
                      <span style={{ color: 'rgba(255,255,255,0.5)' }}>พิกัดเก็บเดิม:</span>
                      <span style={{ fontWeight: 'bold', color: '#67e8f9' }}>{locationText}</span>
                    </div>
                  </div>



                  {/* Images (Show both full and zoom) */}
                  {hasImages ? (
                    <div style={{
                      background: 'rgba(0,0,0,0.2)',
                      borderRadius: '12px',
                      padding: '14px',
                      border: '1px solid rgba(255, 255, 255, 0.06)'
                    }}>
                      <h4 style={{ margin: '0 0 10px 0', fontSize: '0.85em', color: 'rgba(255,255,255,0.6)', fontWeight: 'bold' }}>📸 รูปภาพเครื่องมือทั้งหมด</h4>
                      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        {full && (
                          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{ fontSize: '0.75em', color: 'rgba(255,255,255,0.5)' }}>รูปเต็ม (Full)</span>
                            <img 
                              src={getOptimizedImageUrl(full)} 
                              alt="Full view" 
                              style={{ maxWidth: '140px', maxHeight: '140px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.1)', objectFit: 'contain', cursor: 'pointer', background: '#fff' }} 
                              onClick={() => setExpandedImage(getOptimizedImageUrl(full))}
                            />
                          </div>
                        )}
                        {zoom && (
                          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{ fontSize: '0.75em', color: 'rgba(255,255,255,0.5)' }}>รูปซูม (Zoom)</span>
                            <img 
                              src={getOptimizedImageUrl(zoom)} 
                              alt="Zoom view" 
                              style={{ maxWidth: '140px', maxHeight: '140px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.1)', objectFit: 'contain', cursor: 'pointer', background: '#fff' }} 
                              onClick={() => setExpandedImage(getOptimizedImageUrl(zoom))}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '20px',
                      border: '1px dashed rgba(255,255,255,0.1)',
                      borderRadius: '10px',
                      color: 'rgba(255,255,255,0.4)',
                      fontSize: '0.9em'
                    }}>
                      ไม่มีรูปภาพเครื่องมือ
                    </div>
                  )}

                </div>
              </div>
            </div>
          );
        })()}

        {/* 🌟 Lightbox Modal for Image Zoom */}
        {expandedImage && (
          <div 
            className="sterile-modal-overlay" 
            onClick={() => setExpandedImage(null)}
            style={{
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0, 0, 0, 0.85)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)'
            }}
          >
            <div 
              className="sterile-modal-content"
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'auto',
                maxWidth: '90vw',
                maxHeight: '90vh',
                background: '#ffffff',
                padding: '12px',
                borderRadius: '16px',
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                border: 'none',
                overflow: 'hidden'
              }}
            >
              <button 
                onClick={() => setExpandedImage(null)}
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: 'rgba(0, 0, 0, 0.6)',
                  color: '#ffffff',
                  border: 'none',
                  fontSize: '1em',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  zIndex: 10000,
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)'; }}
              >
                ✕
              </button>
              <img 
                src={expandedImage} 
                alt="Expanded View" 
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  borderRadius: '12px',
                  display: 'block'
                }} 
              />
            </div>
          </div>
        )}

        {/* 🔄 Modal โอนย้ายสต็อกวัสดุสิ้นเปลืองข้ามคลัง (Global) */}
        <ConsumableTransferModal
          isOpen={isTransferModalOpen}
          onClose={handleCloseTransferModal}
          itemMaster={itemMaster}
          supplies={supplies}
          initialItem={transferInitialItem}
          supabase={supabase}
          user={user}
          fetchData={fetchData}
        />

      </div>
    </div>
  );
}

// ==========================================================
// ส่วนที่ 6: คอมโพเนนต์หลัก
// ==========================================================
export default function App() {
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [showGuestPrompt, setShowGuestPrompt] = useState(false);
  const [guestRoom, setGuestRoom] = useState('');
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [targetPageAfterLogin, setTargetPageAfterLogin] = useState(null);

  useEffect(() => {
    if (!supabase) {
      const savedLineSession = localStorage.getItem('dental_line_session');
      if (savedLineSession) {
        try {
          setSession(JSON.parse(savedLineSession));
        } catch (e) {
          console.error("Failed to parse LINE session:", e);
        }
      }
      setAuthLoading(false);
      return;
    }
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setSession(session);
      } else {
        const savedLineSession = localStorage.getItem('dental_line_session');
        if (savedLineSession) {
          try {
            setSession(JSON.parse(savedLineSession));
          } catch (e) {
            console.error("Failed to parse LINE session:", e);
          }
        } else {
          setSession(null);
        }
      }
      setAuthLoading(false);
      
      const params = new URLSearchParams(window.location.search);
      const roomParam = params.get('room');
      if (roomParam && !isRiskSubdomain()) {
        setGuestRoom(roomParam);
        const hasLineSession = Boolean(localStorage.getItem('dental_line_session'));
        if (!session && !hasLineSession) {
          setShowGuestPrompt(true);
        }
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setSession(session);
      } else {
        const savedLineSession = localStorage.getItem('dental_line_session');
        if (savedLineSession) {
          try {
            setSession(JSON.parse(savedLineSession));
          } catch (e) {
            setSession(null);
          }
        } else {
          setSession(null);
        }
      }
      setAuthLoading(false);
    });
    
    return () => subscription.unsubscribe();
  }, []);

  if (!supabase) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '20px',
        textAlign: 'center',
        backgroundColor: '#f8fafc',
        color: '#1e293b'
      }}>
        <div style={{
          backgroundColor: '#fff',
          padding: '40px',
          borderRadius: '16px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          maxWidth: '500px'
        }}>
          <span style={{ fontSize: '48px', marginBottom: '20px', display: 'block' }}>⚠️</span>
          <h2 style={{ margin: '0 0 10px 0', fontSize: '24px', fontWeight: 'bold' }}>ขาดข้อมูลการเชื่อมต่อฐานข้อมูล (Supabase Config Missing)</h2>
          <p style={{ color: '#64748b', fontSize: '15px', lineHeight: '1.6', marginBottom: '24px' }}>
            กรุณาตั้งค่า <strong>Environment Variables</strong> ใน Vercel Dashboard ให้ครบถ้วน เพื่อเชื่อมต่อกับระบบหลังบ้าน
          </p>
          <div style={{
            textAlign: 'left',
            background: '#f1f5f9',
            padding: '16px',
            borderRadius: '8px',
            fontFamily: 'monospace',
            fontSize: '13px',
            color: '#334155'
          }}>
            <strong>ตัวแปรที่ต้องตั้งค่าเพิ่มเติมใน Vercel:</strong>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>REACT_APP_SUPABASE_URL</li>
              <li>REACT_APP_SUPABASE_ANON_KEY</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    localStorage.removeItem('dental_line_session');
    logoutLine();
    setSession(null);
    if (supabase) {
      await supabase.auth.signOut();
    }
  };

  const renderGuestPrompt = () => {
    if (!showGuestPrompt || isRiskSubdomain()) return null;
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000,
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: '24px',
          width: '90%',
          maxWidth: '420px',
          padding: '30px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          color: '#ffffff',
          textAlign: 'center',
          fontFamily: "'Segoe UI', 'Noto Sans Thai', sans-serif"
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: 'rgba(26, 188, 156, 0.15)',
            border: '2px solid #1abc9c',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: '0 auto 20px auto',
            fontSize: '28px',
            color: '#1abc9c',
            boxShadow: '0 0 15px rgba(26, 188, 156, 0.3)'
          }}>
            🛒
          </div>
          <h3 style={{
            margin: '0 0 15px 0',
            color: '#ffffff',
            fontSize: '1.4em',
            fontWeight: 'bold',
            textShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}>
            สั่งพัสดุสำหรับห้องปฏิบัติงาน
          </h3>
          <p style={{
            margin: '0 0 25px 0',
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '1.05em',
            lineHeight: '1.6'
          }}>
            คุณต้องการสั่งของสำหรับ:<br />
            <strong style={{ color: '#1abc9c', fontSize: '1.1em' }}>"{guestRoom.trim()}"</strong>
          </p>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <button
              onClick={() => {
                setShowGuestPrompt(false);
                setIsGuestMode(true);
              }}
              style={{
                padding: '14px',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #1abc9c 0%, #16a085 100%)',
                color: '#ffffff',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(26, 188, 156, 0.3)',
                transition: 'all 0.2s',
                fontSize: '1em'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              👤 สั่งแบบผู้ใช้ทั่วไป (Guest Mode)
            </button>
            <button
              onClick={() => {
                setShowGuestPrompt(false);
              }}
              style={{
                padding: '14px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                color: '#ffffff',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontSize: '1em'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)'}
            >
              🔑 เข้าสู่ระบบ
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (authLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0f172a' }}>
        <div style={{
          border: '4px solid rgba(255,255,255,0.1)',
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          borderLeftColor: '#1abc9c',
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    );
  }

  // On risk subdomain, staff login is strictly required (no guest access allowed)
  if (!session || (isRiskSubdomain() && isGuestMode)) {
    return (
      <>
        <LoginPage
          onLoginSuccess={(session) => {
            setSession(session);
            if (isRiskSubdomain()) {
              setTargetPageAfterLogin('dental-risk-portal');
            }
          }}
        />
        {!isRiskSubdomain() && renderGuestPrompt()}
      </>
    );
  }

  return (
    <>
      <Dashboard
        user={isGuestMode && !isRiskSubdomain() ? { email: 'guest@hospital.com', role: 'guest' } : session.user}
        onLogout={isGuestMode && !isRiskSubdomain() ? () => setIsGuestMode(false) : handleLogout}
        isGuestMode={isGuestMode && !isRiskSubdomain()}
        guestRoom={guestRoom}
        initialPage={targetPageAfterLogin}
      />
      {!isRiskSubdomain() && renderGuestPrompt()}
    </>
  );
}